/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';

import { Undo, Redo, ChevronLeft, ChevronRight, Plus, Palette, Download, Database, Edit2, Layers, FilePlus, Copy, Trash2, X } from 'lucide-react';
import { ElementsPanel } from './ElementsPanel';
import { TextPanel } from './TextPanel';
import { BrandKitPanel } from './BrandKitPanel';
import { SelectionOverlay } from '../../components/template-editor/SelectionOverlay';
import { SnapGuides, calculateSnap } from '../../components/template-editor/SnapGuides';
import { TextStyle } from './textEditorTypes';
import { compilePlayerData, extractOfflineAssets } from '../../utils/compileUtils';
import { TextPropertiesPanel } from '../../components/template-editor/TextPropertiesPanel';
import { BackgroundPropertiesPanel, BackgroundSettings } from '../../components/template-editor/BackgroundPropertiesPanel';
import { ImagePropertiesPanel, ImageSettings } from '../../components/template-editor/ImagePropertiesPanel';
import { ShapePropertiesPanel, ShapeSettings } from '../../components/template-editor/ShapePropertiesPanel';
import { VideoPropertiesPanel } from '../../components/template-editor/VideoPropertiesPanel';
import { VideoSettings } from '../../types/design';
import { AppPropertiesSidepane } from '../../components/template-editor/AppPropertiesSidepane';
import { FloatingContextToolbar } from '../../components/template-editor/FloatingContextToolbar';
import { WidgetContextToolbar } from '../../components/template-editor/WidgetContextToolbar';
import { GroupPropertiesPanel } from '../../components/template-editor/GroupPropertiesPanel';
import { UploadsPanel, UploadedFile } from '../../components/template-editor/UploadsPanel';
import { AppsPanel, APPS_DATA } from './AppsPanel';
import { Widget } from '../../features/widgets/widgetsApi';
import SelectAppModal from '../../../components/SelectAppModal';
import { TemplatesPanel } from './TemplatesPanel';
import { PlaylistPanel } from './PlaylistPanel';

import { LayersPanel } from '../../components/template-editor/LayersPanel';
import { ContentsPanel } from '../../components/template-editor/ContentsPanel';
import { DataBindingSidepane } from '../../components/template-editor/DataBindingSidepane';

import { useSaveUserDesignMutation, useUpdateSavedTemplateMutation } from '../../features/templates/templatesApi';
import { setSelectedTemplate } from '../../features/templates/templatesSlice';
import { toPng, toBlob } from 'html-to-image';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';
import {
  createEmptyHistory,
  canUndo,
  canRedo,
  performUndo,
  performRedo,
  commitAction,
  limitHistorySize,
  HistoryManager,
  HistoryState,
  HistoryActionType
} from '../../utils/historyManager';
import { getAbsolutePosition, getMultiLayerBounds, findLayerById, updateLayerDeep } from '../../utils/treeUtils';

import { Layer, LayerType, TemplateSettings, TemplateData, WidgetSettings, FillSettings } from '../../types/design';
export type { Layer, LayerType, TemplateSettings, TemplateData, WidgetSettings, FillSettings };

import { TimelinePanel } from '../../components/template-editor/TimelinePanel';
import { TemplatePropertiesPanel } from '../../components/template-editor/TemplatePropertiesPanel';
import { usePageManager } from './hooks/usePageManager';
import { PageData } from './types';
import { generateId, checkIsDynamic, clonePageWithMapping } from './utils/layerUtils';
import { saveState, getState, SavedState } from '../../services/indexedDB';

import { getWidgetReplacements } from '../../utils/widgetUtils';
import { TemplatePreview } from '../../components/template-editor/TemplatePreview';
import { CustomSizeModal } from '../home/components/CustomSizeModal';
import {
  getImageVisibleBounds,
  findLayerAtPosition,
  calculateAnchoredPosition,
  removeLayersRecursive,
  updateLockRecursive,
  applyBackgroundSettings,
  normalizeTemplateData
} from './utils/editorUtils';
import { SavingOverlay } from '../../components/template-editor/SavingOverlay';
import { LayerRenderer } from '../../components/template-editor/LayerRenderer';
import { useGetCustomApisQuery } from '../../features/integrations/customApiApi';
import { resolveTemplateBindings, resolveJsonPath } from './utils/bindingResolver';
import { autoMapTemplate, getFlattenedKeys } from './utils/autoMapper';

const DEFAULT_IMAGE_SETTINGS: ImageSettings = {
  url: '',
  opacity: 100,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  flipHorizontal: false,
  flipVertical: false,
  crop: { enabled: false, top: 0, right: 0, bottom: 0, left: 0 },
  borderWidth: 0,
  borderColor: '#000000',
  borderRadius: 0,
  objectFit: 'cover',
  lockAspectRatio: true,
  animation: {
    type: 'none',
    duration: 1000,
    delay: 0,
    easing: 'ease',
    loop: false,
  },
};

// Helper to normalize MongoDB IDs (handles string or {$oid: string})
const normalizeId = (obj: any): string => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  if (obj?.$oid) return obj?.$oid;
  if (obj?._id) return normalizeId(obj._id);
  return String(obj);
};

const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  url: '',
  opacity: 100,
  borderRadius: 0,
  objectFit: 'cover',
  muted: false,
  loop: true,
  autoplay: true,
  volume: 100,
  animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
};

const DEFAULT_SHAPE_SETTINGS: ShapeSettings = {
  fill: { type: 'solid', enabled: true, color: '#1e293b' },
  stroke: { enabled: true, color: '#000000', width: 2, style: 'solid' },
  cornerRadius: 0,
  rotation: 0,
  opacity: 1,
  blur: 0,
  animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
};

const DEFAULT_SCROLLER_SETTINGS = {
  content: 'SCROLLER TEXT HERE',
  fontFamily: 'Segoe UI',
  fontSize: 24,
  direction: 'rtl' as const,
  textStyle: 'regular' as const,
  scrollSpeed: 'medium' as const,
  textColor: '#FFFFFF',
  backgroundColor: '#002B5B',
  letterSpacing: 0
};

interface TemplateEditorPageProps {
  initialTemplate?: TemplateData | null;
  onBack?: () => void;
}

// --- New Layer System ---

// --- Helper Components & Constants ---

const Wrapper2 = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <div className={`relative w-5 h-5 ${className || ''}`}>
    <svg className="block w-full h-full" fill="none" viewBox="0 0 18 18">
      {children}
    </svg>
  </div>
);
// Sidebar Icons (18x18 grid, centered at 9) - Increased container to 24px (w-6) for better visibility
const Frame = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <div className={`relative w-6 h-6 ${className || ''}`}>
    <svg className="block w-full h-full" fill="none" viewBox="0 0 18 18">
      {children}
    </svg>
  </div>
);
// Header Icons (24x24 grid, centered at 12) - w-5 (20px) fits better in 36px buttons
const Frame2 = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <div className={`relative w-5 h-5 ${className || ''}`}>
    <svg className="block w-full h-full" fill="none" viewBox="0 0 24 24">
      {children}
    </svg>
  </div>
);
// const Palette = ({ className }: { className?: string }) => <div className={className} />; // Imported from lucide-react

const svgPaths = {
  p3ba229c0: "M3 13.5H9V19.5H3V13.5Z",
  p1ea61200: "M15 4.5H21V10.5H15V4.5Z",
  p3e4d7380: "M3 4.5H9V10.5H3V4.5Z",
  p213e6d00: "M12 4.5V19.5",
  p11648c20: "M4.5 12H19.5",
  p6520580: "M15 15H19.5V19.5H15V15Z",
  p3320a8e0: "M4.5 4.5H9V9H4.5V4.5Z",
  // Restored original paths
  p8ff3780: "M9.75 10.305C9.52197 10.4367 9.26331 10.506 9 10.506C8.7367 10.506 8.47803 10.4367 8.25 10.305L1.875 6.6525C1.75958 6.58709 1.66357 6.49223 1.59678 6.3776C1.52998 6.26297 1.49479 6.13267 1.49479 6C1.49479 5.86733 1.52998 5.73703 1.59678 5.6224C1.66357 5.50777 1.75958 5.41291 1.875 5.3475L8.25 1.695C8.47803 1.56335 8.7367 1.49404 9 1.49404C9.26331 1.49404 9.52197 1.56335 9.75 1.695L16.125 5.3475C16.2404 5.41291 16.3364 5.50777 16.4032 5.6224C16.47 5.73703 16.5052 5.86733 16.5052 6C16.5052 6.13267 16.47 6.26297 16.4032 6.3776C16.3364 6.49223 16.2404 6.58709 16.125 6.6525L9.75 10.305Z",
  p2d52d00: "M15 10.7138L16.125 11.3475C16.2404 11.4129 16.3364 11.5078 16.4032 11.6224C16.47 11.737 16.5052 11.8673 16.5052 12C16.5052 12.1327 16.47 12.263 16.4032 12.3776C16.3364 12.4922 16.2404 12.5871 16.125 12.6525L9.75 16.305C9.52197 16.4367 9.26331 16.506 9 16.506C8.7367 16.506 8.47803 16.4367 8.25 16.305L1.875 12.6525C1.75958 12.5871 1.66357 12.4922 1.59678 12.3776C1.52998 12.263 1.49479 12.1327 1.49479 12C1.49479 11.8673 1.52998 11.737 1.59678 11.6224C1.66357 11.5078 1.75958 11.4129 1.875 11.3475L3 10.7138",
  p1c7ad000: "M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25",
  p2cd86400: "M15 2.25H3C2.58579 2.25 2.25 2.58579 2.25 3V6.75C2.25 7.16421 2.58579 7.5 3 7.5H15C15.4142 7.5 15.75 7.16421 15.75 6.75V3C15.75 2.58579 15.4142 2.25 15 2.25Z",
  p3f33cb80: "M8.25 10.5H3C2.58579 10.5 2.25 10.8358 2.25 11.25V15C2.25 15.4142 2.58579 15.75 3 15.75H8.25C8.66421 15.75 9 15.4142 9 15V11.25C9 10.8358 8.66421 10.5 8.25 10.5Z",
  p15dde980: "M15 10.5H12.75C12.3358 10.5 12 10.8358 12 11.25V15C12 15.4142 12.3358 15.75 12.75 15.75H15C15.4142 15.75 15.75 15.4142 15.75 15V11.25C15.75 10.8358 15.4142 10.5 15 10.5Z",
  p110e2f80: "M3 5.25V3.75C3 3.55109 3.07902 3.36032 3.21967 3.21967C3.36032 3.07902 3.55109 3 3.75 3H14.25C14.4489 3 14.6397 3.07902 14.7803 3.21967C14.921 3.36032 15 3.55109 15 3.75V5.25",
  p6cfad80: "M6.22549 7.50059C6.1288 7.50586 6.03254 7.4843 5.94735 7.43828C5.86216 7.39226 5.79136 7.32358 5.74277 7.23983C5.69418 7.15607 5.66969 7.06052 5.67203 6.96372C5.67436 6.86692 5.70342 6.77265 5.75599 6.69134L8.55049 2.25059C8.5944 2.17151 8.65799 2.10513 8.73511 2.05786C8.81222 2.01059 8.90024 1.98405 8.99063 1.98081C9.08102 1.97756 9.17072 1.99773 9.25102 2.03934C9.33133 2.08096 9.39952 2.14262 9.44899 2.21834L12.2255 6.67559C12.2802 6.75418 12.3124 6.84625 12.3186 6.94182C12.3247 7.03739 12.3046 7.13283 12.2604 7.2178C12.2163 7.30277 12.1497 7.37403 12.0679 7.42388C11.9861 7.47373 11.8923 7.50025 11.7965 7.50059H6.22549Z",
  p9e34f00: "M6.75 10.5H3C2.58579 10.5 2.25 10.8358 2.25 11.25V15C2.25 15.4142 2.58579 15.75 3 15.75H6.75C7.16421 15.75 7.5 15.4142 7.5 15V11.25C7.5 10.8358 7.16421 10.5 6.75 10.5Z",
  p4031f00: "M13.125 15.75C14.5747 15.75 15.75 14.5747 15.75 13.125C15.75 11.6753 14.5747 10.5 13.125 10.5C11.6753 10.5 10.5 11.6753 10.5 13.125C10.5 14.5747 11.6753 15.75 13.125 15.75Z"
};
const appsSvgPaths: Record<string, string> = {
  p2f7c3ff0: "M6.75 2.25H3C2.58579 2.25 2.25 2.58579 2.25 3V8.25C2.25 8.66421 2.58579 9 3 9H6.75C7.16421 9 7.5 8.66421 7.5 8.25V3C7.5 2.58579 7.16421 2.25 6.75 2.25Z",
  p772e900: "M15 2.25H11.25C10.8358 2.25 10.5 2.58579 10.5 3V5.25C10.5 5.66421 10.8358 6 11.25 6H15C15.4142 6 15.75 5.66421 15.75 5.25V3C15.75 2.58579 15.4142 2.25 15 2.25Z",
  p99ad200: "M15 9H11.25C10.8358 9 10.5 9.33579 10.5 9.75V15C10.5 15.4142 10.8358 15.75 11.25 15.75H15C15.4142 15.75 15.75 15.4142 15.75 15V9.75C15.75 9.33579 15.4142 9 15 9Z",
  p3fc0d440: "M6.75 12H3C2.58579 12 2.25 12.3358 2.25 12.75V15C2.25 15.4142 2.58579 15.75 3 15.75H6.75C7.16421 15.75 7.5 15.4142 7.5 15V12.75C7.5 12.3358 7.16421 12 6.75 12Z"
};
const playlistSvgPaths: Record<string, string> = { p2f7c3ff0: "M11.25 9.00226C11.2496 8.86916 11.2846 8.73835 11.3515 8.62326C11.4183 8.50817 11.5146 8.41295 11.6304 8.34737C11.7463 8.28178 11.8775 8.2482 12.0105 8.25007C12.1436 8.25195 12.2738 8.2892 12.3878 8.35801L16.1355 10.6058C16.2471 10.6723 16.3395 10.7666 16.4036 10.8795C16.4678 10.9925 16.5015 11.1201 16.5015 11.25C16.5015 11.3799 16.4678 11.5076 16.4036 11.6205C16.3395 11.7334 16.2471 11.8278 16.1355 11.8943L12.3878 14.142C12.2738 14.2109 12.1435 14.2481 12.0104 14.25C11.8772 14.2518 11.746 14.2181 11.6301 14.1525C11.5143 14.0868 11.418 13.9915 11.3512 13.8763C11.2844 13.7611 11.2495 13.6302 11.25 13.497V9.00226Z" };
const navHeaderSvgPaths = {
  p13b45f00: "M9 3H15M3 9V15M21 9V15",
  p36f329f0: "M12 3V21M3 12H21",
  p3c401780: "M12 15L12 3",
  pd7dbd80: "M12 15L9 12M12 15L15 12",
  p1d241140: "M3 18H21"
};

/// NOTE: Stateless utility and helper functions (getImageVisibleBounds, findLayerAtPosition, 
// calculateAnchoredPosition, removeLayersRecursive, updateLockRecursive, applyBackgroundSettings, 
// normalizeTemplateData) have been moved to './utils/editorUtils' to modularize the codebase.

  // Inline LayerRenderer removed in favor of shared import in components/template-editor/LayerRenderer

// --- End New Layer System ---

export default function TemplateEditorPage({ initialTemplate, onBack }: TemplateEditorPageProps = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const reduxSelectedTemplate = useAppSelector((state) => state.templates?.selectedTemplate);
  const [_saveUserDesign] = useSaveUserDesignMutation();
  const [_updateSavedTemplate] = useUpdateSavedTemplateMutation();
  const [canvasScale, setCanvasScale] = React.useState(1);
  const [hasManuallyZoomed, setHasManuallyZoomed] = React.useState(false);

  // Use passed prop as priority (especially for the first click), fallback to Redux state
  const selectedTemplate = reduxSelectedTemplate || initialTemplate || { id: 'dev', name: 'Dev Template', previewUrl: '' };

  // Sync initialTemplate prop to Redux only when the prop itself changes its ID
  const lastInitialTemplateIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialTemplate && initialTemplate.id !== lastInitialTemplateIdRef.current) {
      dispatch(setSelectedTemplate(initialTemplate));
      lastInitialTemplateIdRef.current = initialTemplate.id;
    }
  }, [initialTemplate, dispatch]);

  // --- Consolidated State Declarations ---
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: selectedTemplate?.width || 1172,
    height: selectedTemplate?.height || 659,
    unit: 'px'
  });

  // Sync dimensions when template changes
  useEffect(() => {
    if (selectedTemplate?.width && selectedTemplate?.height) {
      setCanvasDimensions({
        width: selectedTemplate.width,
        height: selectedTemplate.height,
        unit: 'px'
      });
    }
  }, [selectedTemplate?.width, selectedTemplate?.height]);

  const [isCustomSizeModalOpen, setIsCustomSizeModalOpen] = useState(false);
  const CANVAS_WIDTH = canvasDimensions.width;
  const CANVAS_HEIGHT = canvasDimensions.height;
  const DRAG_THRESHOLD = 3; // pixels
  const SNAP_THRESHOLD = 8; // pixels
  const canvasRef = useRef<HTMLDivElement>(null);
  const pageNameInputRef = useRef<HTMLInputElement>(null);
  const justDraggedRef = useRef(false);
  const textEditDebounceRef = useRef<any>(null);
  const saveTimeoutRef = useRef<any>(null);

  const [dynamicLayers, setDynamicLayers] = useState<Layer[]>([]);
  const [layerPositions, setLayerPositions] = useState<Record<string, { left: number; top: number; width?: number; height?: number, rotation?: number }>>({});
  const [textContents, setTextContents] = useState<Record<string, string>>({});
  const [textStyles, setTextStyles] = useState<Record<string, Partial<TextStyle>>>({});
  const [backgroundSettings, setBackgroundSettings] = useState<Record<string, BackgroundSettings>>({});
  const [imageSettings, setImageSettings] = useState<Record<string, ImageSettings>>({});
  const [videoSettings, setVideoSettings] = useState<Record<string, VideoSettings>>({});
  const [shapeSettings, setShapeSettings] = useState<Record<string, ShapeSettings>>({});
  const [scrollerSettings, setScrollerSettings] = useState<Record<string, any>>({});
  const [widgetSettings, setWidgetSettings] = useState<Record<string, WidgetSettings>>({});
  const [templateSettings, setTemplateSettings] = useState<TemplateSettings>({
    opacity: 100,
    flipHorizontal: false,
    flipVertical: false,
    crop: { enabled: false, top: 0, right: 0, bottom: 0, left: 0 },
    animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' },
    autoPaginate: false,
    singleItemIndex: 1,
    startIndex: 1,
    endIndex: 5,
    pageInterval: 5
  });
  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);
  const [groupEditMode, _setGroupEditMode] = useState<string | null>(null);
  const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [pageName, setPageName] = useState('Page 01');
  const [isEditingTemplateName, setIsEditingTemplateName] = useState(false);
  const [tempTemplateName, setTempTemplateName] = useState('');
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Refs to store the latest styles state for async callbacks (avoids stale closures)
  const textStylesRef = useRef(textStyles);
  const imageSettingsRef = useRef(imageSettings);
  const scrollerSettingsRef = useRef(scrollerSettings);
  const textContentsRef = useRef(textContents);

  useEffect(() => {
    textStylesRef.current = textStyles;
  }, [textStyles]);

  useEffect(() => {
    imageSettingsRef.current = imageSettings;
  }, [imageSettings]);

  useEffect(() => {
    scrollerSettingsRef.current = scrollerSettings;
  }, [scrollerSettings]);

  useEffect(() => {
    textContentsRef.current = textContents;
  }, [textContents]);
  const [savingStatus, setSavingStatus] = useState<'saved' | 'saving' | 'error' | 'syncing'>('saved');

  const { data: customApiRes } = useGetCustomApisQuery();
  const customApis = useMemo(() => {
    const rawApis = customApiRes?.data || [];
    return rawApis.map(api => {
      try {
        const saved = localStorage.getItem(`wejha_api_injections_${api._id}`);
        if (saved) {
          const localInjections = JSON.parse(saved);
          let mergedData = api.customapidata;
          if (Array.isArray(api.customapidata)) {
            mergedData = [...api.customapidata];
            Object.assign(mergedData, localInjections);
          } else {
            mergedData = { ...(api.customapidata || {}), ...localInjections };
          }
          return {
            ...api,
            customapidata: mergedData
          };
        }
      } catch (e) { /* ignore */ }
      return api;
    });
  }, [customApiRes, textStyles]);

  const [showPlaceholders, setShowPlaceholders] = useState<boolean>(true);

  const resolvedBindings = useMemo(() => {
    console.log('[Editor] Recalculating resolvedBindings:', {
      textContentsKeys: Object.keys(textContents || {}),
      textStylesKeys: Object.keys(textStyles || {}),
      customApisCount: customApis?.length,
      showPlaceholders
    });
    const resolved = resolveTemplateBindings(
      textContents,
      textStyles,
      imageSettings,
      scrollerSettings,
      customApis,
      showPlaceholders
    );
    console.log('[Editor] resolvedBindings output:', resolved);
    return resolved;
  }, [textContents, textStyles, imageSettings, scrollerSettings, customApis, showPlaceholders]);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState<{ current: number, total: number, status: string, subStep: 'capturing' | 'uploading' | 'restoring' }>({ current: 0, total: 0, status: '', subStep: 'capturing' });
  const [history, setHistory] = useState<HistoryManager>(() => {
    const empty = createEmptyHistory();
    return {
      ...empty,
      present: {
        dynamicLayers: [],
        layerPositions: {},
        textContents: {},
        textStyles: {},
        shapeSettings: {},
        scrollerSettings: {},
        widgetSettings: {},
        backgroundSettings: {},
        imageSettings: {},
        videoSettings: {},
        templateSettings: {},
        timestamp: Date.now(),
        actionType: 'TEMPLATE_LOAD',
        actionDescription: 'Initial baseline'
      }
    };
  });
  const pendingActionsRef = useRef<Array<{ type: HistoryActionType; description?: string }>>([]);
  const [triggerHistoryCommit, setTriggerHistoryCommit] = useState(0);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackId, setPlaybackId] = useState(0); // Used to force-restart the animation loop timer
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const [activeLeftPanel, setActiveLeftPanel] = useState<'contents' | 'uploads' | 'templates' | 'apps' | 'playlist' | 'brand' | 'text' | 'elements' | null>(null);
  const [isAppSidepaneOpen, setIsAppSidepaneOpen] = useState(false);
  const [isCreateAppModalOpen, setIsCreateAppModalOpen] = useState(false);
  const [isLayersPanelOpen, setIsLayersPanelOpen] = useState(false);
  const [isDataBindingSidepaneOpen, setIsDataBindingSidepaneOpen] = useState(false);
  const [isEditingPageName, setIsEditingPageName] = useState(false);
  const [imageLoadCount, setImageLoadCount] = useState(0);
  const [apps, setApps] = useState(APPS_DATA);
  const [snapGuides, setSnapGuides] = useState<Array<{ type: 'vertical' | 'horizontal'; position: number; label?: string }>>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [isHoveringCanvas, setIsHoveringCanvas] = useState(false);
  const [layoutSettledTrigger, setLayoutSettledTrigger] = useState(0);

  // Dynamic Array Pagination State
  const [pendingArrayPagination, setPendingArrayPagination] = useState<{ count: number, prefix: string, customApiId: string } | null>(null);

  // Debounce array pagination clone effect
  useEffect(() => {
    if (pendingArrayPagination) {
      const timer = setTimeout(() => {
        setPages(prev => {
          // Bake current page
          const basePageData: PageData = {
            ...prev[currentPageIndex],
            dynamicLayers,
            layerPositions,
            textContents,
            textStyles,
            backgroundSettings,
            imageSettings,
            videoSettings,
            shapeSettings,
            scrollerSettings,
            widgetSettings,
            templateSettings,
            canvasDimensions,
            name: pageName,
          };

          const updatedPages = [...prev];
          if (updatedPages[currentPageIndex]) {
            updatedPages[currentPageIndex] = basePageData;
          }

          const targetApi = customApis.find(api => String(api._id) === String(pendingArrayPagination.customApiId));

          const newPages: PageData[] = [];
          for (let i = 1; i <= pendingArrayPagination.count; i++) {
            if (updatedPages.length + newPages.length >= 20) break;
            
            const cloned = clonePageWithMapping(basePageData);
            
            // Rewrite bindings
            const searchStr = pendingArrayPagination.prefix;
            let replaceStr: string;
            if (searchStr === '0') {
              replaceStr = `${i}`;
            } else if (searchStr.endsWith('.0')) {
              replaceStr = searchStr.substring(0, searchStr.length - 2) + `.${i}`;
            } else {
              replaceStr = searchStr.replace('0', `${i}`);
            }


            Object.keys(cloned.textStyles).forEach(k => {
              if (cloned.textStyles[k]?.binding?.apiKey) {
                const oldKey = cloned.textStyles[k].binding.apiKey;
                if (oldKey.includes(searchStr)) {
                  const newApiKey = oldKey.replace(searchStr, replaceStr);
                  cloned.textStyles[k].binding = {
                    ...cloned.textStyles[k].binding,
                    apiKey: newApiKey
                  };
                  if (targetApi && targetApi.customapidata) {
                    const val = resolveJsonPath(targetApi.customapidata, newApiKey);
                    if (val !== undefined && val !== null) {
                      cloned.textContents[k] = String(val);
                    } else if (cloned.textStyles[k].binding.fallbackValue) {
                      cloned.textContents[k] = cloned.textStyles[k].binding.fallbackValue;
                    }
                  }

                }
              }
            });
            Object.keys(cloned.imageSettings).forEach(k => {
              if (cloned.imageSettings[k]?.binding?.apiKey) {
                const oldKey = cloned.imageSettings[k].binding.apiKey;
                if (oldKey.includes(searchStr)) {
                  const newApiKey = oldKey.replace(searchStr, replaceStr);
                  cloned.imageSettings[k].binding = {
                    ...cloned.imageSettings[k].binding,
                    apiKey: newApiKey
                  };
                  if (targetApi && targetApi.customapidata) {
                    const val = resolveJsonPath(targetApi.customapidata, newApiKey);
                    if (val !== undefined && val !== null) {
                      cloned.imageSettings[k] = {
                        ...cloned.imageSettings[k],
                        url: String(val)
                      };
                    } else if (cloned.imageSettings[k].binding.fallbackValue) {
                      cloned.imageSettings[k] = {
                        ...cloned.imageSettings[k],
                        url: cloned.imageSettings[k].binding.fallbackValue
                      };
                    }
                  }

                }
              }
            });
            Object.keys(cloned.scrollerSettings).forEach(k => {
              if (cloned.scrollerSettings[k]?.binding?.apiKey) {
                const oldKey = cloned.scrollerSettings[k].binding.apiKey;
                if (oldKey.includes(searchStr)) {
                  const newApiKey = oldKey.replace(searchStr, replaceStr);
                  cloned.scrollerSettings[k].binding = {
                    ...cloned.scrollerSettings[k].binding,
                    apiKey: newApiKey
                  };
                  if (targetApi && targetApi.customapidata) {
                    const val = resolveJsonPath(targetApi.customapidata, newApiKey);
                    if (val !== undefined && val !== null) {
                      cloned.scrollerSettings[k] = {
                        ...cloned.scrollerSettings[k],
                        content: String(val)
                      };
                    } else if (cloned.scrollerSettings[k].binding.fallbackValue) {
                      cloned.scrollerSettings[k] = {
                        ...cloned.scrollerSettings[k],
                        content: cloned.scrollerSettings[k].binding.fallbackValue
                      };
                    }
                  }

                }
              }
            });

            cloned.name = `Page ${updatedPages.length + newPages.length + 1}`;
            newPages.push(cloned);
          }

          if (newPages.length > 0) {
            toast.success(`Dynamically created ${newPages.length} pages from array!`);
            commitToHistory('PAGE_ADD', `Generated ${newPages.length} pages from array`);
          }
          return [...updatedPages, ...newPages];
        });
        setPendingArrayPagination(null);
      }, 300); // 300ms debounce allows the canvas state to settle from handleAddBoundLayer
      return () => clearTimeout(timer);
    }
  }, [pendingArrayPagination, dynamicLayers, layerPositions, textContents, textStyles, imageSettings, scrollerSettings, widgetSettings, shapeSettings, videoSettings, backgroundSettings, customApis]);


  // Automatically trigger a layout re-calculation when selection changes
  // to account for DOM/font settlement delays (e.g. Bricolage font loading)
  useEffect(() => {
    if (selectedLayerIds.length > 0) {
      const timer = setTimeout(() => {
        setLayoutSettledTrigger(prev => prev + 1);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [selectedLayerIds]);

  /*
    // Real-time Dual JSON Debugging
    useEffect(() => {
      const editorData: TemplateData = {
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        previewUrl: selectedTemplate.previewUrl || '',
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        dynamicLayers,
        layerPositions,
        textContents,
        textStyles,
        imageSettings,
        videoSettings,
        shapeSettings,
        backgroundSettings,
        templateSettings,
        scrollerSettings,
      };
  
      const playerData = compilePlayerData(editorData);
  

    }, [
      dynamicLayers,
      layerPositions,
      textContents,
      textStyles,
      imageSettings,
      videoSettings,
      shapeSettings,
      backgroundSettings,
      templateSettings,
      scrollerSettings,
      selectedTemplate.id,
      selectedTemplate.name
    ]);
  */

  const [resizingLayer, setResizingLayer] = useState<{
    id: string;
    handle: string;
    startX: number;
    startY: number;
    initialLeft: number;
    initialTop: number;
    initialWidth: number;
    initialHeight: number;
    initialFontSize?: number;
  } | null>(null);

  const [rotatingLayer, setRotatingLayer] = useState<{
    id: string;
    startX: number;
    startY: number;
    initialRotation: number;
    centerX: number;
    centerY: number;
    initialMouseAngle: number;
  } | null>(null);

  const [draggingLayer, setDraggingLayer] = useState<{
    id: string;
    startX: number;
    startY: number;
    hasMoved: boolean;
    initialPositions: Record<string, { left: number; top: number; width?: number; height?: number }>;
    altKey: boolean;
  } | null>(null);

  const [clipboard, setClipboard] = useState<{
    layers: Layer[];
    positions: Record<string, any>;
    textContents: Record<string, string>;
    textStyles: Record<string, any>;
    shapeSettings: Record<string, any>;
    imageSettings: Record<string, any>;
    videoSettings: Record<string, any>;
    backgroundSettings: Record<string, any>;
    scrollerSettings: Record<string, any>;
    widgetSettings: Record<string, any>;
  } | null>(null);

  const {
    pages,
    setPages,
    currentPageIndex,
    setCurrentPageIndex,
    addPage,
    duplicatePage,
    switchPage,
    removePage
  } = usePageManager();

  // Sync pageName local state when currentPageIndex changes
  useEffect(() => {
    if (pages[currentPageIndex]) {
      setPageName(pages[currentPageIndex].name || `Page ${currentPageIndex + 1}`);
    }
  }, [currentPageIndex, pages]);

  useEffect(() => {
    if (isPlaying) {
      lastTimeRef.current = Date.now();
      const animate = () => {
        const now = Date.now();
        const delta = now - lastTimeRef.current;
        lastTimeRef.current = now;

        setCurrentTime(prev => {
          const next = prev + delta;
          // Loop after template duration
          return next > (templateSettings.duration || 20000) ? 0 : next;
        });
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlaying, playbackId]);

  const handleSwitchPage = useCallback((newIndex: number) => {
    if (newIndex === currentPageIndex) return;

    // 1. Bake current state into the current page entry
    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = {
          ...updated[currentPageIndex],
          dynamicLayers: [...dynamicLayers],
          layerPositions: { ...layerPositions },
          textContents: { ...textContents },
          textStyles: { ...textStyles },
          backgroundSettings: { ...backgroundSettings },
          imageSettings: { ...imageSettings },
          videoSettings: { ...videoSettings },
          shapeSettings: { ...shapeSettings },
          scrollerSettings: { ...scrollerSettings },
          widgetSettings: { ...widgetSettings },
          templateSettings: { ...templateSettings },
          canvasDimensions: { ...canvasDimensions },
          name: pageName,
        };
      }
      return updated;
    });

    // 2. Perform the switch
    switchPage(newIndex);
  }, [
    currentPageIndex,
    switchPage,
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    templateSettings
  ]);

  const handleAddPage = useCallback(() => {
    if (pages.length >= 20) {
      toast.error('Maximum limit of 20 pages reached.');
      return;
    }
    // 1. Bake current page state first
    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = {
          ...updated[currentPageIndex],
          dynamicLayers,
          layerPositions,
          textContents,
          textStyles,
          backgroundSettings,
          imageSettings,
          videoSettings,
          shapeSettings,
          scrollerSettings,
          widgetSettings,
          templateSettings,
          canvasDimensions: { ...canvasDimensions },
          name: pageName,
        };
      }
      return updated;
    });

    // 2. Add the new blank page with a standard 'root' container
    const newIndex = addPage({
      opacity: 100,
      flipHorizontal: false,
      flipVertical: false,
      crop: { enabled: false, top: 0, right: 0, bottom: 0, left: 0 },
      animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
    });

    // Ensure the new page has the mandatory root layer for consistency
    setPages(prev => {
      const updated = [...prev];
      if (updated[newIndex]) {
        updated[newIndex] = {
          ...updated[newIndex],
          dynamicLayers: [{
            id: 'root',
            name: 'Canvas',
            type: 'frame',
            className: 'bg-white shadow-lg overflow-hidden shrink-0 relative',
            style: { backgroundColor: '#FFFFFF', width: '100%', height: '100%' },
            children: []
          }],
          layerPositions: {
            root: { left: 0, top: 0, width: CANVAS_WIDTH, height: CANVAS_HEIGHT } as any
          }
        };
      }
      return updated;
    });

    // 3. Switch to it
    setCurrentPageIndex(newIndex);
  }, [
    addPage,
    currentPageIndex,
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    videoSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    templateSettings,
    setCurrentPageIndex
  ]);

  const handleDuplicatePage = useCallback((index = currentPageIndex) => {
    if (pages.length >= 20) {
      toast.error('Maximum limit of 20 pages reached.');
      return;
    }
    // 1. Prepare baked data for the current page to ensure the clone is perfect
    const bakedData: PageData = {
      ...pages[currentPageIndex],
      dynamicLayers: [...dynamicLayers],
      layerPositions: { ...layerPositions },
      textContents: { ...textContents },
      textStyles: { ...textStyles },
      backgroundSettings: { ...backgroundSettings },
      imageSettings: { ...imageSettings },
      videoSettings: { ...videoSettings },
      shapeSettings: { ...shapeSettings },
      scrollerSettings: { ...scrollerSettings },
      widgetSettings: { ...widgetSettings },
      templateSettings: { ...templateSettings },
      canvasDimensions: { ...canvasDimensions },
      name: pageName,
    };

    // 2. Update current page in state
    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = bakedData;
      }
      return updated;
    });

    // 3. Duplicate using the baked data
    const nextIndex = duplicatePage(index, bakedData);
    if (nextIndex !== undefined) {
      setCurrentPageIndex(nextIndex);
    }
  }, [
    duplicatePage,
    currentPageIndex,
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    videoSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    templateSettings,
    pages,
    setCurrentPageIndex
  ]);



  const handleDeletePage = useCallback((index = currentPageIndex) => {
    if (pages.length <= 1) {
      toast.error('A template must have at least one page.');
      return;
    }
    removePage(index);
    // Note: removePage internally handles bounds checking and current page repositioning
  }, [pages.length, removePage, currentPageIndex]);

  // --- End Consolidated State Declarations ---

  // Calculate canvas scale to fit within safe area
  useEffect(() => {
    const calculateScale = () => {
      if (hasManuallyZoomed) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Safe area constraints
      const leftSidebarWidth = viewportWidth >= 1280 ? 130 : viewportWidth >= 1024 ? 114 : 98;
      const topOffset = 140; // Original offset
      const timelineHeight = isTimelineOpen ? 250 : 0;
      const bottomControlsHeight = 80 + timelineHeight; // Zoom bar + Timeline
      const rightPanelWidth = 80;
      const horizontalPadding = 40;
      const verticalPadding = 40;

      // Calculate available space
      const availableWidth = viewportWidth - leftSidebarWidth - rightPanelWidth - horizontalPadding;
      const availableHeight = viewportHeight - topOffset - bottomControlsHeight - verticalPadding;

      // Calculate scale to fit (uniform scaling)
      const scaleX = availableWidth / CANVAS_WIDTH;
      const scaleY = availableHeight / CANVAS_HEIGHT;
      const scale = Math.min(scaleX, scaleY, 1); // Reverted to max scale 1.0
      setCanvasScale(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);

    return () => {
      window.removeEventListener('resize', calculateScale);
    };
  }, [hasManuallyZoomed, CANVAS_WIDTH, CANVAS_HEIGHT]);

  // ESC key to close preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPreviewOpen) {
        setIsPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreviewOpen]);

  useEffect(() => {
    // Only redirect if we are NOT using local state (props) AND no redux state exists
    if (!initialTemplate && !reduxSelectedTemplate) {
      // dispatch(navigateTo('templates'));
    }
  }, [initialTemplate, reduxSelectedTemplate, dispatch]);

  const handleZoomIn = useCallback(() => {
    setHasManuallyZoomed(true);
    setCanvasScale(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setHasManuallyZoomed(true);
    setCanvasScale(prev => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleZoomReset = useCallback(() => {
    setHasManuallyZoomed(true);
    setCanvasScale(1);
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/templates');
    }
  };

  // Compatibility: single selection mode (last selected item for properties panel)
  const selectedLayerId = selectedLayerIds.length > 0 ? selectedLayerIds[selectedLayerIds.length - 1] : null;

  // findSelectableId helper: Finds the topmost selectable ancestor (usually a group)
  // This ensures that clicking a member of a group correctly selects the entire group
  // unless we are in group edit mode.
  const findSelectableId = useCallback((layerId: string) => {
    if (layerId === 'root') return layerId;
    if (groupEditMode) return layerId;

    const findTopmostGroup = (layers: Layer[], targetId: string, currentGroup: string | null = null): string | null => {
      for (const layer of layers) {
        if (layer.id === targetId) return currentGroup || targetId;
        if (layer.children) {
          const nextGroup = (layer.type === 'group' && !currentGroup) ? layer.id : currentGroup;
          const found = findTopmostGroup(layer.children, targetId, nextGroup);
          if (found) return found;
        }
      }
      return null;
    };

    return findTopmostGroup(dynamicLayers, layerId) || layerId;
  }, [dynamicLayers, groupEditMode]);

  const setSelectedLayerId = (id: string | null) => {
    const selectableId = id ? findSelectableId(id) : null;
    setSelectedLayerIds(selectableId ? [selectableId] : []);
    if (selectableId !== selectedLayerId) {
      setIsAppSidepaneOpen(false);
    }
    if (id) {
      const layer = findLayerById(dynamicLayers, id);
      if (layer?.isApp) {
        setIsLayersPanelOpen(false);
        setActiveLeftPanel(null);
        setIsDataBindingSidepaneOpen(false);
      }
    }
  };

  const lastClickRef = useRef<{ time: number, id: string | null }>({ time: 0, id: null });

  const handleImageLoad = () => setImageLoadCount(prev => prev + 1);

  const handleScrollerSettingsChange = (id: string, updates: any) => {
    setScrollerSettings(prev => {
      if (!prev[id] && !updates.content && !updates.fontFamily && !updates.binding) return prev;
      const current = prev[id] || { ...DEFAULT_SCROLLER_SETTINGS };
      return { ...prev, [id]: { ...current, ...updates } };
    });

    setWidgetSettings(ws => {
      if (!ws[id]) return ws;
      const currentWidget = ws[id];
      return {
        ...ws,
        [id]: {
          ...currentWidget,
          ...updates,
          replacements: getWidgetReplacements(currentWidget, updates)
        }
      };
    });

    if (updates.content !== undefined) {
      setDynamicLayers(prev => updateLayerDeep(prev, `${id}-text`, { content: updates.content }));
    }
    commitToHistory('LAYER_EDIT', 'Modified scroller settings');
  };

  const handleWidgetSettingsChange = (id: string, updates: any) => {
    setWidgetSettings(prev => {
      const current = prev[id] || { htmlUrl: '', jsUrl: '', replacements: [] };
      const next = { ...current, ...updates };
      return {
        ...prev,
        [id]: {
          ...next,
          replacements: getWidgetReplacements(next)
        }
      };
    });
    commitToHistory('LAYER_EDIT', 'Modified widget settings');
  };

  useEffect(() => {
    if (!draggingLayer && !resizingLayer) {
      // Force a re-calculation after a short delay to ensure DOM has fully settled
      const timer = setTimeout(() => {
        setLayoutSettledTrigger(prev => prev + 1);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [draggingLayer === null, resizingLayer === null]);

  const currentTemplateData = useMemo(() => {
    return {
      id: selectedTemplate.id,
      name: selectedTemplate.name,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      dynamicLayers: [...dynamicLayers],
      layerPositions: { ...layerPositions },
      textContents: { ...textContents },
      textStyles: { ...textStyles },
      backgroundSettings: { ...backgroundSettings },
      imageSettings: { ...imageSettings },
      videoSettings: { ...videoSettings },
      shapeSettings: { ...shapeSettings },
      scrollerSettings: { ...scrollerSettings },
      widgetSettings: { ...widgetSettings },
      templateSettings: { ...templateSettings },
      pages: pages.map(p => ({
        ...p,
        // Ensure current page is up to date in the pages array if we are looking at it
        ...(p.id === pages[currentPageIndex]?.id ? {
          dynamicLayers: [...dynamicLayers],
          layerPositions: { ...layerPositions },
          textContents: { ...textContents },
          textStyles: { ...textStyles },
          backgroundSettings: { ...backgroundSettings },
          imageSettings: { ...imageSettings },
          videoSettings: { ...videoSettings },
          shapeSettings: { ...shapeSettings },
          scrollerSettings: { ...scrollerSettings },
          widgetSettings: { ...widgetSettings },
          templateSettings: { ...templateSettings },
        } : {})
      })),
      currentPageIndex
    };
  }, [
    selectedTemplate.id,
    selectedTemplate.name,
    CANVAS_WIDTH,
    CANVAS_HEIGHT,
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    videoSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    templateSettings,
    pages,
    currentPageIndex
  ]);

  // Calculate combined bounds for all selected elements (supports multi-selection)
  const selectionBounds = useMemo(() => {
    if (selectedLayerIds.length === 0 || !canvasRef.current) {
      return null;
    }

    // Special case: Single selection (Selection box rotates WITH the element)
    if (selectedLayerIds.length === 1) {
      const id = selectedLayerIds[0];
      if (id === 'root') return null;

      const layer = findLayerById(dynamicLayers, id);
      if (!layer) return null;

      const pos = layerPositions ? layerPositions[id] : undefined;
      const isText = layer.type === 'text';

      // Use absolute coordinates for nested elements (groups) to ensure the bounding box
      // shows at the correct canvas position rather than a relative offset.
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const absPos = getAbsolutePosition(id, canvasRect, canvasScale);

      let left = absPos?.left ?? (pos?.left ?? parseFloat(String(layer.style?.left || '0').replace('px', '')));
      let top = absPos?.top ?? (pos?.top ?? parseFloat(String(layer.style?.top || '0').replace('px', '')));
      let width = absPos?.width ?? (pos?.width ?? parseFloat(String(layer.style?.width || '200').replace('px', '')));
      let height = absPos?.height ?? (pos?.height ?? parseFloat(String(layer.style?.height || '40').replace('px', '')));

      // Fallback to style
      if (left === undefined) left = parseFloat(String(layer.style?.left || '0').replace('px', ''));
      if (top === undefined) top = parseFloat(String(layer.style?.top || '0').replace('px', ''));

      // For text elements, we might need to measure for hugging
      if (isText && (width === undefined || (!draggingLayer && !resizingLayer))) {
        const element = document.getElementById(id);
        if (element) {
          const textNode = element.querySelector('[data-text-node="true"]') as HTMLElement;
          if (textNode) {
            // Use offsetWidth and offsetHeight for accurate dimensions that do not suffer from AABB distortion when rotated.
            // These properties return logical pixels in the element's coordinate space, unaffected by canvas zoom/scale.
            const measuredWidth = textNode.offsetWidth;
            const measuredHeight = textNode.offsetHeight;

            // If measured size is effectively zero (DOM not ready), use a temporary fallback
            // to prevent "half-width" boxes until the layout trigger settles.
            const horizontalSafety = (measuredWidth < 5) ? 200 : measuredWidth;
            const verticalSafety = (measuredHeight < 5) ? 40 : measuredHeight;

            // For text elements, we favor the actual DOM measurement (horizontalSafety/verticalSafety) 
            // when not actively resizing. This ensures the selection box stays tightly locked to 
            // the content as the user types or adjusts font properties.
            // If we ARE resizing, we respect the state-managed width/height to show the target bounds.
            width = (isText && !resizingLayer) ? horizontalSafety : (pos?.width !== undefined ? pos.width : horizontalSafety);
            height = (isText && !resizingLayer) ? verticalSafety : (pos?.height !== undefined ? pos.height : verticalSafety);
          }
        }
      }

      // Padding logic remains the same (removed redundant fallbacks as they are handled above)

      // Apply consistent UI padding to the selection box for ALL elements (Text, Shape, Image, etc.)
      // Ultra-Maximal UI padding for text elements (Hero-sized)
      const horizontalPadding = isText ? 32 : 12;
      const verticalPadding = isText ? 20 : 12;

      const finalWidth = width + (horizontalPadding * 2);
      const finalHeight = height + (verticalPadding * 2);
      const finalLeft = (left ?? 0) - horizontalPadding;
      const finalTop = (top ?? 0) - verticalPadding;

      // Image visible bounds adjustment
      if (layer.type === 'image' && (imageSettings[id]?.objectFit || DEFAULT_IMAGE_SETTINGS.objectFit) === 'contain') {
        const element = document.getElementById(id);
        const img = element?.querySelector('img');
        if (img && img.complete && img.naturalWidth && img.naturalHeight) {
          const visible = getImageVisibleBounds(width, height, img.naturalWidth, img.naturalHeight, 'contain');
          return {
            left: finalLeft + visible.left,
            top: finalTop + visible.top,
            width: visible.width + (horizontalPadding * 2),
            height: visible.height + (verticalPadding * 2),
            rotation: pos?.rotation || 0
          };
        }
      }

      return {
        left: finalLeft,
        top: finalTop,
        width: finalWidth,
        height: finalHeight,
        rotation: pos?.rotation || 0
      };
    }

    // Multi-selection case: Calculate axis-aligned bounding box (AABB)
    let minLeft = Infinity, minTop = Infinity;
    let maxRight = -Infinity, maxBottom = -Infinity;
    let found = false;

    selectedLayerIds.forEach(id => {
      if (id === 'root') return;

      const element = document.getElementById(id);
      if (element) {
        found = true;
        const canvasRect = canvasRef.current!.getBoundingClientRect();
        const rect = element.getBoundingClientRect(); // This IS the AABB (expanded)

        const left = (rect.left - canvasRect.left) / canvasScale;
        const top = (rect.top - canvasRect.top) / canvasScale;
        const width = rect.width / canvasScale;
        const height = rect.height / canvasScale;

        minLeft = Math.min(minLeft, left);
        minTop = Math.min(minTop, top);
        maxRight = Math.max(maxRight, left + width);
        maxBottom = Math.max(maxBottom, top + height);
      }
    });

    if (!found || minLeft === Infinity) return null;

    return {
      left: minLeft,
      top: minTop,
      width: maxRight - minLeft,
      height: maxBottom - minTop,
      rotation: 0 // Multi-selection box is always axis-aligned
    };
  }, [selectedLayerIds, layerPositions, canvasScale, dynamicLayers, textContents, textStyles, imageSettings, imageLoadCount, draggingLayer === null, resizingLayer === null, rotatingLayer === null, layoutSettledTrigger]);

  // High-precision dimension and alignment synchronization for text elements.
  // This effect ensures that as text content or styles (font size, family) change,
  // the state-managed width, height, and coordinates (left, top) are updated
  // to maintain visual alignment (e.g., center-based growth) and accurate bounding boxes.
  useEffect(() => {
    // Only auto-sync when the user isn't actively manipulating the element with their mouse.
    if (draggingLayer || resizingLayer || rotatingLayer || !canvasRef.current) return;

    const textLayerIds = Object.keys(textContents);
    if (textLayerIds.length === 0) return;

    let hasUpdates = false;
    const nextPositions = { ...layerPositions };

    textLayerIds.forEach(id => {
      // CRITICAL: Skip synchronization for the layer being actively edited to 
      // prevent the "width-collapse" loop where typing -> shrink -> wrap -> shrink occur.
      if (id === editingTextId) return;

      const element = document.getElementById(id);
      const textNode = element?.querySelector('[data-text-node="true"]');
      if (!element || !textNode) return;

      const current = layerPositions[id];
      if (!current) return;

      const textHtmlNode = textNode as HTMLElement;
      const measuredWidth = Math.max(textHtmlNode.offsetWidth, 10); // Safety floor
      const measuredHeight = Math.max(textHtmlNode.offsetHeight, 10);

      // Use a strict threshold to determine if dimensions have actually changed
      const widthDiff = Math.abs((current.width || 0) - measuredWidth);
      const heightDiff = Math.abs((current.height || 0) - measuredHeight);

      if (widthDiff > 0.5 || heightDiff > 0.5) {
        hasUpdates = true;
        const style = textStyles[id] || {};
        const isCentered = style.textAlign === 'center';
        const isRight = style.textAlign === 'right';

        let newLeft = current.left;
        let newTop = current.top;

        // Alignment-Aware Repositioning:
        // When text grows, we shift its anchor to maintain its visual center or right-alignment.
        // This creates the "Zooming from Center" feel the user expects for centered text.
        // We use its starting width (even if undefined) to calculate the shift relative to anchor.
        const baseWidth = current.width ?? (isCentered ? 200 : 0);
        const baseHeight = current.height ?? (style.verticalAlign === 'center' ? 40 : 0);

        if (isCentered) {
          newLeft = current.left + (baseWidth - measuredWidth) / 2;
        } else if (isRight) {
          newLeft = current.left + (baseWidth - measuredWidth);
        }

        // Vertical alignment centering (center-center growth)
        if (style.verticalAlign === 'center') {
          newTop = current.top + (baseHeight - measuredHeight) / 2;
        }

        nextPositions[id] = {
          ...current,
          left: newLeft,
          top: newTop,
          width: measuredWidth,
          height: measuredHeight
        };
      }
    });

    if (hasUpdates) {
      setLayerPositions(nextPositions);
    }
  }, [textContents, textStyles, canvasScale, layoutSettledTrigger, draggingLayer === null, resizingLayer === null, rotatingLayer === null, editingTextId]);

  const isUndoRedoAction = useRef(false);



  const handleAppCreated = (appData: any) => {
    const newAppId = `app_${Date.now()}`;
    const newApp = {
      id: newAppId,
      displayName: appData.content || 'New App',
      category: 'general',
      color: 'blue',
      icon: (
        <div className="absolute left-[8px] overflow-clip size-[32px] top-[4px] bg-[#f0f2f5] rounded-full flex items-center justify-center" data-name="Apps">
          <svg className="w-[20px] h-[20px]" fill="none" viewBox="0 0 56 56">
            <path d="M19.8756 11.1322C19.6636 10.6812 19.3277 10.2999 18.907 10.0328C18.4863 9.76573 17.9983 9.62389 17.5 9.62389C17.0017 9.62389 16.5137 9.76573 16.093 10.0328C15.6723 10.2999 15.3364 10.6812 15.1244 11.1322L1.12437 40.8822C0.844116 41.5096 0.821051 42.2219 1.06013 42.8661C1.29921 43.5103 1.78138 44.0351 2.40309 44.3278C3.02481 44.6205 3.7365 44.6577 4.38535 44.4315C5.0342 44.2052 5.56847 43.7336 5.87344 43.1178L9.28375 35.875H25.7162L29.1244 43.1178C29.4208 43.7479 29.9554 44.2343 30.6106 44.4702C31.2657 44.7061 31.9878 44.6721 32.6178 44.3756C33.2479 44.0792 33.7343 43.5446 33.9702 42.8894C34.2061 42.2343 34.1721 41.5122 33.8756 40.8822L19.8756 11.1322ZM11.7556 30.625L17.5 18.4144L23.2466 30.625H11.7556ZM43.75 18.375C40.7203 18.375 38.3316 19.2194 36.6516 20.8863C36.3874 21.1242 36.1743 21.4134 36.0253 21.7363C35.8762 22.0591 35.7943 22.4089 35.7845 22.7643C35.7748 23.1197 35.8373 23.4735 35.9683 23.804C36.0994 24.1346 36.2962 24.435 36.5469 24.6872C36.7976 24.9394 37.0969 25.138 37.4267 25.271C37.7565 25.404 38.1098 25.4686 38.4653 25.4609C38.8208 25.4532 39.171 25.3734 39.4947 25.2263C39.8184 25.0791 40.1089 24.8678 40.3484 24.605C41.0047 23.9488 42.1553 23.6162 43.75 23.6162C46.1562 23.6162 48.125 25.1869 48.125 27.1162V28.0766C46.7536 27.4439 45.2603 27.1191 43.75 27.125C38.4431 27.125 34.125 31.0494 34.125 35.875C34.125 40.7006 38.4431 44.625 43.75 44.625C45.4259 44.6291 47.0776 44.2252 48.5625 43.4481C48.8741 43.9178 49.3286 44.2748 49.8588 44.4662C50.3889 44.6575 50.9666 44.6732 51.5064 44.5108C52.0461 44.3484 52.5193 44.0165 52.8558 43.5643C53.1923 43.1122 53.3744 42.5637 53.375 42V27.125C53.375 22.2994 49.0569 18.375 43.75 18.375ZM43.75 39.375C41.3437 39.375 39.375 37.8044 39.375 35.875C39.375 33.9456 41.3437 32.375 43.75 32.375C46.1562 32.375 48.125 33.9456 48.125 35.875C48.125 37.8044 46.1562 39.375 43.75 39.375Z" fill="#101928" />
          </svg>
        </div>
      )
    };

    setApps([newApp, ...apps]);
    setIsCreateAppModalOpen(false);
  };



  const handleUpdateLayer = useCallback((layerId: string, updates: Partial<Layer>) => {
    setDynamicLayers(prev => {
      const updateRecursive = (layers: Layer[]): Layer[] => {
        return layers.map(layer => {
          if (layer.id === layerId) {
            return { ...layer, ...updates };
          }
          if (layer.children) {
            return { ...layer, children: updateRecursive(layer.children) };
          }
          return layer;
        });
      };
      return updateRecursive(prev);
    });
  }, []);



  const handleTimelineLayerUpdate = (layerId: string, updates: Partial<Layer>) => {
    const layer = findLayerById(dynamicLayers, layerId);
    if (!layer) return;

    // 1. Update the base layer (for shapes, groups, etc.)
    handleUpdateLayer(layerId, updates);

    // 2. Also update specialized settings maps which take precedence in the renderer
    const anim = updates.animation;
    if (!anim) return;

    // CRITICAL FIX: Strip baked keyframes before writing to settings stores.
    // TimelinePanel's track builder calls getEffectiveKeyframes() to bake preset keyframes
    // (e.g. rise, fade, zoom) onto track.animation for display. If we store those baked
    // keyframes, getEffectiveKeyframes() returns early with the OLD keyframes even after
    // the user changes the animation type — making type changes appear to have no effect.
    // Only user-explicitly-added keyframes (via "+ KEY" in Advanced mode) should persist.
    const { keyframes: _bakedKeyframes, ...animWithoutBakedKeyframes } = anim as any;
    const cleanAnim = animWithoutBakedKeyframes;

    if (layer.type === 'text') {
      setTextStyles(prev => ({
        ...prev,
        [layerId]: {
          ...prev[layerId],
          animation: cleanAnim as any
        }
      }));
    } else if (layer.type === 'image') {
      setImageSettings(prev => ({
        ...prev,
        [layerId]: {
          ...prev[layerId],
          animation: cleanAnim as any
        }
      }));
    } else if (layer.type === 'video') {
      setVideoSettings(prev => ({
        ...prev,
        [layerId]: {
          ...prev[layerId],
          animation: cleanAnim as any
        }
      }));
    } else {
      // Shapes and others use shapeSettings for animation
      setShapeSettings(prev => {
        const current = prev[layerId] || {
          fill: { enabled: true, color: '#1e293b' },
          stroke: { enabled: true, color: '#000000', width: 2, style: 'solid' },
          cornerRadius: 0,
          rotation: 0,
          opacity: 1,
          blur: 0
        };
        return {
          ...prev,
          [layerId]: {
            ...current,
            animation: cleanAnim as any
          }
        };
      });
    }
  };



  // Consolidate initialization logic
  useEffect(() => {
    const init = async () => {
      // Reset load state to ensure fresh initialization of pages and other states
      setIsInitialLoadComplete(false);
      setPages([]);
      setCurrentPageIndex(0);
      setSelectedLayerIds([]);
      let initialSnapshot: HistoryState;
      const initContents: Record<string, string> = {};

      // Helper to collect initial text content
      const collectTextContent = (layers: Layer[]) => {
        layers.forEach(layer => {
          if (layer.type === 'text' && layer.content) {
            initContents[layer.id] = layer.content;
          }
          if (layer.children) {
            collectTextContent(layer.children);
          }
        });
      };
      // Default to a blank canvas if no template is provided
      const defaultLayers: Layer[] = [{
        id: 'root',
        name: 'Canvas',
        type: 'frame',
        className: 'bg-white shadow-lg overflow-hidden shrink-0 relative',
        style: { backgroundColor: '#FFFFFF', width: '100%', height: '100%' },
        children: []
      }];
      collectTextContent(defaultLayers);
      setTextContents(initContents);

      try {
        // Source 1: Fresh Dynamic Data from Library (API/Redux) or Saved Design
        const rawSourceData = normalizeTemplateData(selectedTemplate);

        if (rawSourceData && rawSourceData.dynamicLayers && rawSourceData.dynamicLayers.length > 0) {
          const layers = rawSourceData.dynamicLayers;
          const contents = rawSourceData.textContents;

          setDynamicLayers(layers);
          setTextContents(contents);
          setLayerPositions(rawSourceData.layerPositions);
          setTextStyles(rawSourceData.textStyles as any);
          setImageSettings(rawSourceData.imageSettings as any);
          setShapeSettings(rawSourceData.shapeSettings as any);
          setBackgroundSettings(rawSourceData.backgroundSettings as any);
          setScrollerSettings(rawSourceData.scrollerSettings as any);
          setWidgetSettings(rawSourceData.widgetSettings as any);
          setTemplateSettings(rawSourceData.templateSettings as any);
          if (rawSourceData.name) setPageName(rawSourceData.name);

          // Support multi-page templates from API
          if (rawSourceData.pages && rawSourceData.pages.length > 0) {
            const pageIndex = rawSourceData.currentPageIndex || 0;
            const healedPages = [...rawSourceData.pages];
            if (healedPages[pageIndex]) {
              healedPages[pageIndex] = {
                ...healedPages[pageIndex],
                dynamicLayers: layers,
                layerPositions: rawSourceData.layerPositions,
                textContents: contents,
                textStyles: rawSourceData.textStyles as any,
                backgroundSettings: rawSourceData.backgroundSettings as any,
                imageSettings: rawSourceData.imageSettings as any,
                shapeSettings: rawSourceData.shapeSettings as any,
                scrollerSettings: rawSourceData.scrollerSettings as any,
                widgetSettings: rawSourceData.widgetSettings as any,
                templateSettings: rawSourceData.templateSettings
              };
            }
            setPages(healedPages);
            if (rawSourceData.currentPageIndex !== undefined) {
              setCurrentPageIndex(rawSourceData.currentPageIndex);
            }
          }

          initialSnapshot = {
            id: selectedTemplate?.id || '',
            dynamicLayers: layers,
            layerPositions: rawSourceData.layerPositions,
            textContents: contents,
            textStyles: rawSourceData.textStyles as any,
            shapeSettings: rawSourceData.shapeSettings as any,
            backgroundSettings: rawSourceData.backgroundSettings as any,
            imageSettings: rawSourceData.imageSettings as any,
            scrollerSettings: rawSourceData.scrollerSettings as any,
            widgetSettings: rawSourceData.widgetSettings as any,
            templateSettings: rawSourceData.templateSettings as any,
            canvasDimensions: rawSourceData.canvasDimensions || { ...canvasDimensions },
            currentPageIndex: rawSourceData.currentPageIndex || 0,
            pageName: rawSourceData.name || 'New Design',
            timestamp: Date.now(),
            actionType: 'TEMPLATE_LOAD',
            actionDescription: 'Loaded dynamic template'
          };
        }
        // Source 2: Saved Session (Auto-save)
        else {
          const state = await getState(selectedTemplate?.id || 'default');
          if (state && state.dynamicLayers && state.dynamicLayers.length > 0) {
            // Heal any missing bindings from layer objects in the restored local draft state
            const healedTextStyles = { ...(state.textStyles || {}) };
            const healedImageSettings = { ...(state.imageSettings || {}) };
            const healedScrollerSettings = { ...(state.scrollerSettings || {}) };

            const syncRestoredBindings = (ls: any[]) => {
              ls.forEach(l => {
                if (l.binding) {
                  if (l.type === 'text') {
                    healedTextStyles[l.id] = { ...(healedTextStyles[l.id] || {}), binding: l.binding };
                  } else if (l.type === 'image') {
                    healedImageSettings[l.id] = { ...(healedImageSettings[l.id] || {}), binding: l.binding };
                  } else if (l.type === 'scroller' || l.isApp) {
                    healedScrollerSettings[l.id] = { ...(healedScrollerSettings[l.id] || {}), binding: l.binding };
                  }
                }
                if (l.children) {
                  syncRestoredBindings(l.children);
                }
              });
            };
            syncRestoredBindings(state.dynamicLayers);

            setDynamicLayers(state.dynamicLayers);
            if (state.layerPositions) setLayerPositions(state.layerPositions);
            if (state.textContents) setTextContents(state.textContents);
            setTextStyles(healedTextStyles as any);
            if (state.backgroundSettings) setBackgroundSettings(state.backgroundSettings);
            setImageSettings(healedImageSettings as any);
            if (state.shapeSettings) setShapeSettings(state.shapeSettings as any);
            setScrollerSettings(healedScrollerSettings as any);
            if (state.widgetSettings) setWidgetSettings(state.widgetSettings as any);
            if (state.pageName) setPageName(state.pageName);
            if (state.templateSettings) setTemplateSettings(state.templateSettings);
            if (state.pages && state.pages.length > 0) {
              const pIndex = state.currentPageIndex || 0;
              const healedPages = [...state.pages];
              if (healedPages[pIndex]) {
                healedPages[pIndex] = {
                  ...healedPages[pIndex],
                  dynamicLayers: state.dynamicLayers || [],
                  layerPositions: state.layerPositions || {},
                  textContents: state.textContents || {},
                  textStyles: state.textStyles || {},
                  backgroundSettings: state.backgroundSettings || {},
                  imageSettings: state.imageSettings || {},
                  shapeSettings: state.shapeSettings || {},
                  scrollerSettings: state.scrollerSettings || {},
                  widgetSettings: state.widgetSettings || {},
                  templateSettings: state.templateSettings || {},
                  name: state.pageName || healedPages[pIndex].name
                };
              }
              setPages(healedPages);
              if (state.currentPageIndex !== undefined) {
                setCurrentPageIndex(state.currentPageIndex);
              }
            }

            initialSnapshot = {
              dynamicLayers: state.dynamicLayers || [],
              layerPositions: state.layerPositions || {},
              textContents: state.textContents || initContents,
              textStyles: state.textStyles || {},
              shapeSettings: state.shapeSettings || {},
              backgroundSettings: state.backgroundSettings || {},
              imageSettings: state.imageSettings || {},
              scrollerSettings: state.scrollerSettings || {},
              canvasDimensions: state.canvasDimensions || { ...canvasDimensions },
              currentPageIndex: state.currentPageIndex || 0,
              pageName: state.pageName || 'Page 01',
              timestamp: Date.now(),
              actionType: 'TEMPLATE_LOAD',
              actionDescription: 'Resumed from storage'
            };
          }
          // Default Fallback: Blank Canvas
          else {
            const customRoot = [{
              id: 'root',
              name: 'Canvas',
              type: 'frame',
              className: `w-[${CANVAS_WIDTH}px] h-[${CANVAS_HEIGHT}px] bg-white shadow-lg overflow-hidden shrink-0 relative`,
              style: { backgroundColor: '#FFFFFF', width: `${CANVAS_WIDTH}px`, height: `${CANVAS_HEIGHT}px` },
              children: []
            }] as Layer[];
            setDynamicLayers(customRoot);

            initialSnapshot = {
              dynamicLayers: customRoot,
              layerPositions: {},
              textContents: {},
              textStyles: {},
              shapeSettings: {},
              backgroundSettings: {},
              imageSettings: {},
              scrollerSettings: {},
              widgetSettings: {},
              templateSettings: {
                opacity: 100,
                flipHorizontal: false,
                flipVertical: false,
                crop: { enabled: false, top: 0, right: 0, bottom: 0, left: 0 },
                animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
              },
              canvasDimensions: { ...canvasDimensions },
              currentPageIndex: 0,
              pageName: 'Page 01',
              timestamp: Date.now()
            };
          }
        }
      } catch (e) {
        console.error('Failed to load state', e);
        // Fallback if something went wrong
        initialSnapshot = {
          dynamicLayers: defaultLayers,
          layerPositions: {},
          textContents: initContents,
          textStyles: {},
          shapeSettings: {},
          backgroundSettings: {},
          imageSettings: {},
          scrollerSettings: {},
          widgetSettings: {},
          templateSettings: {
            opacity: 100,
            flipHorizontal: false,
            flipVertical: false,
            crop: { enabled: false, top: 0, right: 0, bottom: 0, left: 0 },
            animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
          },
          canvasDimensions: { ...canvasDimensions },
          currentPageIndex: 0,
          pageName: 'Page 01',
          timestamp: Date.now()
        };
      }

      // Only reset history if it's a completely different design
      setHistory(prev => {
        const currentDesignId = selectedTemplate?.id || '';
        if (prev.present?.id === currentDesignId) {
          return prev;
        }
        return {
          past: [],
          present: initialSnapshot,
          future: []
        };
      });
      setIsInitialLoadComplete(true);
    };

    init();
  }, [selectedTemplate?.id]);

  // Auto-mapping effect on load
  useEffect(() => {
    if (isInitialLoadComplete && customApis.length > 0 && selectedTemplate) {
      const templateCategory = selectedTemplate.category;
      if (!templateCategory || templateCategory === 'general') return;

      const matchingApi = customApis.find(api => api.api_type === templateCategory.toLowerCase());
      if (!matchingApi) return;

      const hasAnyBinding = 
        Object.values(textStyles).some((s: any) => s?.binding) ||
        Object.values(imageSettings).some((s: any) => s?.binding) ||
        Object.values(scrollerSettings).some((s: any) => s?.binding);

      if (!hasAnyBinding) {

        const result = autoMapTemplate(
          dynamicLayers,
          textStyles,
          imageSettings,
          scrollerSettings,
          matchingApi
        );

        if (result.count > 0) {
          setTextStyles(result.textStyles);
          setImageSettings(result.imageSettings);
          setScrollerSettings(result.scrollerSettings);
          toast.success(`Automatically mapped ${result.count} layers to "${matchingApi.name_custom}" API!`, {
            duration: 4000,
            icon: '🔌'
          });
        }
      }
    }
  }, [isInitialLoadComplete, customApis, selectedTemplate]);


  // Multi-page foundation: Sync initial load to pages array
  useEffect(() => {
    if (isInitialLoadComplete && pages.length === 0) {
      const firstPage: PageData = {
        id: generateId('page'),
        name: pageName || 'Page 01',
        dynamicLayers: [...dynamicLayers],
        layerPositions: { ...layerPositions },
        textContents: { ...textContents },
        textStyles: { ...textStyles },
        backgroundSettings: { ...backgroundSettings },
        imageSettings: { ...imageSettings },
        videoSettings: { ...videoSettings },
        shapeSettings: { ...shapeSettings },
        scrollerSettings: { ...scrollerSettings },
        templateSettings: { ...templateSettings },
        canvasDimensions: { ...canvasDimensions }
      };
      setPages([firstPage]);
    }
  }, [isInitialLoadComplete, pages.length, pageName, dynamicLayers, layerPositions, textContents, textStyles, backgroundSettings, imageSettings, shapeSettings, scrollerSettings, widgetSettings, videoSettings, templateSettings]);

  // Sync editor state from current page index
  useEffect(() => {
    if (!isInitialLoadComplete || pages.length === 0) return;

    const page = pages[currentPageIndex];
    if (page) {
      // Load current workspace from page data
      setDynamicLayers(page.dynamicLayers || []);
      setLayerPositions(page.layerPositions || {});
      setTextContents(page.textContents || {});
      setTextStyles(page.textStyles || {});
      setBackgroundSettings(page.backgroundSettings || {});
      setImageSettings(page.imageSettings || {});
      setShapeSettings(page.shapeSettings || {});
      setScrollerSettings(page.scrollerSettings || {});
      setWidgetSettings(page.widgetSettings || {});
      setVideoSettings(page.videoSettings || {});
      setTemplateSettings(page.templateSettings || {});
      setPageName(page.name);

      if (page.canvasDimensions) {
        setCanvasDimensions(page.canvasDimensions);
      }

      // Clear selection when switching pages to prevent stale handles
      setSelectedLayerIds([]);
    }
  }, [currentPageIndex, isInitialLoadComplete, pages, setDynamicLayers, setLayerPositions, setTextContents, setTextStyles, setBackgroundSettings, setImageSettings, setShapeSettings, setScrollerSettings, setWidgetSettings, setVideoSettings, setTemplateSettings, setPageName, setSelectedLayerIds]); // Only trigger when index changes


  // Save to storage on change
  useEffect(() => {
    // Prevent saving if the initial load hasn't completed yet
    if (!isInitialLoadComplete) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      setSavingStatus('syncing');
      try {
        // Synchronize current page state into the pages array before saving
        const freshPages = [...pages];
        if (freshPages[currentPageIndex]) {
          freshPages[currentPageIndex] = {
            ...freshPages[currentPageIndex],
            dynamicLayers: [...dynamicLayers],
            layerPositions: { ...layerPositions },
            textContents: { ...textContents },
            textStyles: { ...textStyles },
            backgroundSettings: { ...backgroundSettings },
            imageSettings: { ...imageSettings },
            shapeSettings: { ...shapeSettings },
            scrollerSettings: { ...scrollerSettings },
            widgetSettings: { ...widgetSettings },
            videoSettings: { ...videoSettings },
            templateSettings: { ...templateSettings },
            name: pageName,
          };
        }

        const state: SavedState = {
          id: selectedTemplate?.id || 'default',
          name: selectedTemplate.name || 'Untitled Design',
          previewUrl: selectedTemplate.previewUrl || '',
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          canvasDimensions,
          dynamicLayers,
          layerPositions,
          textContents,
          textStyles,
          backgroundSettings,
          imageSettings,
          shapeSettings,
          scrollerSettings,
          widgetSettings,
          videoSettings,
          pageName,
          templateSettings,
          pages: freshPages,
          currentPageIndex,
          lastModified: Date.now()
        };
        await saveState(state);
        setSavingStatus('saved');
      } catch (e) {
        console.error('Failed to save state', e);
        setSavingStatus('error');
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    videoSettings,
    pageName,
    templateSettings,
    currentPageIndex,
    isInitialLoadComplete,
    pages,
    selectedTemplate?.id,
    selectedTemplate?.name,
    selectedTemplate?.previewUrl,
    CANVAS_WIDTH,
    CANVAS_HEIGHT
  ]);

  const handleSaveDesign = async () => {
    if (isManualSaving) return;

    // Record original selection to restore it after save
    const originalSelectedIds = [...selectedLayerIds];

    // Clear selection immediately so it's not captured in thumbnails
    setSelectedLayerIds([]);

    // Wait briefly for React to re-render the canvas without the selection overlay
    await new Promise(resolve => setTimeout(resolve, 100));

    setSavingStatus('saving');
    setIsManualSaving(true);
    setSaveProgress({ current: 1, total: pages.length || 1, status: 'Initializing...', subStep: 'capturing' });

    try {
      // 2. Ensure pages array is synchronized with current editor state before saving
      const freshPages = [...pages];
      if (freshPages[currentPageIndex]) {
        freshPages[currentPageIndex] = {
          ...freshPages[currentPageIndex],
          dynamicLayers: [...dynamicLayers],
          layerPositions: { ...layerPositions },
          textContents: { ...textContents },
          textStyles: { ...textStyles },
          backgroundSettings: { ...backgroundSettings },
          imageSettings: { ...imageSettings },
          videoSettings: { ...videoSettings },
          shapeSettings: { ...shapeSettings },
          scrollerSettings: { ...scrollerSettings },
          widgetSettings: { ...widgetSettings },
          templateSettings: { ...templateSettings },
          name: pageName,
        };
      }

      if (freshPages.length === 0) {
        setIsManualSaving(false);
        setSavingStatus('error');
        return;
      }

      const originalPageIndex = currentPageIndex;
      const savedIds: string[] = [];
      let currentPageInCanvas = originalPageIndex;

      // Loop through each page, render, capture, and save
      for (let i = 0; i < freshPages.length; i++) {
        const page = freshPages[i];
        setSaveProgress({
          current: i + 1,
          total: freshPages.length,
          status: `Capturing ${page.name || `Page ${i + 1}`}...`,
          subStep: 'capturing'
        });

        // Render the page if it's not currently active so we can capture it
        if (currentPageInCanvas !== i) {
          setCurrentPageIndex(i);
          currentPageInCanvas = i;
          // Wait longer for React to render and UI (like images/videos) to settle
          await new Promise(resolve => setTimeout(resolve, 800));
        }

        const isOriginalPage = (i === originalPageIndex);

        // ... (Construct pageCanvasData remains the same)
        const pageCanvasData: SavedState = {
          id: isOriginalPage ? (normalizeId(selectedTemplate?.id) || 'default') : `page-${Date.now()}-${i}`,
          originTemplateId: selectedTemplate?.originTemplateId,
          version: '1.0',
          name: freshPages.length > 1
            ? `${selectedTemplate.name || 'Untitled Design'} - ${page.name || `Page ${i + 1}`}`
            : (selectedTemplate.name || 'Untitled Design'),
          previewUrl: '',
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          canvasDimensions,

          dynamicLayers: page.dynamicLayers || [],
          layerPositions: page.layerPositions || {},
          textContents: page.textContents || {},
          textStyles: page.textStyles || {},
          backgroundSettings: page.backgroundSettings || {},
          imageSettings: page.imageSettings || {},
          shapeSettings: page.shapeSettings || {},
          templateSettings: page.templateSettings || templateSettings,
          scrollerSettings: page.scrollerSettings || {},
          widgetSettings: page.widgetSettings || {},
          videoSettings: page.videoSettings || {},
          pageName: page.name || `Page ${i + 1}`,

          pages: [page],
          currentPageIndex: 0,
          lastModified: Date.now()
        };

        const formData = new FormData();
        formData.append('name', pageCanvasData.name);
        formData.append('data', JSON.stringify(pageCanvasData));

        // Auto-categorize template based on presence of dynamic elements
        const isDynamic = checkIsDynamic(page);
        formData.append('file_type', isDynamic ? 'creator_template_dynamic' : 'creator_template_static');

        // Calculate and send orientation
        const orientation = CANVAS_WIDTH >= CANVAS_HEIGHT ? 'landscape' : 'portrait';
        formData.append('orientation', orientation);

        try {
          const playerData = compilePlayerData(pageCanvasData);
          formData.append('player_data', JSON.stringify(playerData));

          // Extract all downloadable media URLs for Android offline playback.
          // Apps/widgets are excluded — they cache via the browser inside iframes.
          const offlineAssets = extractOfflineAssets(playerData);
          formData.append('offline_assets', JSON.stringify(offlineAssets));
        } catch (compileErr) {
          console.error(`❌ [SAVE] Player Data compilation failed for page ${i + 1}:`, compileErr);
        }

        if (canvasRef.current) {
          try {
            // Wait slightly longer for dynamic templates to ensure everything is rendered
            const isDynamicPage = checkIsDynamic(page);
            // Wait longer for all assets to settle, especially on slow connections
            await new Promise(resolve => setTimeout(resolve, isDynamicPage ? 2500 : 1500));

            const blob = await toBlob(canvasRef.current, {
              width: CANVAS_WIDTH,
              height: CANVAS_HEIGHT,
              style: {
                transform: 'scale(1)',
                transformOrigin: 'top left',
              },
              cacheBust: false,
              backgroundColor: '#ffffff',
              pixelRatio: 1,
              imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=', // Blank 1x1 fallback
            });
            if (blob) {
              formData.append('thumbnail', blob, `thumbnail_${i + 1}.png`);
            } else {
              throw new Error(`The canvas was unable to render a preview (Page ${i + 1}).`);
            }
          } catch (thumbErr) {
            console.error(`❌ [SAVE] Thumbnail generation failed for page ${i + 1}:`, thumbErr);
            // Extract more info if it's an Event or Error
            const errorMsg = (thumbErr instanceof Error) ? thumbErr.message :
              (thumbErr instanceof Event && thumbErr.type === 'error') ?
                "An image in your design failed to load (CORS or expired link). Please check the 'bbc-logo' or similar external images." :
                "Thumbnail generation failed. Ensure all images are loaded.";
            throw new Error(errorMsg);
          }
        }

        // Send to Backend
        const isTemporaryId = !selectedTemplate.id || selectedTemplate.id === 'dev' || selectedTemplate.id.startsWith('custom-') || selectedTemplate.id.startsWith('new-');
        const isExistingCustom = isOriginalPage && !isTemporaryId && Boolean(selectedTemplate.isCustom);
        if (isExistingCustom) {
          const normalizedId = normalizeId(selectedTemplate.id);
          formData.append('_id', normalizedId);
          formData.append('id', normalizedId);
          formData.append('savedTemplateId', normalizedId);
          if (selectedTemplate.cmsContentId) {
            formData.append('cmsContentId', selectedTemplate.cmsContentId);
            formData.append('isSavedToCms', 'true');
          }
        } else if (selectedTemplate.originTemplateId && !selectedTemplate.originTemplateId.startsWith('custom-') && !selectedTemplate.originTemplateId.startsWith('new-')) {
          const normalizedId = normalizeId(selectedTemplate.originTemplateId);
          formData.append('originTemplateId', normalizedId);
        }

        setSaveProgress(prev => ({
          ...prev,
          status: `Uploading ${page.name || `Page ${i + 1}`}...`,
          subStep: 'uploading'
        }));

        let result: any;
        if (isExistingCustom) {
          const normalizedId = normalizeId(selectedTemplate.id);
          result = await _updateSavedTemplate({ id: normalizedId, data: formData }).unwrap();
        } else {
          result = await _saveUserDesign(formData).unwrap();
        }

        const savedId = normalizeId(result?.data?._id || result?._id || (isExistingCustom ? selectedTemplate.id : null));
        if (savedId && isOriginalPage) {
          savedIds.push(savedId);
        }
      }

      // Restore original page visually
      if (originalPageIndex !== currentPageIndex) {
        setSaveProgress(prev => ({ ...prev, status: 'Finalizing...', subStep: 'restoring' }));
        setCurrentPageIndex(originalPageIndex);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Sync original page id to state
      if (savedIds.length > 0 && savedIds[0]) {
        dispatch(setSelectedTemplate({
          ...selectedTemplate,
          id: savedIds[0],
          isCustom: true // Mark as saved project
        }));
      }

      setSavingStatus('saved');
      setIsManualSaving(false);

      toast.success('Design Saved Successfully!');

      // Cleanup local draft on successful backend save
      if (selectedTemplate.id) {
        const { deleteState } = await import('../../services/indexedDB');
        await deleteState(normalizeId(selectedTemplate.id));
      }

      // Restore automatic navigation as requested
      navigate('/my-projects');

    } catch (err: any) {
      console.error('Save failed:', err);
      setSavingStatus('error');
      setIsManualSaving(false);

      // Restore selection if save failed so user can keep editing
      if (originalSelectedIds.length > 0) {
        setSelectedLayerIds(originalSelectedIds);
      }

      toast.error(err.message || 'Failed to save design. Please check your connection and try again.');
      // Reset status after a delay so the UI doesn't get stuck in error
      setTimeout(() => setSavingStatus('saved'), 3000);
    }
  };

  // Create snapshot of current state
  const createSnapshot = useCallback((actionType?: HistoryActionType, description?: string): HistoryState => {
    const safeClone = (obj: any) => obj ? JSON.parse(JSON.stringify(obj)) : obj;

    // Step 1: Create a live copy of the pages array with the current workspace state injected
    // This prevents the pages array in history from being stale relative to current moves/edits.
    const reconciledPages = [...pages];
    if (reconciledPages[currentPageIndex]) {
      reconciledPages[currentPageIndex] = {
        ...reconciledPages[currentPageIndex],
        dynamicLayers: [...dynamicLayers],
        layerPositions: { ...layerPositions },
        textContents: { ...textContents },
        textStyles: { ...textStyles },
        backgroundSettings: { ...backgroundSettings },
        imageSettings: { ...imageSettings },
        videoSettings: { ...videoSettings },
        shapeSettings: { ...shapeSettings },
        scrollerSettings: { ...scrollerSettings },
        widgetSettings: { ...widgetSettings },
        templateSettings: { ...templateSettings },
        canvasDimensions: { ...canvasDimensions },
        name: pageName || reconciledPages[currentPageIndex].name
      };
    }

    return {
      id: selectedTemplate?.id || '',
      dynamicLayers: safeClone(dynamicLayers) || [],
      layerPositions: safeClone(layerPositions) || {},
      textContents: safeClone(textContents) || {},
      textStyles: safeClone(textStyles) || {},
      shapeSettings: safeClone(shapeSettings) || {},
      scrollerSettings: safeClone(scrollerSettings) || {},
      widgetSettings: safeClone(widgetSettings) || {},
      backgroundSettings: safeClone(backgroundSettings) || {},
      imageSettings: safeClone(imageSettings) || {},
      videoSettings: safeClone(videoSettings) || {},
      templateSettings: safeClone(templateSettings) || {},
      canvasDimensions: safeClone(canvasDimensions),
      currentPageIndex: currentPageIndex,
      pages: safeClone(reconciledPages),
      pageName: pageName,
      timestamp: Date.now(),
      actionType,
      actionDescription: description
    };
  }, [dynamicLayers, layerPositions, textContents, textStyles, shapeSettings, scrollerSettings, widgetSettings, backgroundSettings, imageSettings, videoSettings, templateSettings, pageName, pages, currentPageIndex, selectedTemplate?.id]);

  // Commit action to history - now uses a queued mechanism to ensure
  // that snapshots are taken AFTER React state updates have been applied.
  const commitToHistory = useCallback((actionType: HistoryActionType, description?: string) => {
    if (isUndoRedoAction.current) {

      return;
    }


    pendingActionsRef.current.push({ type: actionType, description });
    setTriggerHistoryCommit(prev => prev + 1);
  }, []);

  // Process pending history commits after state updates have reflected in the UI
  useEffect(() => {
    if (pendingActionsRef.current.length > 0) {
      const actions = [...pendingActionsRef.current];
      pendingActionsRef.current = [];

      // If multiple actions happened in the same render tick, we commit the final settled state.
      // We use the info from the last action as the representative for this commit.
      const lastAction = actions[actions.length - 1];
      const snapshot = createSnapshot(lastAction.type, lastAction.description);



      setHistory(prev => {
        const nextHistory = limitHistorySize(commitAction(prev, snapshot));
        return nextHistory;
      });
    }
  }, [triggerHistoryCommit, createSnapshot]);

  const handleAutoMapAll = useCallback((apiId: string, activeLayerBindingOverride?: any) => {
    const api = customApis.find(a => String(a._id) === String(apiId));
    if (!api) {
      toast.error('Selected integration API data not found.');
      return;
    }

    // 1. Prepare base settings using the latest references
    let currentTextStyles = { ...textStylesRef.current };
    let currentImageSettings = { ...imageSettingsRef.current };
    let currentScrollerSettings = { ...scrollerSettingsRef.current };

    // 2. If an override for the active layer is provided, apply it immediately to base settings
    if (selectedLayerId && activeLayerBindingOverride) {
      const activeLayer = findLayerById(dynamicLayers, selectedLayerId);
      if (activeLayer) {
        if (activeLayer.type === 'text') {
          currentTextStyles[selectedLayerId] = {
            ...(currentTextStyles[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        } else if (activeLayer.type === 'image') {
          currentImageSettings[selectedLayerId] = {
            ...(currentImageSettings[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        } else if (activeLayer.type === 'scroller') {
          currentScrollerSettings[selectedLayerId] = {
            ...(currentScrollerSettings[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        }
      }
    }

    // 3. Perform template-wide auto-mapping
    const result = autoMapTemplate(
      dynamicLayers,
      currentTextStyles,
      currentImageSettings,
      currentScrollerSettings,
      api
    );

    // 4. Force override of the active layer's binding in the final output to guarantee it isn't clobbered
    if (selectedLayerId && activeLayerBindingOverride) {
      const activeLayer = findLayerById(dynamicLayers, selectedLayerId);
      if (activeLayer) {
        if (activeLayer.type === 'text') {
          result.textStyles[selectedLayerId] = {
            ...(result.textStyles[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        } else if (activeLayer.type === 'image') {
          result.imageSettings[selectedLayerId] = {
            ...(result.imageSettings[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        } else if (activeLayer.type === 'scroller') {
          result.scrollerSettings[selectedLayerId] = {
            ...(result.scrollerSettings[selectedLayerId] || {}),
            binding: activeLayerBindingOverride
          };
        }
      }
    }

    // Resolve all bindings from the API data to update canvas content immediately
    const nextTextContents = { ...textContentsRef.current };
    Object.keys(result.textStyles).forEach(layerId => {
      const style = result.textStyles[layerId];
      if (style?.binding) {
        const val = resolveJsonPath(api.customapidata, style.binding.apiKey);
        if (val !== undefined && val !== null) {
          nextTextContents[layerId] = String(val);
        } else if (style.binding.fallbackValue) {
          nextTextContents[layerId] = style.binding.fallbackValue;
        }
      }
    });

    setTextContents(nextTextContents);

    Object.keys(result.imageSettings).forEach(layerId => {
      const setting = result.imageSettings[layerId];
      if (setting?.binding) {
        const val = resolveJsonPath(api.customapidata, setting.binding.apiKey);
        if (val !== undefined && val !== null) {
          result.imageSettings[layerId] = {
            ...setting,
            url: String(val)
          };
        } else if (setting.binding.fallbackValue) {
          result.imageSettings[layerId] = {
            ...setting,
            url: setting.binding.fallbackValue
          };
        }
      }
    });

    Object.keys(result.scrollerSettings).forEach(layerId => {
      const setting = result.scrollerSettings[layerId];
      if (setting?.binding) {
        const val = resolveJsonPath(api.customapidata, setting.binding.apiKey);
        if (val !== undefined && val !== null) {
          result.scrollerSettings[layerId] = {
            ...setting,
            content: String(val)
          };
        } else if (setting.binding.fallbackValue) {
          result.scrollerSettings[layerId] = {
            ...setting,
            content: setting.binding.fallbackValue
          };
        }
      }
    });

    // 5. Update state for all panels together in one batch
    setTextStyles(result.textStyles);
    setImageSettings(result.imageSettings);
    setScrollerSettings(result.scrollerSettings);

    const isActiveLayerText = selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'text';
    const isActiveLayerImage = selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'image';
    const isActiveLayerScroller = selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'scroller';

    const isActiveMapped = selectedLayerId && (
      (isActiveLayerText && !!result.textStyles[selectedLayerId]?.binding?.apiKey) ||
      (isActiveLayerImage && !!result.imageSettings[selectedLayerId]?.binding?.apiKey) ||
      (isActiveLayerScroller && !!result.scrollerSettings[selectedLayerId]?.binding?.apiKey)
    );

    if (result.count > 0) {
      let message = '';
      if (isActiveMapped) {
        if (result.count === 1) {
          message = `Automatically mapped this layer to "${api.name_custom}" API!`;
        } else {
          message = `Automatically mapped this layer and ${result.count - 1} other layers to "${api.name_custom}" API!`;
        }
      } else {
        if (result.count === 1) {
          message = `Automatically mapped 1 other layer, but no match found for this layer.`;
        } else {
          message = `Automatically mapped ${result.count} other layers, but no match found for this layer.`;
        }
      }

      toast.success(message, {
        duration: 4500,
        icon: '🔌'
      });
      commitToHistory('LAYER_EDIT', `Auto-mapped elements to ${api.name_custom}`);

      // Scan new bindings for array structures to auto-paginate in one step
      const arrayBindingInfo = (() => {
        const scan = (bindingsRecord: Record<string, any>) => {
          for (const [, settings] of Object.entries(bindingsRecord)) {
            const binding = settings?.binding;
            if (binding?.apiKey && binding?.customApiId) {
              const key = binding.apiKey;
              if (key === '0' || key.startsWith('0.') || key.includes('.0.') || key.endsWith('.0') ||
                  key === '[0]' || key.startsWith('[0].') || key.includes('[0]') || key.endsWith('[0]')) {
                let prefix = '0';
                if (key === '0' || key.startsWith('0.')) {
                  prefix = '0';
                } else if (key === '[0]' || key.startsWith('[0].')) {
                  prefix = '[0]';
                } else if (key.includes('[0]')) {
                  const idx = key.indexOf('[0]');
                  prefix = key.substring(0, idx + 3);
                } else {
                  const index = key.indexOf('.0.');
                  if (index !== -1) {
                    prefix = key.substring(0, index + 2);
                  } else if (key.endsWith('.0')) {
                    prefix = key;
                  }
                }
                return { prefix, customApiId: binding.customApiId };
              }
            }
          }
          return null;
        };

        let found = scan(result.textStyles);
        if (!found) found = scan(result.imageSettings);
        if (!found) found = scan(result.scrollerSettings);
        return found;
      })();

      const autoPaginate = localStorage.getItem('wejha_auto_array_pagination') !== 'false';

      if (arrayBindingInfo && autoPaginate) {
        // Run handleGeneratePagesFromArray synchronously with the new overrides to avoid React state delays
        setTimeout(() => {
          handleGeneratePagesFromArray(
            arrayBindingInfo.prefix, 
            arrayBindingInfo.customApiId, 
            {
              textStyles: result.textStyles,
              imageSettings: result.imageSettings,
              scrollerSettings: result.scrollerSettings,
              textContents: nextTextContents
            }
          );
        }, 0);
      }
    } else if (!activeLayerBindingOverride) {
      toast.error(`No matching layers found to map with "${api.name_custom}" API.`);
    } else {
      commitToHistory('LAYER_EDIT', `Bound active layer to ${api.name_custom}`);
    }
  }, [dynamicLayers, customApis, commitToHistory, selectedLayerId]);



  const handleGeneratePagesFromArray = useCallback((
    prefix: string, 
    customApiId: string,
    overrideData?: {
      textStyles?: Record<string, any>;
      imageSettings?: Record<string, any>;
      scrollerSettings?: Record<string, any>;
      textContents?: Record<string, string>;
    }
  ) => {
    const targetApi = customApis.find(api => String(api._id) === String(customApiId));
    if (!targetApi || !targetApi.customapidata) {
      toast.error('Could not find source API feed.');
      return;
    }

    const getArrayPath = (p: string) => {
      if (p === '0' || p === '[0]') return '';
      if (p.endsWith('.0')) {
        return p.substring(0, p.length - 2);
      }
      if (p.endsWith('[0]')) {
        return p.substring(0, p.length - 3);
      }
      return p.replace('.0', '');
    };

    const arrayPath = getArrayPath(prefix);
    const arrayVal = arrayPath === '' ? targetApi.customapidata : resolveJsonPath(targetApi.customapidata, arrayPath);

    if (!Array.isArray(arrayVal) || arrayVal.length <= 1) {
      toast.error('Source variable is not a valid array, or array has only 1 item.');
      return;
    }

    const basePageData: PageData = {
      ...pages[currentPageIndex],
      dynamicLayers,
      layerPositions,
      textContents: overrideData?.textContents || textContents,
      textStyles: overrideData?.textStyles || textStyles,
      backgroundSettings,
      imageSettings: overrideData?.imageSettings || imageSettings,
      videoSettings,
      shapeSettings,
      scrollerSettings: overrideData?.scrollerSettings || scrollerSettings,
      widgetSettings,
      templateSettings,
      canvasDimensions,
      name: pageName,
    };

    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = basePageData;
      }

      const newPages: PageData[] = [];
      const limit = (templateSettings as any)?.endIndex === 'array.length' ? arrayVal.length : (parseInt(String((templateSettings as any)?.endIndex)) || 5);
      const count = Math.min(arrayVal.length, limit);
      for (let i = 1; i < count; i++) {
        const maxCap = (templateSettings as any)?.endIndex === 'array.length' ? 100 : limit;
        if (updated.length + newPages.length >= maxCap) break;

        const cloned = clonePageWithMapping(basePageData);

        const searchStr = prefix;
        let replaceStr: string;
        if (searchStr === '0' || searchStr === '[0]') {
          replaceStr = searchStr === '0' ? `${i}` : `[${i}]`;
        } else if (searchStr.endsWith('.0')) {
          replaceStr = searchStr.substring(0, searchStr.length - 2) + `.${i}`;
        } else if (searchStr.endsWith('[0]')) {
          replaceStr = searchStr.substring(0, searchStr.length - 3) + `[${i}]`;
        } else {
          replaceStr = searchStr.replace('0', `${i}`);
        }

        Object.keys(cloned.textStyles).forEach(k => {
          if (cloned.textStyles[k]?.binding?.apiKey) {
            const oldKey = cloned.textStyles[k].binding.apiKey;
            if (oldKey.includes(searchStr)) {
              const newApiKey = oldKey.replace(searchStr, replaceStr);
              cloned.textStyles[k].binding = {
                ...cloned.textStyles[k].binding,
                apiKey: newApiKey
              };
              const val = resolveJsonPath(targetApi.customapidata, newApiKey);
              if (val !== undefined && val !== null) {
                cloned.textContents[k] = String(val);
              } else if (cloned.textStyles[k].binding.fallbackValue) {
                cloned.textContents[k] = cloned.textStyles[k].binding.fallbackValue;
              }
            }
          }
        });

        Object.keys(cloned.imageSettings).forEach(k => {
          if (cloned.imageSettings[k]?.binding?.apiKey) {
            const oldKey = cloned.imageSettings[k].binding.apiKey;
            if (oldKey.includes(searchStr)) {
              const newApiKey = oldKey.replace(searchStr, replaceStr);
              cloned.imageSettings[k].binding = {
                ...cloned.imageSettings[k].binding,
                apiKey: newApiKey
              };
              const val = resolveJsonPath(targetApi.customapidata, newApiKey);
              if (val !== undefined && val !== null) {
                cloned.imageSettings[k] = {
                  ...cloned.imageSettings[k],
                  url: String(val)
                };
              } else if (cloned.imageSettings[k].binding.fallbackValue) {
                cloned.imageSettings[k] = {
                  ...cloned.imageSettings[k],
                  url: cloned.imageSettings[k].binding.fallbackValue
                };
              }
            }
          }
        });

        Object.keys(cloned.scrollerSettings).forEach(k => {
          if (cloned.scrollerSettings[k]?.binding?.apiKey) {
            const oldKey = cloned.scrollerSettings[k].binding.apiKey;
            if (oldKey.includes(searchStr)) {
              const newApiKey = oldKey.replace(searchStr, replaceStr);
              cloned.scrollerSettings[k].binding = {
                ...cloned.scrollerSettings[k].binding,
                apiKey: newApiKey
              };
              const val = resolveJsonPath(targetApi.customapidata, newApiKey);
              if (val !== undefined && val !== null) {
                cloned.scrollerSettings[k] = {
                  ...cloned.scrollerSettings[k],
                  content: String(val)
                };
              } else if (cloned.scrollerSettings[k].binding.fallbackValue) {
                cloned.scrollerSettings[k] = {
                  ...cloned.scrollerSettings[k],
                  content: cloned.scrollerSettings[k].binding.fallbackValue
                };
              }
            }
          }
        });

        cloned.name = `Page ${updated.length + newPages.length + 1}`;
        newPages.push(cloned);
      }

      if (newPages.length > 0) {
        toast.success(`Dynamically generated ${newPages.length} cloned pages from array!`);
        commitToHistory('PAGE_ADD', `Generated ${newPages.length} pages from array`);
      }
      return [...updated, ...newPages];
    });

  }, [
    customApis,
    currentPageIndex,
    pages,
    dynamicLayers,
    layerPositions,
    textContents,
    textStyles,
    backgroundSettings,
    imageSettings,
    videoSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    templateSettings,
    canvasDimensions,
    pageName,
    commitToHistory
  ]);

  const handleResizeApply = useCallback((width: number, height: number, unit: string) => {
    setCanvasDimensions({ width, height, unit });

    // Update the 'root' layer to ensure it matches the new dimensions
    setDynamicLayers(prev => prev.map(layer =>
      layer.id === 'root'
        ? {
          ...layer,
          className: `w-[${width}px] h-[${height}px] bg-white shadow-lg overflow-hidden shrink-0 relative`,
          style: { ...layer.style, width: `${width}px`, height: `${height}px` }
        }
        : layer
    ));

    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = {
          ...updated[currentPageIndex],
          canvasDimensions: { width, height, unit }
        };
      }
      return updated;
    });

    commitToHistory('CANVAS_RESIZE', `Resized canvas to ${width}x${height}${unit}`);
    toast.success('Canvas resized successfully');
  }, [commitToHistory, currentPageIndex, setDynamicLayers, setPages, setCanvasDimensions]);

  // Multi-selection handler with Shift+Click support
  // Behavior:
  // - First click (no Shift): Clears previous selection, selects clicked element
  // - Shift+Click: Adds element to selection (or removes if already selected)
  // - All selected elements show individual green outlines
  // - Combined SelectionOverlay shows bounding box with resize handles
  const handleLayerSelect = useCallback((layerId: string | null, shiftKey: boolean) => {

    if (!layerId) {
      setSelectedLayerIds([]);
      return;
    }

    if (shiftKey) {
      // Shift+Click: Add/remove from selection (toggle behavior)
      setSelectedLayerIds(prev => {
        if (layerId === 'root') return prev;
        if (prev.includes(layerId)) {
          if (layerId === 'root') return prev;
          const newSelection = prev.filter(id => id !== layerId);
          return newSelection;
        } else {
          const cleanPrev = prev.filter(id => id !== 'root');
          const newSelection = cleanPrev.includes(layerId) ? cleanPrev : [...cleanPrev, layerId];
          return newSelection;
        }
      });
    } else {
      setSelectedLayerIds([layerId]);
    }
  }, []); // No dependencies needed since we use functional updates

  // Debug: Log selection changes
  // useEffect(() => {
  // }, [selectedLayerIds]);

  // Auto-initialize imageSettings for any image layers that don't have settings
  useEffect(() => {
    let hasUpdates = false;
    const newSettings: Record<string, ImageSettings> = {};

    const collectImageSettings = (layers: Layer[]) => {
      layers.forEach(layer => {
        if (layer.type === 'image' && layer.content) {
          // We check against the current imageSettings state implicitly via the functional update later
          // but we can't easily check it here without imageSettings in deps.
          // However, we only need to run this when dynamicLayers changes.
          newSettings[layer.id] = {
            url: layer.content || '',
            opacity: 100,
            brightness: 100,
            contrast: 100,
            saturation: 100,
            blur: 0,
            flipHorizontal: false,
            flipVertical: false,
            borderWidth: 0,
            borderColor: '#000000',
            borderRadius: 0,
            objectFit: 'cover',
            lockAspectRatio: false,
            animation: {
              type: 'none',
              duration: 1000,
              delay: 0,
              easing: 'ease',
              loop: false,
            },
          };
          hasUpdates = true;
        }
        if (layer.children) {
          collectImageSettings(layer.children);
        }
      });
    };

    collectImageSettings(dynamicLayers);

    if (hasUpdates) {
      setImageSettings(prev => {
        const actualUpdates: Record<string, ImageSettings> = {};
        let reallyHasUpdates = false;

        Object.keys(newSettings).forEach(id => {
          if (!prev[id]) {
            actualUpdates[id] = newSettings[id];
            reallyHasUpdates = true;
          }
        });

        return reallyHasUpdates ? { ...prev, ...actualUpdates } : prev;
      });
    }
  }, [dynamicLayers]); // REMOVED imageSettings from dependencies to prevent loop

  // Auto-initialize videoSettings for any video layers that don't have settings
  useEffect(() => {
    let hasUpdates = false;
    const newSettings: Record<string, VideoSettings> = {};

    const collectVideoSettings = (layers: Layer[]) => {
      layers.forEach(layer => {
        if (layer.type === 'video' && layer.content) {
          newSettings[layer.id] = {
            url: layer.content || '',
            opacity: 100,
            borderRadius: 0,
            objectFit: 'cover',
            muted: true,
            loop: true,
            autoplay: true,
            volume: 100,
            animation: { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
          };
          hasUpdates = true;
        }
        if (layer.children) {
          collectVideoSettings(layer.children);
        }
      });
    };

    collectVideoSettings(dynamicLayers);

    if (hasUpdates) {
      setVideoSettings(prev => {
        const actualUpdates: Record<string, VideoSettings> = {};
        let reallyHasUpdates = false;

        Object.keys(newSettings).forEach(id => {
          if (!prev[id]) {
            actualUpdates[id] = newSettings[id];
            reallyHasUpdates = true;
          }
        });

        return reallyHasUpdates ? { ...prev, ...actualUpdates } : prev;
      });
    }
  }, [dynamicLayers]);


  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (!canUndo(history)) return;

    // Clear any pending text edit commits
    if (textEditDebounceRef.current) {
      clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = null;
    }


    isUndoRedoAction.current = true;
    const newHistory = performUndo(history);
    setHistory(newHistory);

    // Reset the flag after a short delay to allow all state-change side effects to settle
    // without triggering a new history commit.
    setTimeout(() => {
      isUndoRedoAction.current = false;
    }, 100);

    if (newHistory.present) {
      setDynamicLayers(newHistory.present.dynamicLayers);
      setLayerPositions(newHistory.present.layerPositions);
      setTextContents(newHistory.present.textContents);
      setTextStyles(newHistory.present.textStyles || {});
      setShapeSettings(newHistory.present.shapeSettings || {});
      setScrollerSettings(newHistory.present.scrollerSettings || {});
      setWidgetSettings(newHistory.present.widgetSettings || {});
      if (newHistory.present.backgroundSettings) {
        setBackgroundSettings(newHistory.present.backgroundSettings);
      }
      if (newHistory.present.imageSettings) {
        setImageSettings(newHistory.present.imageSettings);
      }
      if (newHistory.present.templateSettings) {
        setTemplateSettings(newHistory.present.templateSettings);
      }
      if (newHistory.present.canvasDimensions) {
        setCanvasDimensions({
          width: newHistory.present.canvasDimensions.width,
          height: newHistory.present.canvasDimensions.height,
          unit: (newHistory.present.canvasDimensions as any).unit || 'px'
        });
      }
      if (newHistory.present.pageName) {
        setPageName(newHistory.present.pageName);
      }
      if (newHistory.present.currentPageIndex !== undefined) {
        setCurrentPageIndex(newHistory.present.currentPageIndex);
      }
      if (newHistory.present.pages) {
        setPages(newHistory.present.pages);
      }
    }
  }, [history, setPages]);

  const handleRedo = useCallback(() => {
    if (!canRedo(history)) return;

    // Clear any pending text edit commits
    if (textEditDebounceRef.current) {
      clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = null;
    }

    isUndoRedoAction.current = true;
    const newHistory = performRedo(history);
    setHistory(newHistory);

    // Reset the flag after a short delay
    setTimeout(() => {
      isUndoRedoAction.current = false;
    }, 100);

    if (newHistory.present) {
      setDynamicLayers(newHistory.present.dynamicLayers);
      setLayerPositions(newHistory.present.layerPositions);
      setTextContents(newHistory.present.textContents);
      setTextStyles(newHistory.present.textStyles || {});
      setShapeSettings(newHistory.present.shapeSettings || {});
      setScrollerSettings(newHistory.present.scrollerSettings || {});
      setWidgetSettings(newHistory.present.widgetSettings || {});
      if (newHistory.present.backgroundSettings) {
        setBackgroundSettings(newHistory.present.backgroundSettings);
      }
      if (newHistory.present.imageSettings) {
        setImageSettings(newHistory.present.imageSettings);
      }
      if (newHistory.present.templateSettings) {
        setTemplateSettings(newHistory.present.templateSettings);
      }
      if (newHistory.present.canvasDimensions) {
        setCanvasDimensions({
          width: newHistory.present.canvasDimensions.width,
          height: newHistory.present.canvasDimensions.height,
          unit: (newHistory.present.canvasDimensions as any).unit || 'px'
        });
      }
      if (newHistory.present.pageName) {
        setPageName(newHistory.present.pageName);
      }
      if (newHistory.present.currentPageIndex !== undefined) {
        setCurrentPageIndex(newHistory.present.currentPageIndex);
      }
      if (newHistory.present.pages) {
        setPages(newHistory.present.pages);
      }
    }
  }, [history, setPages]);

  // Page name editing handlers
  const handlePageNameClick = () => {
    setIsEditingPageName(true);
  };

  const handlePageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageName(e.target.value);
  };

  const handlePageNameBlur = () => {
    setIsEditingPageName(false);
    const defaultName = pages.length === 1 ? (selectedTemplate.name || 'Untitled Design') : `Page ${currentPageIndex + 1}`;
    const newName = pageName.trim() || defaultName;
    setPageName(newName);

    // Sync template name if single-page design
    if (pages.length === 1) {
      dispatch(setSelectedTemplate({
        ...selectedTemplate,
        name: newName
      }));
    }
  };

  const handlePageNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsEditingPageName(false);
      const defaultName = pages.length === 1 ? (selectedTemplate.name || 'Untitled Design') : `Page ${currentPageIndex + 1}`;
      const newName = pageName.trim() || defaultName;
      setPageName(newName);

      if (pages.length === 1) {
        dispatch(setSelectedTemplate({
          ...selectedTemplate,
          name: newName
        }));
      }
    } else if (e.key === 'Escape') {
      setIsEditingPageName(false);
      setPageName(pages[currentPageIndex]?.name || `Page ${currentPageIndex + 1}`);
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingPageName && pageNameInputRef.current) {
      pageNameInputRef.current.focus();
      pageNameInputRef.current.select();
    }
  }, [isEditingPageName]);

  // Unified placement logic
  const placeElementOnCanvas = (
    newElement: Partial<Layer>,
    dropX?: number,
    dropY?: number,
    ignoreBounds: boolean = false
  ): { layer: Layer, position: { left: number, top: number, width?: number, height?: number } } => {
    // Determine size
    const isText = newElement.type === 'text';
    const inputWidth = newElement.style?.width;
    const inputHeight = newElement.style?.height;

    // Use provided dimensions, or default to 100 for non-text, or undefined for text (hugging)
    const width = inputWidth ? parseFloat(String(inputWidth).replace('px', '')) : (isText ? undefined : 100);
    const height = inputHeight ? parseFloat(String(inputHeight).replace('px', '')) : (isText ? undefined : 100);

    // Default to center if no drop coordinates
    // For text with no width, we assume a reasonable initial width for centering purposes (e.g. 200)
    const measureWidth = width ?? 200;
    const measureHeight = height ?? 40;

    let x = (CANVAS_WIDTH - measureWidth) / 2;
    let y = (CANVAS_HEIGHT - measureHeight) / 2;

    if (dropX !== undefined && dropY !== undefined) {
      x = dropX;
      y = dropY;

      const centerX = x + measureWidth / 2;
      const centerY = y + measureHeight / 2;

      if (!ignoreBounds && (centerX < 0 || centerX > CANVAS_WIDTH || centerY < 0 || centerY > CANVAS_HEIGHT)) {
        x = (CANVAS_WIDTH - measureWidth) / 2;
        y = (CANVAS_HEIGHT - measureHeight) / 2;
      }
    }

    const layer = {
      ...newElement,
      id: newElement.id || `${newElement.type}-${Date.now()}`,
      style: {
        position: newElement.id === 'root' ? 'relative' : 'absolute',
        ...newElement.style,
        left: `${x}px`,
        top: `${y}px`,
        // Only set width/height if they were explicitly provided or are defaults for non-text
        ...(width !== undefined ? { width: `${width}px` } : {}),
        ...(height !== undefined ? { height: `${height}px` } : {}),
      }
    } as Layer;

    return {
      layer,
      position: { left: x, top: y, width, height }
    };
  };

  // Text panel handlers
  const handleAddText = (type: 'heading' | 'subheading' | 'body', dropX?: number, dropY?: number) => {
    const id = `text-${Date.now()}`;
    const content = type === 'heading' ? 'Add a heading' : type === 'subheading' ? 'Add a subheading' : 'Add a little bit of body text';

    // Initial Styles: HERO enlarged for maximum impact and visibility
    const fontSize = type === 'heading' ? 72 : type === 'subheading' ? 48 : 28;
    const fontWeight = type === 'heading' ? 'bold' : type === 'subheading' ? 'semibold' : 'normal';

    const result = placeElementOnCanvas({
      id,
      name: type.charAt(0).toUpperCase() + type.slice(1),
      type: 'text',
      content,
      className: 'absolute cursor-move group',
      style: {
        fontSize: `${fontSize}px`,
        fontWeight,
        textAlign: 'center',
        color: '#000000',
        fontFamily: 'Bricolage Grotesque',
      }
    }, dropX, dropY);

    const { layer: newLayer, position } = result;

    // Sane initial width to facilitate centering and prevent letter-stacking,
    // but height remains undefined to allow auto-growth.
    const initialWidth = type === 'heading' ? 600 : type === 'subheading' ? 400 : 300;
    setLayerPositions(prev => ({
      ...prev,
      [id]: {
        left: position.left,
        top: position.top,
        width: initialWidth,
        height: undefined
      }
    }));

    // Nest the new layer within the 'root' frame
    setDynamicLayers(prev => {
      const hasRoot = prev.some(l => l.id === 'root');
      if (hasRoot) {
        return prev.map(layer => {
          if (layer.id === 'root') {
            return {
              ...layer,
              children: [...(layer.children || []), newLayer]
            };
          }
          return layer;
        });
      }
      return [...prev, newLayer];
    });

    setTextContents(prev => ({ ...prev, [id]: content }));
    setTextStyles(prev => ({
      ...prev,
      [id]: {
        fontFamily: 'Bricolage Grotesque',
        fontSize,
        fontWeight,
        textAlign: 'center',
        color: '#000000'
      }
    }));

    setSelectedLayerId(id);
    commitToHistory('LAYER_ADD', `Added ${type}`);

    // Trigger layout re-calculation after DOM and fonts have settled
    setTimeout(() => {
      setLayoutSettledTrigger(prev => prev + 1);
    }, 100);
  };

  // Brand Kit panel handlers
  const handleApplyBrandColor = (color: string, mode: 'text' | 'background' = 'text') => {
    if (!selectedLayerId) return;

    // Check if text
    if (textStyles[selectedLayerId]) {
      if (mode === 'text') {
        setTextStyles(prev => ({
          ...prev,
          [selectedLayerId]: { ...prev[selectedLayerId], color }
        }));
      } else {
        setTextStyles(prev => ({
          ...prev,
          [selectedLayerId]: {
            ...prev[selectedLayerId],
            textEffect: 'background',
            backgroundColor: color
          }
        }));
      }
      commitToHistory('STYLE_CHANGE', `Applied brand ${mode} color`);
      return;
    }

    // Check if shape - use recursive find
    const layer = findLayerById(dynamicLayers, selectedLayerId);

    // Support fillable types
    const fillableTypes = ['rectangle', 'circle', 'triangle', 'rhombus', 'pentagon', 'hexagon', 'octagon', 'star', 'heart', 'shape'];
    if (layer && fillableTypes.includes(layer.type)) {
      setShapeSettings(prev => ({
        ...prev,
        [selectedLayerId]: { ...prev[selectedLayerId], fill: { type: 'solid', enabled: true, color } }
      }));
      commitToHistory('STYLE_CHANGE', 'Applied brand color');
    }
  };

  const handleApplyBrandFont = (fontFamily: string) => {
    if (!selectedLayerId || !textStyles[selectedLayerId]) return;
    setTextStyles(prev => ({
      ...prev,
      [selectedLayerId]: { ...prev[selectedLayerId], fontFamily }
    }));
    commitToHistory('STYLE_CHANGE', 'Applied brand font');
  };

  const handleAddBrandLogo = (url: string, dropX?: number, dropY?: number) => {
    const id = `logo-${Date.now()}`;
    const width = 200;
    const height = 200;

    // Find if we're dropping onto a shape for masking
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    const hitLayer = (dropX !== undefined && dropY !== undefined)
      ? findLayerAtPosition(dynamicLayers, dropX, dropY, layerPositions, canvasRect || undefined, canvasScale)
      : null;

    const isShape = hitLayer && (['rectangle', 'circle', 'triangle', 'rhombus', 'pentagon', 'hexagon', 'octagon', 'star', 'heart', 'shape'].includes(hitLayer.type));

    if (isShape && hitLayer) {
      const element = document.getElementById(hitLayer.id);
      const rect = element?.getBoundingClientRect();
      const shapeWidth = rect ? (rect.width / canvasScale) : (layerPositions[hitLayer.id]?.width || 150);
      const shapeHeight = rect ? (rect.height / canvasScale) : (layerPositions[hitLayer.id]?.height || 150);

      const newLayer: Layer = {
        id,
        type: 'image',
        name: 'Brand Logo',
        content: url,
        style: {
          width: '100%',
          height: '100%',
          left: '0px',
          top: '0px',
          objectFit: 'cover'
        },
        locked: false,
        hidden: false
      };

      setDynamicLayers(prev => updateLayerDeep(prev, hitLayer.id, {
        children: [...(hitLayer.children || []), newLayer]
      }));

      setLayerPositions(prev => ({
        ...prev,
        [id]: { left: 0, top: 0, width: shapeWidth, height: shapeHeight }
      }));

      setImageSettings(prev => ({
        ...prev,
        [id]: {
          ...DEFAULT_IMAGE_SETTINGS,
          url: url,
          objectFit: 'cover'
        }
      }));

      setSelectedLayerIds([id]);
      setSelectedLayerId(id);
      commitToHistory('LAYER_ADD', `Masked logo into ${hitLayer.name}`);
      return;
    }

    const result = placeElementOnCanvas({
      id,
      type: 'image',
      name: 'Brand Logo',
      content: url,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        objectFit: 'contain'
      },
      locked: false,
      hidden: false
    });

    const { layer: newLayer, position } = result;

    // Nest within root
    setDynamicLayers(prev => {
      const hasRoot = prev.some(l => l.id === 'root');
      if (hasRoot) {
        return prev.map(layer => {
          if (layer.id === 'root') {
            return {
              ...layer,
              children: [...(layer.children || []), newLayer]
            };
          }
          return layer;
        });
      }
      return [...prev, newLayer];
    });

    setLayerPositions(prev => ({
      ...prev,
      [id]: position
    }));

    setImageSettings(prev => ({
      ...prev,
      [id]: {
        ...DEFAULT_IMAGE_SETTINGS,
        url: url,
        objectFit: 'contain'
      }
    }));

    setSelectedLayerIds([id]);
    commitToHistory('LAYER_ADD', 'Added brand logo');
  };

  // Uploads panel handlers

  const handleAddUploadToCanvas = (file: UploadedFile, dropX?: number, dropY?: number, overrideMuted?: boolean) => {

    if (file.type === 'image' && file.thumbnail) {
      const id = `uploaded-image-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const img = new Image();

      // Define a timeout to add with default size if it fails or takes too long
      let hasLoaded = false;
      const loadTimeout = setTimeout(() => {
        if (!hasLoaded) {
          console.warn('[Editor] Image load timed out, adding with default dimensions');
          performAddImage(300, 300);
        }
      }, 3000);

      const performAddImage = (naturalWidth: number, naturalHeight: number) => {
        if (hasLoaded) return; // Strict guard to prevent double add
        hasLoaded = true;
        clearTimeout(loadTimeout);

        const maxFitWidth = CANVAS_WIDTH * 0.8;
        const maxFitHeight = CANVAS_HEIGHT * 0.8;

        let width = file.width || naturalWidth;
        let height = file.height || naturalHeight;
        const ratio = (file.width && file.height) ? (file.width / file.height) : (naturalWidth / naturalHeight);

        if (!file.width && width > maxFitWidth) {
          width = maxFitWidth;
          height = width / ratio;
        }

        if (!file.height && height > maxFitHeight) {
          height = maxFitHeight;
          width = height * ratio;
        }

        const canvasRect = canvasRef.current?.getBoundingClientRect();
        const hitLayer = (dropX !== undefined && dropY !== undefined)
          ? findLayerAtPosition(dynamicLayers, dropX, dropY, layerPositions, canvasRect || undefined, canvasScale)
          : null;

        const isShape = hitLayer && (['rectangle', 'circle', 'triangle', 'rhombus', 'pentagon', 'hexagon', 'octagon', 'star', 'heart', 'shape'].includes(hitLayer.type));

        let finalDropX = dropX;
        let finalDropY = dropY;

        if (dropX !== undefined && dropY !== undefined) {
          finalDropX = dropX - width / 2;
          finalDropY = dropY - height / 2;
        }

        if (isShape && hitLayer) {
          const element = document.getElementById(hitLayer.id);
          const rect = element?.getBoundingClientRect();
          const shapeWidth = rect ? (rect.width / canvasScale) : (layerPositions[hitLayer.id]?.width || 150);
          const shapeHeight = rect ? (rect.height / canvasScale) : (layerPositions[hitLayer.id]?.height || 150);

          const newLayer: Layer = {
            id,
            type: 'image',
            name: file.name,
            content: file.rawAsset?.url || file.thumbnail,
            assetId: file.id, // Ensure unique content ID is saved
            className: 'absolute cursor-move group',
            style: {
              width: '100%',
              height: '100%',
              left: '0px',
              top: '0px',
              objectFit: 'contain',
            }
          };

          setDynamicLayers(prev => updateLayerDeep(prev, hitLayer.id, {
            children: [...(hitLayer.children || []), newLayer]
          }));

          setLayerPositions(prev => ({
            ...prev,
            [id]: { left: 0, top: 0, width: shapeWidth, height: shapeHeight }
          }));

          setImageSettings(prev => ({
            ...prev,
            [id]: {
              ...DEFAULT_IMAGE_SETTINGS,
              url: file.rawAsset?.url || file.thumbnail,
              assetId: file.id,
              objectFit: 'cover'
            }
          }));

          setSelectedLayerIds([id]);
          setSelectedLayerId(id);
          commitToHistory('LAYER_ADD', `Masked image into ${hitLayer.name}`);
          return;
        }

        const result = placeElementOnCanvas({
          id,
          type: 'image',
          name: file.name,
          content: file.rawAsset?.url || file.thumbnail,
          assetId: file.id,
          className: 'absolute cursor-move group',
          style: {
            width: `${width}px`,
            height: `${height}px`,
            objectFit: 'contain',
          }
        }, finalDropX, finalDropY);

        const { layer: newLayer, position } = result;

        setDynamicLayers(prev => {
          const hasRoot = prev.some(l => l.id === 'root');
          if (hasRoot) {
            return prev.map(layer => {
              if (layer.id === 'root') {
                return {
                  ...layer,
                  children: [...(layer.children || []), newLayer]
                };
              }
              return layer;
            });
          }
          return [...prev, newLayer];
        });

        setLayerPositions(prev => ({
          ...prev,
          [id]: position
        }));

        setImageSettings(prev => ({
          ...prev,
          [id]: {
            ...DEFAULT_IMAGE_SETTINGS,
            url: file.rawAsset?.url || file.thumbnail,
            assetId: file.id,
            objectFit: 'cover'
          }
        }));

        setSelectedLayerIds([id]);
        setSelectedLayerId(id);
        commitToHistory('LAYER_ADD', `Added uploaded image: ${file.name}`);
      };

      img.onload = () => performAddImage(img.naturalWidth, img.naturalHeight);
      img.onerror = () => {
        console.error('[Editor] Failed to load image:', file.thumbnail);
        performAddImage(300, 300);
      };
      img.crossOrigin = 'anonymous';
      img.src = file.thumbnail;
    } else if (file.type === 'video') {
      const id = `uploaded-video-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

      const width = 480; // Default video width
      const height = 270; // Default 16:9 

      let finalDropX = dropX;
      let finalDropY = dropY;

      if (dropX !== undefined && dropY !== undefined) {
        finalDropX = dropX - width / 2;
        finalDropY = dropY - height / 2;
      }

      const result = placeElementOnCanvas({
        id,
        type: 'video',
        name: file.name,
        content: file.rawAsset?.url || file.thumbnail,
        assetId: file.id,
        className: 'absolute cursor-move group',
        style: {
          width: `${width}px`,
          height: `${height}px`,
        }
      }, finalDropX, finalDropY);

      const { layer: newLayer, position } = result;

      setDynamicLayers(prev => {
        const hasRoot = prev.some(l => l.id === 'root');
        if (hasRoot) {
          return prev.map(layer => {
            if (layer.id === 'root') {
              return {
                ...layer,
                children: [...(layer.children || []), newLayer]
              };
            }
            return layer;
          });
        }
        return [...prev, newLayer];
      });

      setLayerPositions(prev => ({
        ...prev,
        [id]: position
      }));

      setVideoSettings(prev => ({
        ...prev,
        [id]: {
          ...DEFAULT_VIDEO_SETTINGS,
          url: file.rawAsset?.url || file.thumbnail,
          assetId: file.id,
          muted: overrideMuted !== undefined ? overrideMuted : DEFAULT_VIDEO_SETTINGS.muted
        }
      }));

      setSelectedLayerIds([id]);
      commitToHistory('LAYER_ADD', `Added uploaded video: ${file.name}`);
    }
  };

  const getPlaylistConfig = (playlistData: any, id: string): Partial<Layer> => {
    return {
      id,
      name: `Playlist: ${playlistData.name}`,
      type: 'playlist', // New layer type
      content: JSON.stringify(playlistData), // Store data here
      className: 'absolute cursor-move overflow-hidden bg-black',
      style: {
        width: `${playlistData.width || 480}px`,
        height: `${playlistData.height || 360}px`,
      },
      children: []
    };
  };

  const getAppConfig = (appName: string, id: string, widget?: Widget): Partial<Layer> => {
    const nameLower = appName.toLowerCase();

    // Default config
    let config: Partial<Layer> = {
      id,
      name: appName,
      type: 'app',
      isApp: true,
      className: 'flex items-center justify-center bg-gray-100 rounded absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500 overflow-hidden',
      style: {
        width: `${widget?.width || 150}px`,
        height: `${widget?.height || 100}px`,
      },
      children: [
        {
          id: `${id}-label`,
          type: 'text',
          name: 'App Label',
          content: appName,
          className: 'text-sm font-medium text-gray-700 pointer-events-none'
        }
      ]
    };

    if (nameLower.includes('scroller')) {
      config = {
        ...config,
        type: 'scroller',
        className: 'flex items-center justify-start bg-[#002B5B] shadow-md absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500 overflow-hidden',
        style: {
          width: '300px',
          height: '60px',
        },
        children: [
          {
            id: `${id}-text`,
            type: 'text',
            name: 'Scroller Text',
            content: 'SCROLLER TEXT HERE',
            className: 'uppercase text-center pointer-events-none'
          }
        ]
      };
    } else if (nameLower.includes('clock')) {
      config = {
        ...config,
        className: 'flex items-center justify-center bg-[#42a5f5] absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500 overflow-hidden',
        style: {
          width: '120px',
          height: '120px',
        },
        children: [
          {
            id: `${id}-time`,
            type: 'text',
            name: 'Time',
            content: '12:00 PM',
            className: 'text-white font-black text-2xl pointer-events-none'
          }
        ]
      };
    } else if (nameLower.includes('weather')) {
      config = {
        ...config,
        className: 'flex items-center justify-center bg-[#009688] shadow-md absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500 overflow-hidden',
        style: {
          width: '120px',
          height: '120px',
        },
        children: [
          {
            id: `${id}-temp`,
            type: 'text',
            name: 'Temperature',
            content: '24°C',
            className: 'text-white font-black text-2xl pointer-events-none'
          }
        ]
      };
    }

    return config;
  };

  const handleAddApp = (appName: string, widget?: Widget, dropX?: number, dropY?: number) => {

    const id = `app-${Date.now()}`;
    const baseConfig = getAppConfig(appName, id, widget);

    // If we have a real widget object, use its specific settings
    if (widget) {
      baseConfig.name = widget.name;
      baseConfig.type = 'app';
      baseConfig.isApp = true;
      // Use preferred dimensions or fallback to defaults
      baseConfig.style = {
        width: `${widget.width || 480}px`,
        height: `${widget.height || 360}px`
      };
      baseConfig.children = [];
      baseConfig.className = 'absolute cursor-move overflow-hidden rounded-lg';

      // Use the centralized utility for mapping widget data to replacements
      const replacements = getWidgetReplacements(widget);

      // Handle RSS feed if present (legacy support for specific rssData structure if needed)
      if (widget.rss_feed_url) {
        replacements.push(['rssData', JSON.stringify({
          url: widget.rss_feed_url,
          count: 10
        })]);
      }

      const newWidgetSettings: WidgetSettings = {
        htmlUrl: widget.html_url,
        jsUrl: widget.widget_id?.script_url || '',
        replacements,
        title: widget.name,
        category: widget.widget_id?.category,
        isCmsApp: true,
        latitude: (widget as any).latitude,
        longitude: (widget as any).longitude,
        location: (widget as any).location || (widget as any).CurrentLocation,
        time_zone: (widget as any).time_zone || (widget as any).TimeZone
      };

      setWidgetSettings(prev => ({
        ...prev,
        [id]: newWidgetSettings
      }));
    }

    // Add to canvas at default position
    const { layer: newLayer, position } = placeElementOnCanvas(baseConfig, dropX, dropY);

    if (newLayer) {
      setLayerPositions(prev => ({ ...prev, [id]: position }));

      // Nest within root
      setDynamicLayers(prev => {
        const hasRoot = prev.some(l => l.id === 'root');
        if (hasRoot) {
          return prev.map(layer => {
            if (layer.id === 'root') {
              return {
                ...layer,
                children: [...(layer.children || []), newLayer]
              };
            }
            return layer;
          });
        }
        return [...prev, newLayer];
      });

      setSelectedLayerId(id);
      commitToHistory('LAYER_ADD', `Added app: ${appName}`);
    }
  };

  const handleAddPlaylist = (playlistData: any, dropX?: number, dropY?: number) => {

    const id = `playlist-${Date.now()}`;
    const baseConfig = getPlaylistConfig(playlistData, id);

    // Add to canvas at default position
    const { layer: newLayer, position } = placeElementOnCanvas(baseConfig, dropX, dropY);

    if (newLayer) {
      setLayerPositions(prev => ({ ...prev, [id]: position }));

      // Nest within root
      setDynamicLayers(prev => {
        const hasRoot = prev.some(l => l.id === 'root');
        if (hasRoot) {
          return prev.map(layer => {
            if (layer.id === 'root') {
              return {
                ...layer,
                children: [...(layer.children || []), newLayer]
              };
            }
            return layer;
          });
        }
        return [...prev, newLayer];
      });

      setSelectedLayerId(id);
      commitToHistory('LAYER_ADD', `Added playlist: ${playlistData.name}`);
      setActiveLeftPanel(null);
    }
  };

  // Text editing handlers
  const handleTextDoubleClick = (id: string) => {
    setEditingTextId(id || null);
  };

  const handleTextContentChange = (id: string, content: string) => {
    setTextContents(prev => ({
      ...prev,
      [id]: content
    }));

    // If the layer has an active data binding, remove it because the user manually edited the text, making it static.
    // This prevents the text from reverting back to the API resolved value on editing completion.
    if (textStyles[id]?.binding) {
      setTextStyles(prev => {
        const next = { ...prev };
        if (next[id]) {
          const { binding, ...rest } = next[id];
          next[id] = rest;
        }
        return next;
      });
    }

    // Trigger an immediate layout re-measurement so the selection box adapts to the new text volume
    setLayoutSettledTrigger(prev => prev + 1);
  };

  const handleTextEditComplete = (id: string) => {
    // If there's a pending debounced commit, clear it
    if (textEditDebounceRef.current) {
      clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = null;
    }
    // Commit text content change to history when editing finishes
    commitToHistory('LAYER_EDIT', `Edited text in ${id}`);
  };

  const handleTextStyleChange = (styleChanges: Partial<TextStyle>, commit = false) => {
    if (!selectedLayerId) return;

    // Get current style (either from textStyles or compute from element)
    let currentStyle = textStyles[selectedLayerId];

    if (!currentStyle) {
      // First time editing this text - extract current computed styles
      const element = document.getElementById(selectedLayerId);
      if (element) {
        const computed = window.getComputedStyle(element);
        const fontSize = parseFloat(computed.fontSize);
        currentStyle = {
          fontFamily: computed.fontFamily.replace(/['"]/g, '').split(',')[0].trim(),
          fontSize: fontSize,
          fontWeight: normalizeFontWeight(computed.fontWeight),
          lineHeight: computed.lineHeight === 'normal' ? 1.5 : parseFloat(computed.lineHeight) / fontSize,
          letterSpacing: parseFloat(computed.letterSpacing) || 0,
          textAlign: (computed.textAlign as 'left' | 'center' | 'right') || 'left',
          textTransform: (computed.textTransform as 'none' | 'uppercase' | 'lowercase') || 'none',
          color: rgbToHex(computed.color),
          opacity: parseFloat(computed.opacity) * 100 || 100,
          italic: computed.fontStyle === 'italic',
          underline: computed.textDecoration.includes('underline'),
          uppercase: computed.textTransform === 'uppercase',
        };
      }
    }

    // 1. Calculate new style
    const newStyle = {
      ...currentStyle,
      ...styleChanges
    };

    // 2. Update text styles state
    setTextStyles(prev => ({
      ...prev,
      [selectedLayerId]: newStyle
    }));

    // 3. Handle animation-specific logic
    const isAnimationChange = Object.keys(styleChanges).some(key => key.startsWith('animation')) || styleChanges.animation !== undefined;
    if (isAnimationChange) {
      if (newStyle.animation?.type && newStyle.animation.type !== 'none' && !isTimelineOpen) {
        setIsTimelineOpen(true);
      }
      // Sync to base layer
      handleUpdateLayer(selectedLayerId, {
        animation: newStyle.animation
      });

      // Reset and play preview
      setCurrentTime(0);
      setPlaybackId(prev => prev + 1);
      // Removed explicit setIsPlaying(true) to avoid "messy" behavior
    }

    if (commit) {
      commitToHistory('TEXT_STYLE_CHANGE', `Finalized text style for ${selectedLayerId}`);
    }

    // Trigger layout re-measurement after style change (fontSize, fontFamily, etc.)
    // to ensure the bounding box adapts correctly to the new visual properties.
    setTimeout(() => {
      setLayoutSettledTrigger(prev => prev + 1);
    }, 10);
  };

  // Helper to convert rgb/rgba to hex
  const rgbToHex = (rgb: string): string => {
    const matches = rgb.match(/\d+/g);
    if (!matches || matches.length < 3) return '#000000';
    const r = parseInt(matches[0]);
    const g = parseInt(matches[1]);
    const b = parseInt(matches[2]);
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  };

  const handleBackgroundSettingsChange = (changes: Partial<BackgroundSettings>, commit = false) => {
    if (!selectedLayerId) return;

    setBackgroundSettings(prev => ({
      ...prev,
      [selectedLayerId]: {
        ...prev[selectedLayerId],
        ...changes
      }
    }));

    if (commit) {
      // Commit to history only when requested (usually on release)
      commitToHistory('LAYER_EDIT', `Finalized background settings for ${selectedLayerId}`);
    }
  };

  const handleImageSettingsChange = (changes: Partial<ImageSettings>, commit = false) => {
    if (!selectedLayerId) return;

    // 1. Calculate new settings
    const current = imageSettings[selectedLayerId] || {};
    const newSettings = {
      ...current,
      ...changes
    };

    // Automatically open timeline when an animation is selected
    if (changes.animation?.type && changes.animation.type !== 'none' && !isTimelineOpen) {
      setIsTimelineOpen(true);
    }

    // 2. Update image settings state
    setImageSettings(prev => ({
      ...prev,
      [selectedLayerId]: newSettings
    }));

    // 3. Handle animation-specific logic
    const isAnimationChange = Object.keys(changes).some(key => key.startsWith('animation')) || changes.animation !== undefined;
    if (isAnimationChange) {
      // Sync to base layer
      handleUpdateLayer(selectedLayerId, {
        animation: newSettings.animation
      });

      // Reset and play preview immediately
      setCurrentTime(0);
      setPlaybackId(prev => prev + 1);
      setIsPlaying(true);
    }

    if (commit) {
      // Commit to history only when requested (usually on release)
      commitToHistory('LAYER_EDIT', `Finalized image settings for ${selectedLayerId}`);
    }
  };

  const handleBindingChange = (layerId: string, binding: any | null) => {
    setSelectedLayerId(layerId);
    const layer = findLayerById(dynamicLayers, layerId);
    if (!layer) return;

    if (binding) {
      const api = customApis.find(a => String(a._id) === String(binding.customApiId));
      let resolvedValue = '';
      let hasResolved = false;
      
      if (binding.apiKey && api && api.customapidata) {
        const val = resolveJsonPath(api.customapidata, binding.apiKey);
        if (val !== undefined && val !== null && typeof val !== 'object') {
          resolvedValue = String(val);
          hasResolved = true;
        }
      }
      if (!hasResolved && binding.fallbackValue !== undefined && binding.fallbackValue !== '') {
        resolvedValue = binding.fallbackValue;
        hasResolved = true;
      }

      if (layer.type === 'text') {
        if (hasResolved) {
          setTextContents(prev => ({
            ...prev,
            [layerId]: resolvedValue
          }));
        }
        handleTextStyleChange({ binding }, true);
      } else if (layer.type === 'image') {
        if (hasResolved) {
          handleImageSettingsChange({ binding, url: resolvedValue }, true);
        } else {
          handleImageSettingsChange({ binding }, true);
        }
      } else if (layer.type === 'scroller' || layer.isApp) {
        if (hasResolved) {
          handleScrollerSettingsChange(layerId, { binding, content: resolvedValue });
        } else {
          handleScrollerSettingsChange(layerId, { binding });
        }
      }
    } else {
      if (layer.type === 'text') {
        const oldBinding = textStyles[layerId]?.binding;
        const restoredText = oldBinding?.fallbackValue || layer.content || '';
        setTextContents(prev => ({
          ...prev,
          [layerId]: restoredText
        }));
        handleTextStyleChange({ binding: undefined }, true);
      } else if (layer.type === 'image') {
        const oldBinding = imageSettings[layerId]?.binding;
        const restoredUrl = oldBinding?.fallbackValue || layer.content || '';
        handleImageSettingsChange({ binding: undefined, url: restoredUrl }, true);
      } else if (layer.type === 'scroller' || layer.isApp) {
        const oldBinding = scrollerSettings[layerId]?.binding;
        const restoredContent = oldBinding?.fallbackValue || layer.content || '';
        handleScrollerSettingsChange(layerId, { binding: undefined, content: restoredContent });
      }
    }
  };

  const handleVideoSettingsChange = (changes: Partial<VideoSettings>, commit = false) => {
    if (!selectedLayerId) return;

    // 1. Calculate new settings
    const current = videoSettings[selectedLayerId] || {};
    const newSettings = {
      ...current,
      ...changes
    };

    // 2. Update video settings state
    setVideoSettings(prev => ({
      ...prev,
      [selectedLayerId]: newSettings
    }));

    // 3. Handle animation-specific logic
    const isAnimationChange = Object.keys(changes).some(key => key.startsWith('animation'));
    if (isAnimationChange) {
      if (newSettings.animation?.type && newSettings.animation.type !== 'none' && !isTimelineOpen) {
        setIsTimelineOpen(true);
      }
      // Sync to base layer
      const s = newSettings.animation;
      if (s) {
        handleUpdateLayer(selectedLayerId, {
          animation: {
            type: (s.type as any) || 'none',
            duration: s.duration ?? 1000,
            delay: s.delay ?? 0,
            easing: (s.easing as any) || 'ease',
            loop: s.loop,
            keyframes: s.keyframes
          }
        });
      }

      // Reset and play preview immediately
      setCurrentTime(0);
      setPlaybackId(prev => prev + 1);
      // Removed explicit setIsPlaying(true) to avoid "messy" behavior
    }

    if (commit) {
      commitToHistory('LAYER_EDIT', `Finalized video settings for ${selectedLayerId}`);
    }
  };

  const handleShapeSettingsChange = (changes: Partial<ShapeSettings>, commit = false) => {
    if (!selectedLayerId) return;

    // 1. Calculate new settings
    const current = shapeSettings[selectedLayerId] || {};
    const newSettings = {
      ...current,
      ...changes
    };

    // 2. Update shape settings state
    setShapeSettings(prev => ({
      ...prev,
      [selectedLayerId]: newSettings
    }));

    // 3. Handle animation-specific logic
    const isAnimationChange = Object.keys(changes).some(key => key.startsWith('animation'));
    if (isAnimationChange) {
      if (newSettings.animation?.type && newSettings.animation.type !== 'none' && !isTimelineOpen) {
        setIsTimelineOpen(true);
      }
      // Sync to base layer
      const anim = newSettings.animation;
      if (anim) {
        handleUpdateLayer(selectedLayerId, { animation: anim });
      }

      // Reset and play preview immediately
      setCurrentTime(0);
      setPlaybackId(prev => prev + 1);
      setIsPlaying(true);
    }

    if (commit) {
      // Commit to history only when requested (usually on release)
      commitToHistory('LAYER_EDIT', `Finalized shape settings for ${selectedLayerId}`);
    }
  };

  // Helper to normalize font weight
  const normalizeFontWeight = (weight: string | number): 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black' => {
    const numWeight = typeof weight === 'number' ? weight : parseInt(weight);
    if (numWeight >= 900) return 'black';
    if (numWeight >= 800) return 'extrabold';
    if (numWeight >= 700) return 'bold';
    if (numWeight >= 600) return 'semibold';
    if (numWeight >= 500) return 'medium';
    return 'normal';
  };

  const handleDeleteSelected = useCallback(() => {
    // Filter out the root canvas from deletion
    const idsToRemove = selectedLayerIds.filter(id => id !== 'root');
    if (idsToRemove.length === 0) return;

    setDynamicLayers(prev => removeLayersRecursive(prev, idsToRemove));

    setLayerPositions(prev => {
      const newPositions = { ...prev };
      idsToRemove.forEach(id => delete newPositions[id]);
      return newPositions;
    });

    // Clean up all state maps associated with the deleted layers
    setTextContents(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setTextStyles(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setShapeSettings(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setImageSettings(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setBackgroundSettings(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setScrollerSettings(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    setWidgetSettings(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });

    // If root was selected, keep it selected, otherwise clear selection
    const remainingSelected = selectedLayerIds.includes('root') ? ['root'] : [];
    setSelectedLayerIds(remainingSelected);

    commitToHistory('DELETE_ELEMENT', `Deleted ${idsToRemove.length} elements`);
  }, [selectedLayerIds, commitToHistory]);

  const handleGroupSelected = useCallback(() => {
    if (selectedLayerIds.length < 2 || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();

    // Get all selected layers
    const selectedLayers = selectedLayerIds
      .map(id => findLayerById(dynamicLayers, id))
      .filter((l): l is Layer => l !== null && l.id !== 'root');

    if (selectedLayers.length < 2) return;

    // Use the absolute bounds utility for consistent positioning
    const groupBounds = getMultiLayerBounds(selectedLayerIds, canvasRect, canvasScale);
    if (!groupBounds) {
      console.error("Failed to calculate group bounds");
      return;
    }

    const groupId = `group-${Date.now()}`;
    const newGroup: Layer = {
      id: groupId,
      type: 'group',
      name: 'Group',
      children: selectedLayers,
      locked: false,
      hidden: false,
      style: {
        zIndex: 50 // Standardize on a high z-index for new groups so they don't default to 20 and get hidden
      }
    };

    // Calculate the group position and size
    const groupPosition = {
      left: groupBounds.left,
      top: groupBounds.top,
      width: groupBounds.width,
      height: groupBounds.height
    };
    const layerIdsToGroup = selectedLayers.map(l => l.id);

    setDynamicLayers(prev => {
      // 1. Remove grouped layers from everywhere in the tree
      const cleanedLayers = removeLayersRecursive(prev, layerIdsToGroup);

      // 2. Add the group to the root (or common ancestor - for now root is fine)
      const hasRoot = cleanedLayers.some(l => l.id === 'root');
      if (hasRoot) {
        return cleanedLayers.map(layer => {
          if (layer.id === 'root') {
            return {
              ...layer,
              children: [...(layer.children || []), newGroup]
            };
          }
          return layer;
        });
      }
      return [...cleanedLayers, newGroup];
    });

    setLayerPositions(prev => {
      const next = { ...prev };
      next[newGroup.id] = groupPosition;

      // Update children to be relative to the new group using absolute measurements
      selectedLayers.forEach(layer => {
        const absPos = getAbsolutePosition(layer.id, canvasRect, canvasScale);
        if (absPos) {
          next[layer.id] = {
            ...prev[layer.id],
            left: absPos.left - groupBounds.left,
            top: absPos.top - groupBounds.top,
            width: absPos.width,
            height: absPos.height
          };
        }
      });
      return next;
    });

    setSelectedLayerIds([newGroup.id]);
    commitToHistory('GROUP_LAYERS', `Grouped ${selectedLayers.length} layers`);
  }, [selectedLayerIds, dynamicLayers, layerPositions, canvasScale, commitToHistory]);

  const handleToggleLockSelected = useCallback(() => {
    if (selectedLayerIds.length === 0) return;

    const idsToLock = selectedLayerIds.filter(id => id !== 'root');
    if (idsToLock.length === 0) return;

    const layers = idsToLock.map(id => findLayerById(dynamicLayers, id)).filter((l): l is Layer => l !== null);
    const anyUnlocked = layers.some(l => !l.locked);
    const shouldLock = anyUnlocked;

    setDynamicLayers(prev => updateLockRecursive(prev, idsToLock, shouldLock));
    commitToHistory('LAYER_EDIT', `${shouldLock ? 'Locked' : 'Unlocked'} ${idsToLock.length} layers`);
  }, [selectedLayerIds, dynamicLayers, commitToHistory]);

  const handleUngroupSelected = useCallback(() => {
    if (selectedLayerIds.length !== 1) return;
    const groupId = selectedLayerIds[0];
    const group = findLayerById(dynamicLayers, groupId);
    if (!group || group.type !== 'group' || !group.children) return;

    const groupPos = layerPositions[groupId] || { left: 0, top: 0 };
    const childrenToRestore = group.children;

    // Helper to replace group with children in the tree
    const replaceWithChildren = (layers: Layer[]): Layer[] => {
      return layers.flatMap(l => {
        if (l.id === groupId) return childrenToRestore;
        if (l.children) return [{ ...l, children: replaceWithChildren(l.children) }];
        return [l];
      });
    };

    setDynamicLayers(prev => replaceWithChildren(prev));

    setLayerPositions(prev => {
      const next = { ...prev };
      childrenToRestore.forEach(child => {
        const childPos = prev[child.id] || { left: 0, top: 0 };
        next[child.id] = {
          ...childPos,
          left: childPos.left + groupPos.left,
          top: childPos.top + groupPos.top
        };
      });
      delete next[groupId];
      return next;
    });

    setSelectedLayerIds(childrenToRestore.map(c => c.id));
    commitToHistory('GROUP_LAYERS', `Ungrouped ${childrenToRestore.length} layers`);
  }, [selectedLayerIds, dynamicLayers, layerPositions, commitToHistory]);

  const handleDuplicateSelected = useCallback(() => {
    if (selectedLayerIds.length === 0) return;

    const idsToDuplicate = selectedLayerIds.filter(id => id !== 'root');
    if (idsToDuplicate.length === 0) return;

    const OFFSET = 20;
    const newSelectedIds: string[] = [];
    const nextPositions = { ...layerPositions };
    const nextTextContents = { ...textContents };
    const nextTextStyles = { ...textStyles };
    const nextShapeSettings = { ...shapeSettings };
    const nextImageSettings = { ...imageSettings };
    const nextBackgroundSettings = { ...backgroundSettings };
    const nextScrollerSettings = { ...scrollerSettings };
    const nextWidgetSettings = { ...widgetSettings };
    const nextVideoSettings = { ...videoSettings };

    const duplicateLayer = (layer: Layer): Layer => {
      const newId = `${layer.id}-copy-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      newSelectedIds.push(newId);

      const pos = layerPositions[layer.id];
      const canvasRect = canvasRef.current?.getBoundingClientRect();

      const absPos = canvasRect ? getAbsolutePosition(layer.id, canvasRect, canvasScale) : null;

      if (absPos) {
        // Use current absolute position to ensure the copy is placed correctly 
        // even if the original is nested in a group but the copy will be at root
        nextPositions[newId] = {
          ...pos,
          left: absPos.left + OFFSET,
          top: absPos.top + OFFSET,
          width: absPos.width,
          height: absPos.height
        };
      } else if (pos) {
        nextPositions[newId] = {
          ...pos,
          left: (pos.left ?? 0) + OFFSET,
          top: (pos.top ?? 0) + OFFSET
        };
      } else {
        const left = parseFloat(String(layer.style?.left || '0'));
        const top = parseFloat(String(layer.style?.top || '0'));
        nextPositions[newId] = {
          left: left + OFFSET,
          top: top + OFFSET
        };
      }

      if (textContents[layer.id]) nextTextContents[newId] = textContents[layer.id];
      if (textStyles[layer.id]) nextTextStyles[newId] = { ...textStyles[layer.id] };
      if (shapeSettings[layer.id]) nextShapeSettings[newId] = { ...shapeSettings[layer.id] };
      if (imageSettings[layer.id]) nextImageSettings[newId] = { ...imageSettings[layer.id] };
      if (backgroundSettings[layer.id]) nextBackgroundSettings[newId] = { ...backgroundSettings[layer.id] };
      if (scrollerSettings[layer.id]) nextScrollerSettings[newId] = { ...scrollerSettings[layer.id] };
      if (widgetSettings[layer.id]) nextWidgetSettings[newId] = { ...widgetSettings[layer.id] };
      if (videoSettings[layer.id]) nextVideoSettings[newId] = { ...videoSettings[layer.id] };

      return {
        ...layer,
        id: newId,
        name: `${layer.name} (Copy)`,
        children: layer.children ? layer.children.map(duplicateLayer) : undefined,
        style: {
          ...layer.style,
          left: `${(nextPositions[newId].left)}px`,
          top: `${(nextPositions[newId].top)}px`
        }
      };
    };

    setDynamicLayers(prev => {
      const newDuplicatedLayers = idsToDuplicate.map(id => {
        const layer = findLayerById(prev, id);
        return layer && layer.id !== 'root' ? duplicateLayer(layer) : null;
      }).filter((l): l is Layer => l !== null);

      const root = prev.find(l => l.id === 'root');
      if (root) {
        return prev.map(l => l.id === 'root' ? {
          ...l,
          children: [...(l.children || []), ...newDuplicatedLayers]
        } : l);
      }
      return [...prev, ...newDuplicatedLayers];
    });

    setLayerPositions(nextPositions);
    setTextContents(nextTextContents);
    setTextStyles(nextTextStyles);
    setShapeSettings(nextShapeSettings);
    setImageSettings(nextImageSettings);
    setBackgroundSettings(nextBackgroundSettings);
    setScrollerSettings(nextScrollerSettings);
    setWidgetSettings(nextWidgetSettings);
    setVideoSettings(prev => ({ ...prev, ...nextVideoSettings }));

    setSelectedLayerIds(newSelectedIds);
    commitToHistory('DUPLICATE_ELEMENT', `Duplicated ${idsToDuplicate.length} elements`);
  }, [selectedLayerIds, dynamicLayers, layerPositions, textContents, textStyles, shapeSettings, imageSettings, videoSettings, backgroundSettings, scrollerSettings, widgetSettings, canvasScale, commitToHistory]);


  const handleCopy = useCallback(() => {
    const idsToCopy = selectedLayerIds.filter(id => id !== 'root');
    if (idsToCopy.length === 0) return;

    const copiedLayers = idsToCopy.map(id => findLayerById(dynamicLayers, id)).filter((l): l is Layer => l !== null);

    // Capture all associated settings
    const positions: Record<string, any> = {};
    const contents: Record<string, string> = {};
    const styles: Record<string, any> = {};
    const shapes: Record<string, any> = {};
    const images: Record<string, any> = {};
    const videos: Record<string, any> = {};
    const scrollers: Record<string, any> = {};
    const widgets: Record<string, any> = {};
    const backgrounds: Record<string, any> = {};

    const captureSettings = (layer: Layer) => {
      positions[layer.id] = layerPositions[layer.id];
      if (textContents[layer.id]) contents[layer.id] = textContents[layer.id];
      if (textStyles[layer.id]) styles[layer.id] = textStyles[layer.id];
      if (shapeSettings[layer.id]) shapes[layer.id] = shapeSettings[layer.id];
      if (imageSettings[layer.id]) images[layer.id] = imageSettings[layer.id];
      if (videoSettings[layer.id]) videos[layer.id] = videoSettings[layer.id];
      if (scrollerSettings[layer.id]) scrollers[layer.id] = scrollerSettings[layer.id];
      if (widgetSettings[layer.id]) widgets[layer.id] = widgetSettings[layer.id];
      if (backgroundSettings[layer.id]) backgrounds[layer.id] = backgroundSettings[layer.id];

      if (layer.children) {
        layer.children.forEach(captureSettings);
      }
    };

    copiedLayers.forEach(captureSettings);

    setClipboard({
      layers: copiedLayers,
      positions,
      textContents: contents,
      textStyles: styles,
      shapeSettings: shapes,
      imageSettings: images,
      videoSettings: videos,
      scrollerSettings: scrollers,
      widgetSettings: widgets,
      backgroundSettings: backgrounds
    });

    toast.success(`Copied ${idsToCopy.length} element(s)`);
  }, [selectedLayerIds, dynamicLayers, layerPositions, textContents, textStyles, shapeSettings, imageSettings, videoSettings, scrollerSettings, widgetSettings, backgroundSettings]);

  const handlePaste = useCallback(() => {
    if (!clipboard) return;

    const OFFSET = 20;
    const newSelectedIds: string[] = [];
    const nextPositions = { ...layerPositions };
    const nextTextContents = { ...textContents };
    const nextTextStyles = { ...textStyles };
    const nextShapeSettings = { ...shapeSettings };
    const nextImageSettings = { ...imageSettings };
    const nextScrollerSettings = { ...scrollerSettings };
    const nextWidgetSettings = { ...widgetSettings };
    const nextVideoSettings = { ...videoSettings };
    const nextBackgroundSettings = { ...backgroundSettings };

    const duplicateLayer = (layer: Layer): Layer => {
      const newId = `${layer.id}-copy-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      newSelectedIds.push(newId);

      const pos = clipboard.positions[layer.id];

      // When pasting, we want to offset everything from its copied position
      if (pos) {
        nextPositions[newId] = {
          ...pos,
          left: (pos.left ?? 0) + OFFSET,
          top: (pos.top ?? 0) + OFFSET
        };
      } else {
        const left = parseFloat(String(layer.style?.left || '0'));
        const top = parseFloat(String(layer.style?.top || '0'));
        nextPositions[newId] = {
          left: left + OFFSET,
          top: top + OFFSET
        };
      }

      if (clipboard.textContents[layer.id]) nextTextContents[newId] = clipboard.textContents[layer.id];
      if (clipboard.textStyles[layer.id]) nextTextStyles[newId] = { ...clipboard.textStyles[layer.id] };
      if (clipboard.shapeSettings[layer.id]) nextShapeSettings[newId] = { ...clipboard.shapeSettings[layer.id] };
      if (clipboard.imageSettings[layer.id]) nextImageSettings[newId] = { ...clipboard.imageSettings[layer.id] };
      if (clipboard.videoSettings[layer.id]) nextVideoSettings[newId] = { ...clipboard.videoSettings[layer.id] };
      if (clipboard.scrollerSettings[layer.id]) nextScrollerSettings[newId] = { ...clipboard.scrollerSettings[layer.id] };
      if (clipboard.widgetSettings[layer.id]) nextWidgetSettings[newId] = { ...clipboard.widgetSettings[layer.id] };
      if (clipboard.backgroundSettings[layer.id]) nextBackgroundSettings[newId] = { ...clipboard.backgroundSettings[layer.id] };

      return {
        ...layer,
        id: newId,
        name: `${layer.name} (Copy)`,
        children: layer.children ? layer.children.map(duplicateLayer) : undefined,
        style: {
          ...layer.style,
          left: `${(nextPositions[newId].left)}px`,
          top: `${(nextPositions[newId].top)}px`
        }
      };
    };

    setDynamicLayers(prev => {
      const newPastedLayers = clipboard.layers.map(layer => {
        return duplicateLayer(layer);
      });

      const root = prev.find(l => l.id === 'root');
      if (root) {
        return prev.map(l => l.id === 'root' ? {
          ...l,
          children: [...(l.children || []), ...newPastedLayers]
        } : l);
      }
      return [...prev, ...newPastedLayers];
    });

    setLayerPositions(nextPositions);
    setTextContents(nextTextContents);
    setTextStyles(nextTextStyles);
    setShapeSettings(nextShapeSettings);
    setImageSettings(nextImageSettings);
    setScrollerSettings(nextScrollerSettings);
    setWidgetSettings(nextWidgetSettings);
    setVideoSettings(prev => ({ ...prev, ...nextVideoSettings }));
    setBackgroundSettings(nextBackgroundSettings);

    setSelectedLayerIds(newSelectedIds);
    commitToHistory('LAYER_ADD', `Pasted ${clipboard.layers.length} elements`);
  }, [clipboard, dynamicLayers, layerPositions, textContents, textStyles, shapeSettings, imageSettings, videoSettings, scrollerSettings, widgetSettings, backgroundSettings, commitToHistory]);

  // Robust layout fix: ensure document body doesn't scroll while in editor
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleAlignSelected = useCallback((type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {

    if (selectedLayerIds.length < 2 || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();

    // Use the absolute bounds utility for consistent alignment
    const bounds = getMultiLayerBounds(selectedLayerIds, canvasRect, canvasScale);
    if (!bounds) return;

    const nextPositions = { ...layerPositions };
    const idsToAlign = selectedLayerIds.filter(id => id !== 'root');

    idsToAlign.forEach(id => {
      const element = document.getElementById(id);
      if (!element) return;

      const pos = layerPositions[id] || {};
      const rect = element.getBoundingClientRect();
      const currentWidth = rect.width / canvasScale;
      const currentHeight = rect.height / canvasScale;

      // Get current absolute position to calculate the shift needed
      const absPos = getAbsolutePosition(id, canvasRect, canvasScale);
      if (!absPos) return;

      let targetAbsLeft = absPos.left;
      let targetAbsTop = absPos.top;

      switch (type) {
        case 'left':
          targetAbsLeft = bounds.left;
          break;
        case 'center':
          targetAbsLeft = bounds.left + (bounds.width - currentWidth) / 2;
          break;
        case 'right':
          targetAbsLeft = bounds.left + bounds.width - currentWidth;
          break;
        case 'top':
          targetAbsTop = bounds.top;
          break;
        case 'middle':
          targetAbsTop = bounds.top + (bounds.height - currentHeight) / 2;
          break;
        case 'bottom':
          targetAbsTop = bounds.top + bounds.height - currentHeight;
          break;
      }

      // Calculate shift and apply to existing position (which might be relative to a parent)
      const shiftX = targetAbsLeft - absPos.left;
      const shiftY = targetAbsTop - absPos.top;

      // Use element measurements as fallback for base coordinates
      const baseLeft = pos.left ?? ((element.offsetLeft) || 0);
      const baseTop = pos.top ?? ((element.offsetTop) || 0);

      nextPositions[id] = {
        ...pos,
        left: baseLeft + shiftX,
        top: baseTop + shiftY,
        width: currentWidth,
        height: currentHeight
      };
    });

    setLayerPositions(nextPositions);

    setDynamicLayers(prev => {
      const updateRecursive = (layers: Layer[]): Layer[] => {
        return layers.map(l => {
          if (idsToAlign.includes(l.id) && nextPositions[l.id]) {
            return {
              ...l,
              style: {
                ...l.style,
                left: `${nextPositions[l.id].left}px`,
                top: `${nextPositions[l.id].top}px`,
                width: nextPositions[l.id].width !== undefined ? `${nextPositions[l.id].width}px` : l.style?.width,
                height: nextPositions[l.id].height !== undefined ? `${nextPositions[l.id].height}px` : l.style?.height,
              }
            };
          }
          if (l.children) return { ...l, children: updateRecursive(l.children) };
          return l;
        });
      };
      return updateRecursive(prev);
    });

    commitToHistory('ALIGN_ELEMENTS', `Aligned ${idsToAlign.length} elements to ${type}`);
  }, [selectedLayerIds, layerPositions, canvasScale, commitToHistory]);

  // Keyboard shortcuts (undo/redo and nudging)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when editing page name or text
      if (isEditingPageName || editingTextId) return;

      // Also don't trigger if focus is in an input or textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Zoom Shortcuts
      if (cmdOrCtrl && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
        return;
      }
      if (cmdOrCtrl && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
        return;
      }
      if (cmdOrCtrl && e.key === '0') {
        e.preventDefault();
        handleZoomReset();
        return;
      }

      // Undo: Cmd/Ctrl + Z
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Enter to Edit: If exactly one text layer is selected, Enter enters edit mode
      if (e.key === 'Enter' && selectedLayerIds.length === 1 && !e.shiftKey) {
        const id = selectedLayerIds[0];
        const layer = findLayerById(dynamicLayers, id);
        if (layer?.type === 'text') {
          e.preventDefault();
          handleTextDoubleClick(id);
          return;
        }
      }

      // Redo: Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y
      if ((cmdOrCtrl && e.shiftKey && e.key === 'z') || (cmdOrCtrl && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
        return;
      }

      // Copy: Cmd/Ctrl + C
      if (cmdOrCtrl && (e.key === 'c' || e.key === 'C')) {
        e.preventDefault();
        handleCopy();
        return;
      }

      // Paste: Cmd/Ctrl + V
      if (cmdOrCtrl && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        handlePaste();
        return;
      }

      // Delete: Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedLayerIds.length > 0) {
        // We filter 'root' inside handleDeleteSelected, so we just call it here if anything is selected
        const idsToDelete = selectedLayerIds.filter(id => id !== 'root');
        if (idsToDelete.length > 0) {
          e.preventDefault();
          handleDeleteSelected();
          return;
        }
      }

      // Keyboard nudging
      if (selectedLayerIds.length === 0) return;

      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;
      let handled = false;

      switch (e.key) {
        case 'ArrowLeft': dx = -step; handled = true; break;
        case 'ArrowRight': dx = step; handled = true; break;
        case 'ArrowUp': dy = -step; handled = true; break;
        case 'ArrowDown': dy = step; handled = true; break;
        case 'Escape':
          setSelectedLayerIds([]);
          handled = true;
          break;
      }

      if (handled && (dx !== 0 || dy !== 0)) {
        e.preventDefault();

        setLayerPositions(prev => {
          const next = { ...prev };
          selectedLayerIds.forEach(id => {
            const current = prev[id];
            if (current) {
              next[id] = {
                ...current,
                left: current.left + dx,
                top: current.top + dy
              };
            } else {
              const element = document.getElementById(id);
              if (element) {
                next[id] = {
                  left: element.offsetLeft + dx,
                  top: element.offsetTop + dy
                };
              }
            }
          });
          return next;
        });

        // Also update the permanent styles in dynamicLayers for persistence
        const updateRecursive = (layers: Layer[]): Layer[] => {
          return layers.map(layer => {
            let updatedLayer = layer;
            if (selectedLayerIds.includes(layer.id)) {
              const pos = layerPositions[layer.id];
              const element = document.getElementById(layer.id);

              const currentLeft = pos ? pos.left : (parseFloat(String(layer.style?.left || '0')) || (element?.offsetLeft || 0));
              const currentTop = pos ? pos.top : (parseFloat(String(layer.style?.top || '0')) || (element?.offsetTop || 0));

              updatedLayer = {
                ...layer,
                style: {
                  ...layer.style,
                  left: `${currentLeft + dx}px`,
                  top: `${currentTop + dy}px`,
                  ...(pos?.width !== undefined ? { width: `${pos.width}px` } : {}),
                  ...(pos?.height !== undefined ? { height: `${pos.height}px` } : {}),
                }
              };
            }
            if (updatedLayer.children) {
              return {
                ...updatedLayer,
                children: updateRecursive(updatedLayer.children)
              };
            }
            return updatedLayer;
          });
        };
        setDynamicLayers(prev => updateRecursive(prev));
        commitToHistory('LAYER_MOVE', `Nudged ${selectedLayerIds.length} layer(s) ${step}px`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedLayerIds,
    canvasScale,
    commitToHistory,
    isEditingPageName,
    editingTextId,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleUndo,
    handleRedo,
    handleCopy,
    handlePaste,
    handleDeleteSelected,
    layerPositions,
    dynamicLayers
  ]);



  // --- Dimension Synchronization Helpers ---
  const handleDimensionsChange = useCallback((id: string, updates: Partial<{ left: number; top: number; width: number; height: number; rotation?: number }>) => {
    const pos = layerPositions[id];
    if (!pos) return;



    const deltaW = (updates.width !== undefined ? updates.width : (pos.width || 0)) - (pos.width || 0);
    const deltaH = (updates.height !== undefined ? updates.height : (pos.height || 0)) - (pos.height || 0);

    if (deltaW !== 0 || deltaH !== 0) {
      // Use anchor (0,0) for manual numeric inputs (top-left remains fixed)
      const anchored = calculateAnchoredPosition(
        {
          left: pos.left,
          top: pos.top,
          width: pos.width || 0,
          height: pos.height || 0,
          rotation: pos.rotation || 0
        },
        deltaW,
        deltaH,
        0, 0
      );

      setLayerPositions(prev => ({
        ...prev,
        [id]: { ...prev[id], ...updates, ...anchored }
      }));
    } else {
      setLayerPositions(prev => ({
        ...prev,
        [id]: { ...prev[id], ...updates }
      }));
    }
  }, [layerPositions]);

  const handleDimensionsCommit = useCallback((id: string, type: string) => {
    const pos = layerPositions[id];
    if (!pos) return;



    setDynamicLayers(prev => {
      const updateRecursive = (layers: Layer[]): Layer[] => {
        return layers.map(l => {
          if (l.id === id) {
            return {
              ...l,
              style: {
                ...l.style,
                left: `${pos.left}px`,
                top: `${pos.top}px`,
                width: `${pos.width}px`,
                height: `${pos.height}px`,
                ...(pos.rotation !== undefined ? { transform: `rotate(${pos.rotation}deg)` } : {})
              }
            };
          }
          if (l.children) return { ...l, children: updateRecursive(l.children) };
          return l;
        });
      };
      return updateRecursive(prev);
    });

    commitToHistory('LAYER_EDIT', `Adjusted ${type} ${id}`);
  }, [layerPositions]);

  // Single layer actions for Layers Panel
  const handleLayerDeleteDirect = useCallback((id: string) => {
    if (id === 'root') return;

    setDynamicLayers(prev => removeLayersRecursive(prev, [id]));

    setLayerPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[id];
      return newPositions;
    });

    if (selectedLayerIds.includes(id)) {
      setSelectedLayerIds(prev => prev.filter(lid => lid !== id));
    }
    if (selectedLayerId === id) {
      setSelectedLayerId(null);
    }

    commitToHistory('DELETE_ELEMENT', 'Deleted layer');
  }, [selectedLayerId, selectedLayerIds, commitToHistory]);

  const handleLayerDuplicateDirect = useCallback((id: string) => {
    const layer = findLayerById(dynamicLayers, id);
    if (!layer) return;

    const newId = `${layer.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const cloneWithNewIds = (l: Layer): Layer => {
      const childNewId = `${l.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      return {
        ...l,
        id: childNewId,
        children: l.children ? l.children.map(c => cloneWithNewIds(c)) : undefined
      };
    };

    const newLayer = {
      ...layer,
      id: newId,
      name: `${layer.name} Copy`,
      children: layer.children ? layer.children.map(c => cloneWithNewIds(c)) : undefined
    };

    const pos = layerPositions[id];
    const newPos = pos
      ? { ...pos, left: pos.left + 20, top: pos.top + 20 }
      : { left: 20, top: 20 };

    // Duplicate settings
    if (shapeSettings[id]) setShapeSettings(prev => ({ ...prev, [newId]: { ...shapeSettings[id] } }));
    if (textStyles[id]) setTextStyles(prev => ({ ...prev, [newId]: { ...textStyles[id] } }));
    if (imageSettings[id]) setImageSettings(prev => ({ ...prev, [newId]: { ...imageSettings[id] } }));
    if (scrollerSettings[id]) setScrollerSettings(prev => ({ ...prev, [newId]: { ...scrollerSettings[id] } }));
    if (widgetSettings[id]) setWidgetSettings(prev => ({ ...prev, [newId]: { ...widgetSettings[id] } }));
    if (videoSettings[id]) setVideoSettings(prev => ({ ...prev, [newId]: { ...videoSettings[id] } }));

    setDynamicLayers(prev => {
      return prev.map(l => {
        if (l.id === 'root') {
          return {
            ...l,
            children: [...(l.children || []), newLayer]
          };
        }
        return l;
      });
    });

    setLayerPositions(prev => ({ ...prev, [newId]: newPos }));
    setSelectedLayerIds([newId]);

    commitToHistory('ADD_ELEMENT', `Duplicated ${layer.name}`);
  }, [dynamicLayers, layerPositions, shapeSettings, textStyles, imageSettings, scrollerSettings, widgetSettings, commitToHistory, setSelectedLayerIds, setDynamicLayers, setLayerPositions, setShapeSettings, setTextStyles, setImageSettings, setScrollerSettings, setWidgetSettings]);

  // Deselect text on selection change (if not a text layer)
  useEffect(() => {
    if (selectedLayerId) {
      const layer = findLayerById(dynamicLayers, selectedLayerId);
      if (layer && layer.type !== 'text') {
        setEditingTextId(null);
      }
    } else {
      setEditingTextId(null);
    }
  }, [selectedLayerId, dynamicLayers]);

  // Layer drag handlers
  const handleLayerDragStart = (layerId: string, e: React.MouseEvent) => {
    if (!canvasRef.current) return;

    // Resolve the actual ID we want to drag (e.g. the parent group)
    const resolvedId = findSelectableId(layerId);

    // If the layer is NOT already selected, select it (unless shift is pressed)
    if (!selectedLayerIds.includes(resolvedId)) {
      setSelectedLayerIds(prev => {
        if (e.shiftKey) {
          if (resolvedId === 'root') return prev;
          const cleanPrev = prev.filter(id => id !== 'root');
          if (cleanPrev.includes(resolvedId)) return cleanPrev;
          return [...cleanPrev, resolvedId];
        } else {
          return [resolvedId];
        }
      });
    }

    // Capture initial positions of ALL selected layers
    let layersToCapture: string[] = [];
    if (selectedLayerIds.includes(resolvedId)) {
      layersToCapture = selectedLayerIds;
    } else {
      if (e.shiftKey) {
        layersToCapture = [...selectedLayerIds.filter(id => id !== 'root'), resolvedId];
      } else {
        layersToCapture = [resolvedId];
      }
    }

    // Ensure uniqueness in layersToCapture
    layersToCapture = [...new Set(layersToCapture)];

    const initialPositions: Record<string, { left: number; top: number; width?: number; height?: number; rotation?: number }> = {};

    layersToCapture.forEach(id => {
      const element = document.getElementById(id);
      const layer = findLayerById(dynamicLayers, id);
      if (element && layer) {
        const statePos = layerPositions[id];

        // CRITICAL: Capture width/height from state OR DOM to ensure they aren't lost during move
        // For text elements, we ONLY capture fixed dimensions if they have been explicitly resized
        // We track width and height independently to support "Hug Horizontal" and "Hug Vertical"
        // We also check layer.style as a fallback if transient state isn't populated
        const initialWidth = statePos?.width !== undefined ? statePos.width : (layer.style?.width !== undefined ? parseFloat(String(layer.style.width).replace('px', '')) : undefined);
        const initialHeight = statePos?.height !== undefined ? statePos.height : (layer.style?.height !== undefined ? parseFloat(String(layer.style.height).replace('px', '')) : undefined);

        initialPositions[id] = {
          left: statePos?.left ?? element.offsetLeft,
          top: statePos?.top ?? element.offsetTop,
          width: initialWidth,
          height: initialHeight,
          rotation: statePos?.rotation || 0
        };
      }
    });

    setDraggingLayer({
      id: layerId,
      startX: e.clientX,
      startY: e.clientY,
      hasMoved: false,
      initialPositions,
      altKey: e.altKey
    });
  };

  const handleResizeStart = (handle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedLayerIds.length !== 1) return;

    const layerId = selectedLayerIds[0];
    const element = document.getElementById(layerId);
    const layer = findLayerById(dynamicLayers, layerId);
    if (!element || !layer) return;

    const isManualSized = layer.type === 'rectangle' || layer.type === 'image' || layer.type === 'frame';
    const pos = layerPositions[layerId];

    // Authoritative dimensions
    let initialLeft = pos?.left ?? parseFloat(String(layer.style?.left || '0').replace('px', ''));
    let initialTop = pos?.top ?? parseFloat(String(layer.style?.top || '0').replace('px', ''));
    let initialWidth = pos?.width;
    let initialHeight = pos?.height;

    if (isManualSized) {
      if (initialWidth === undefined) initialWidth = parseFloat(String(layer.style?.width || '100').replace('px', ''));
      if (initialHeight === undefined) initialHeight = parseFloat(String(layer.style?.height || '100').replace('px', ''));
    }

    // Fallback to DOM only if state/style are missing
    if (initialWidth === undefined) initialWidth = element.offsetWidth;
    if (initialHeight === undefined) initialHeight = element.offsetHeight;

    // If it's an image with 'contain', we use the visible bounds for the initial resize values
    // This keeps handles aligned with the image content during the resize operation
    if (layer.type === 'image' && (imageSettings[layerId]?.objectFit || DEFAULT_IMAGE_SETTINGS.objectFit) === 'contain') {
      const img = element.querySelector('img');
      if (img && img.complete && img.naturalWidth && img.naturalHeight) {
        const visible = getImageVisibleBounds(initialWidth, initialHeight, img.naturalWidth, img.naturalHeight, 'contain');
        initialLeft += visible.left;
        initialTop += visible.top;
        initialWidth = visible.width;
        initialHeight = visible.height;
      }
    }

    // Capture initial font size for text layers
    let initialFontSize = undefined;
    if (layer.type === 'text') {
      initialFontSize = textStyles[layerId]?.fontSize || 16;
      // If still not found, try to parse from style
      if (!textStyles[layerId]?.fontSize && layer.style?.fontSize) {
        initialFontSize = parseFloat(String(layer.style.fontSize).replace('px', ''));
      }
    }

    setResizingLayer({
      id: layerId,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      initialLeft,
      initialTop,
      initialWidth,
      initialHeight,
      initialFontSize
    });
  };

  const handleRotateStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedLayerIds.length === 0 || !selectionBounds) return;

    const id = selectedLayerIds[0];
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Calculate center of the selection in screen pixels
    const centerX = rect.left + (selectionBounds.left + selectionBounds.width / 2) * canvasScale;
    const centerY = rect.top + (selectionBounds.top + selectionBounds.height / 2) * canvasScale;

    const pos = layerPositions[id];
    const initialRotation = pos?.rotation || 0;

    // Calculate initial angle of mouse relative to center
    const startDx = e.clientX - centerX;
    const startDy = e.clientY - centerY;
    const initialMouseAngle = Math.atan2(startDy, startDx) * (180 / Math.PI);

    setRotatingLayer({
      id,
      startX: e.clientX,
      startY: e.clientY,
      initialRotation,
      centerX,
      centerY,
      initialMouseAngle
    } as any); // Type cast temporarily as we're adding a property
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (resizingLayer) {
      const deltaX = (e.clientX - resizingLayer.startX) / canvasScale;
      const deltaY = (e.clientY - resizingLayer.startY) / canvasScale;

      const layer = findLayerById(dynamicLayers, resizingLayer.id);
      const isText = layer?.type === 'text';
      const isImage = layer?.type === 'image';
      const imgSettings = isImage ? imageSettings[resizingLayer.id] : null;

      const handle = resizingLayer.handle;
      const pos = layerPositions[resizingLayer.id];
      const rotation = pos?.rotation || 0;
      const rad = rotation * (Math.PI / 180);
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      // Transform mouse delta into local space coordinate system
      const localDeltaX = deltaX * cos + deltaY * sin;
      const localDeltaY = -deltaX * sin + deltaY * cos;

      let newWidth = resizingLayer.initialWidth;
      let newHeight = resizingLayer.initialHeight;

      // REFINED ASPECT RATIO LOCK LOGIC
      // Edge handles (top, bottom, left, right) should NEVER lock ratio unless Shift is held.
      const isSideHandle = handle === 'left' || handle === 'right' || handle === 'top' || handle === 'bottom';
      const isCornerHandle = !isSideHandle;

      const shouldPreserveRatio =
        (isText && isCornerHandle) ||
        (isImage && isCornerHandle && (imgSettings?.lockAspectRatio ?? true) && !e.altKey) ||
        (e.shiftKey && !e.altKey);

      if (shouldPreserveRatio) {
        const isEnforcedSquare = e.shiftKey && !isText && !(isImage && (imgSettings?.lockAspectRatio ?? true));
        const aspectRatio = isEnforcedSquare ? 1 : (resizingLayer.initialWidth / resizingLayer.initialHeight);

        if (handle === 'right' || handle === 'left') {
          newWidth = Math.max(10, handle === 'right' ? resizingLayer.initialWidth + localDeltaX : resizingLayer.initialWidth - localDeltaX);
          newHeight = newWidth / aspectRatio;
        } else if (handle === 'bottom' || handle === 'top') {
          newHeight = Math.max(10, handle === 'bottom' ? resizingLayer.initialHeight + localDeltaY : resizingLayer.initialHeight - localDeltaY);
          newWidth = newHeight * aspectRatio;
        } else {
          // Corner handles with ratio preservation
          const absDeltaX = Math.abs(localDeltaX);
          const absDeltaY = Math.abs(localDeltaY);
          if (absDeltaX > absDeltaY) {
            newWidth = Math.max(10, handle.includes('right') ? resizingLayer.initialWidth + localDeltaX : resizingLayer.initialWidth - localDeltaX);
            newHeight = newWidth / aspectRatio;
          } else {
            newHeight = Math.max(10, handle.includes('bottom') ? resizingLayer.initialHeight + localDeltaY : resizingLayer.initialHeight - localDeltaY);
            newWidth = newHeight * aspectRatio;
          }
        }
      } else {
        // Independent resizing
        if (handle.includes('right')) newWidth = Math.max(1, resizingLayer.initialWidth + localDeltaX);
        if (handle.includes('left')) newWidth = Math.max(1, resizingLayer.initialWidth - localDeltaX);
        if (handle.includes('bottom')) newHeight = Math.max(1, resizingLayer.initialHeight + localDeltaY);
        if (handle.includes('top')) newHeight = Math.max(1, resizingLayer.initialHeight - localDeltaY);
      }

      // Determine the anchor point (the point that stays fixed) based on the handle being dragged
      // Normalized coordinates: 0 = left/top, 0.5 = center, 1 = right/bottom
      let anchorX = 0.5;
      let anchorY = 0.5;

      if (handle.includes('left')) anchorX = 1;
      else if (handle.includes('right')) anchorX = 0;

      if (handle.includes('top')) anchorY = 1;
      else if (handle.includes('bottom')) anchorY = 0;

      // Calculate new position using the anchored helper
      const { left: newLeft, top: newTop } = calculateAnchoredPosition(
        {
          left: resizingLayer.initialLeft,
          top: resizingLayer.initialTop,
          width: resizingLayer.initialWidth,
          height: resizingLayer.initialHeight,
          rotation
        },
        newWidth - resizingLayer.initialWidth,
        newHeight - resizingLayer.initialHeight,
        anchorX,
        anchorY
      );

      // Update local state for real-time visual feedback
      setLayerPositions(prev => ({
        ...prev,
        [resizingLayer.id]: {
          ...prev[resizingLayer.id],
          left: newLeft,
          top: newTop,
          width: newWidth,
          height: newHeight
        }
      }));

      // Special case: update font size for text layers
      if (isText && resizingLayer.initialFontSize && !isSideHandle) {
        const scale = newHeight / resizingLayer.initialHeight;
        const newFontSize = Math.max(1, Math.round(resizingLayer.initialFontSize * scale));
        setTextStyles(prev => ({
          ...prev,
          [resizingLayer.id]: { ...prev[resizingLayer.id], fontSize: newFontSize }
        }));
      }

      // Special case: sync scroller settings if it's a native scroller
      const isNativeScroller = layer && !layer.isApp && (layer.name?.toLowerCase().includes('scroller') || layer.id?.toLowerCase().includes('scroller'));
      if (isNativeScroller) {
        handleScrollerSettingsChange(resizingLayer.id, { width: newWidth, height: newHeight });
      }

      return;
    }

    if (rotatingLayer) {
      // Calculate angle from center to current mouse position
      const dx = e.clientX - rotatingLayer.centerX;
      const dy = e.clientY - rotatingLayer.centerY;

      const currentMouseAngle = Math.atan2(dy, dx) * (180 / Math.PI);
      const deltaAngle = currentMouseAngle - rotatingLayer.initialMouseAngle;

      let newRotation = rotatingLayer.initialRotation + deltaAngle;

      // Snap to 15 degree increments if shift is held
      if (e.shiftKey) {
        newRotation = Math.round(newRotation / 15) * 15;
      }

      setLayerPositions(prev => ({
        ...prev,
        [rotatingLayer.id]: {
          ...prev[rotatingLayer.id],
          rotation: newRotation
        }
      }));
      return;
    }

    if (!draggingLayer || !canvasRef.current) return;

    const deltaX = (e.clientX - draggingLayer.startX) / canvasScale;
    const deltaY = (e.clientY - draggingLayer.startY) / canvasScale;

    // Check drag threshold
    const screenDeltaX = Math.abs(e.clientX - draggingLayer.startX);
    const screenDeltaY = Math.abs(e.clientY - draggingLayer.startY);

    if (!draggingLayer.hasMoved && (screenDeltaX < DRAG_THRESHOLD && screenDeltaY < DRAG_THRESHOLD)) {
      return;
    }

    if (!draggingLayer.hasMoved) {
      if (draggingLayer.altKey) {
        // DUPLICATION LOGIC: Create copies of the currently dragging elements
        const idsToDuplicate = Object.keys(draggingLayer.initialPositions);
        const newIdMap: Record<string, string> = {};
        const newInitialPositions: Record<string, { left: number; top: number; width?: number; height?: number }> = {};
        const newSelectedIds: string[] = [];

        // Prepare settings updates
        const nextTextContents = { ...textContents };
        const nextTextStyles = { ...textStyles };
        const nextShapeSettings = { ...shapeSettings };
        const nextImageSettings = { ...imageSettings };
        const nextBackgroundSettings = { ...backgroundSettings };
        const nextScrollerSettings = { ...scrollerSettings };
        const nextWidgetSettings = { ...widgetSettings };

        const duplicateRecursive = (layer: Layer): Layer => {
          const newId = `${layer.id.split('-copy-')[0]}-copy-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          newIdMap[layer.id] = newId;
          newSelectedIds.push(newId);

          // Copy initial positions (same as originals at start of drag)
          if (draggingLayer.initialPositions[layer.id]) {
            newInitialPositions[newId] = { ...draggingLayer.initialPositions[layer.id] };
          }

          // Copy settings for the specific layer
          if (textContents[layer.id]) nextTextContents[newId] = textContents[layer.id];
          if (textStyles[layer.id]) nextTextStyles[newId] = { ...textStyles[layer.id] };
          if (shapeSettings[layer.id]) nextShapeSettings[newId] = { ...shapeSettings[layer.id] };
          if (imageSettings[layer.id]) nextImageSettings[newId] = { ...imageSettings[layer.id] };
          if (backgroundSettings[layer.id]) nextBackgroundSettings[newId] = { ...backgroundSettings[layer.id] };
          if (scrollerSettings[layer.id]) nextScrollerSettings[newId] = { ...scrollerSettings[layer.id] };
          if (widgetSettings[layer.id]) nextWidgetSettings[newId] = { ...widgetSettings[layer.id] };

          return {
            ...layer,
            id: newId,
            name: `${layer.name.replace(/ \(Copy\)$/, '')} (Copy)`,
            children: layer.children ? layer.children.map(duplicateRecursive) : undefined
          };
        };

        // Update dynamicLayers with the new copies inserted next to originals
        setDynamicLayers(prev => {
          const processLayers = (layers: Layer[]): Layer[] => {
            const next: Layer[] = [];
            for (const layer of layers) {
              next.push(layer);
              // If this layer is one of the ones we're duplicating AND it's not a child of another duplicated layer
              // (to avoid double duplication, though in Figma selection this is handled by only selecting top-level)
              if (idsToDuplicate.includes(layer.id)) {
                next.push(duplicateRecursive(layer));
              } else if (layer.children) {
                // If not duplicating this layer, recurse into children
                // Note: we don't recurse if we duplicated this layer because duplicateRecursive handles its own children
                layer.children = processLayers(layer.children);
              }
            }
            return next;
          };
          return processLayers([...prev]);
        });

        // Update all other states for the new copies
        setTextContents(nextTextContents);
        setTextStyles(nextTextStyles);
        setShapeSettings(nextShapeSettings);
        setImageSettings(nextImageSettings);
        setBackgroundSettings(nextBackgroundSettings);
        setScrollerSettings(nextScrollerSettings);
        setWidgetSettings(nextWidgetSettings);
        setSelectedLayerIds(newSelectedIds);

        // Transition the drag state to use the NEW IDs immediately
        const newPrimaryId = newIdMap[draggingLayer.id] || draggingLayer.id;

        commitToHistory('DUPLICATE_ELEMENT', `Duplicated ${idsToDuplicate.length} elements via Alt + drag`);

        setDraggingLayer(prev => prev ? {
          ...prev,
          id: newPrimaryId,
          hasMoved: true,
          initialPositions: newInitialPositions,
          altKey: false // Prevent further duplications in this drag sequence
        } : null);

        return;
      } else {
        setDraggingLayer(prev => prev ? { ...prev, hasMoved: true } : null);
      }
    }

    const nextPositions = { ...layerPositions };
    const primaryInitial = draggingLayer.initialPositions[draggingLayer.id];
    if (!primaryInitial) return;

    const targetLeft = primaryInitial.left + deltaX;
    const targetTop = primaryInitial.top + deltaY;

    // Get primary layer dimensions for snapping
    const element = document.getElementById(draggingLayer.id);
    if (!element) return;

    // Use captured width/height from initialPositions for snapping logic
    const layerWidth = primaryInitial.width || element.offsetWidth;
    const layerHeight = primaryInitial.height || element.offsetHeight;

    const snapResult = calculateSnap(
      targetLeft,
      targetTop,
      layerWidth,
      layerHeight,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
      SNAP_THRESHOLD
    );

    setSnapGuides(snapResult.guides);

    const finalDeltaX = snapResult.x - primaryInitial.left;
    const finalDeltaY = snapResult.y - primaryInitial.top;

    Object.keys(draggingLayer.initialPositions).forEach(id => {
      const initial = draggingLayer.initialPositions[id];
      nextPositions[id] = {
        ...initial, // Preserves captured width and height
        left: initial.left + finalDeltaX,
        top: initial.top + finalDeltaY
      };
    });

    setLayerPositions(nextPositions);
  }, [
    draggingLayer, resizingLayer, rotatingLayer, canvasScale, layerPositions, dynamicLayers,
    textContents, textStyles, shapeSettings, imageSettings, backgroundSettings, scrollerSettings, widgetSettings,
    setTextStyles, setTextContents, setShapeSettings, setImageSettings, setBackgroundSettings, setScrollerSettings, setWidgetSettings, setSelectedLayerIds, commitToHistory
  ]);


  const handleMouseUp = useCallback(() => {
    if (resizingLayer) {
      const pos = layerPositions[resizingLayer.id];
      if (pos) {
        setDynamicLayers(prev => {
          const updateRecursive = (layers: Layer[]): Layer[] => {
            return layers.map(l => {
              if (l.id === resizingLayer.id) {
                // Get updated font size from textStyles if it's a text layer
                const textStyle = textStyles[resizingLayer.id];
                const fontSizeStyle = (l.type === 'text' && textStyle?.fontSize)
                  ? { fontSize: `${textStyle.fontSize}px` }
                  : {};

                // For text elements, we only want to save width/height if they were explicitly changed
                // Side handles always trigger a fixed width/height save
                const isSideHandle = resizingLayer.handle === 'left' || resizingLayer.handle === 'right' || resizingLayer.handle === 'top' || resizingLayer.handle === 'bottom';
                const isText = l.type === 'text';
                const shouldSaveSize = !isText || isSideHandle || (l.style?.width !== undefined);

                return {
                  ...l,
                  style: {
                    ...l.style,
                    left: `${pos.left}px`,
                    top: `${pos.top}px`,
                    ...(shouldSaveSize ? { width: `${pos.width}px` } : {}),
                    ...(shouldSaveSize ? { height: `${pos.height}px` } : {}),
                    ...fontSizeStyle
                  }
                };
              }
              if (l.children) return { ...l, children: updateRecursive(l.children) };
              return l;
            });
          };
          return updateRecursive(prev);
        });
      }

      commitToHistory('LAYER_EDIT', `Resized element ${resizingLayer.id}`);
      setResizingLayer(null);

      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 50);
      return;
    }

    if (rotatingLayer) {
      const pos = layerPositions[rotatingLayer.id];
      if (pos) {
        setDynamicLayers(prev => {
          const updateRecursive = (layers: Layer[]): Layer[] => {
            return layers.map(l => {
              if (l.id === rotatingLayer.id) {
                return {
                  ...l,
                  style: {
                    ...l.style,
                    transform: pos.rotation ? `rotate(${pos.rotation}deg)` : l.style?.transform,
                    transformOrigin: 'center center'
                  }
                };
              }
              if (l.children) return { ...l, children: updateRecursive(l.children) };
              return l;
            });
          };
          return updateRecursive(prev);
        });
      }

      commitToHistory('LAYER_EDIT', `Rotated element ${rotatingLayer.id}`);
      setRotatingLayer(null);

      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 50);
      return;
    }

    if (draggingLayer && draggingLayer.hasMoved) {
      const draggedIds = Object.keys(draggingLayer.initialPositions);

      // Measure elements to ensure we have the most accurate dimensions for synchronization
      const recalculatedPositions: Record<string, { left: number; top: number; width?: number; height?: number, rotation?: number }> = {};
      draggedIds.forEach(id => {
        const element = document.getElementById(id);
        const layer = findLayerById(dynamicLayers, id);
        if (element && canvasRef.current && layer) {
          const statePos = layerPositions[id];
          const isText = layer.type === 'text';

          // CRITICAL: For rotated elements, getBoundingClientRect returns the AABB (axis-aligned bounding box),
          // which is larger than the element's actual dimensions. Using it here would cause the selection box
          // to "grow" and the element to shift. 
          // We rely on the linear deltas calculated during handleMouseMove for rotated elements.
          const currentRotation = statePos?.rotation || 0;
          if (currentRotation !== 0) {
            recalculatedPositions[id] = {
              ...statePos,
              left: statePos?.left ?? 0,
              top: statePos?.top ?? 0,
              width: statePos?.width,
              height: statePos?.height
            };
            return;
          }

          const baseRect = element.getBoundingClientRect(); // Still need the container for position
          const canvasRect = canvasRef.current.getBoundingClientRect();
          const wasWidthResized = draggingLayer.initialPositions[id]?.width !== undefined;
          const wasHeightResized = draggingLayer.initialPositions[id]?.height !== undefined;

          recalculatedPositions[id] = {
            ...statePos,
            left: (baseRect.left - canvasRect.left) / canvasScale,
            top: (baseRect.top - canvasRect.top) / canvasScale,
            // Only store width/height in transient state if it was already explicitly sized/resized
            // For text elements, we use the container measurement if resized, or tight measurement if hugging
            // We respect width and height independence for text elements
            width: (!isText || wasWidthResized) ? (baseRect.width / canvasScale) : undefined,
            height: (!isText || wasHeightResized) ? (baseRect.height / canvasScale) : undefined
          };
        }
      });

      // Update transient state with precise measurements
      setLayerPositions(prev => ({ ...prev, ...recalculatedPositions }));

      const updateRecursive = (layers: Layer[]): Layer[] => {
        return layers.map(layer => {
          let updatedLayer = layer;
          const pos = recalculatedPositions[layer.id] || layerPositions[layer.id];
          if (draggedIds.includes(layer.id) && pos) {
            const isText = layer.type === 'text';
            const wasWidthResized = draggingLayer.initialPositions[layer.id]?.width !== undefined;
            const wasHeightResized = draggingLayer.initialPositions[layer.id]?.height !== undefined;

            updatedLayer = {
              ...layer,
              style: {
                ...layer.style,
                left: `${pos.left}px`,
                top: `${pos.top}px`,
                // Only save width/height to persistent style if it's NOT a hugging text element
                // We handle width and height independence
                ...(pos.width !== undefined && (!isText || wasWidthResized) ? { width: `${pos.width}px` } : {}),
                ...(pos.height !== undefined && (!isText || wasHeightResized) ? { height: `${pos.height}px` } : {}),
              }
            };
          }
          if (updatedLayer.children) {
            return {
              ...updatedLayer,
              children: updateRecursive(updatedLayer.children)
            };
          }
          return updatedLayer;
        });
      };

      setDynamicLayers(prev => updateRecursive(prev));

      commitToHistory('LAYER_MOVE', `Moved ${draggedIds.length} layer(s)`);
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 50);
    }

    setDraggingLayer(null);
    setSnapGuides([]);
  }, [draggingLayer, resizingLayer, rotatingLayer, layerPositions, commitToHistory, textStyles]);

  // Refs to store the latest callbacks and interaction state to prevent stale closures and window listener churn
  const mouseMoveRef = useRef(handleMouseMove);
  const mouseUpRef = useRef(handleMouseUp);
  const isInteractingRef = useRef(false);

  useEffect(() => {
    mouseMoveRef.current = handleMouseMove;
  }, [handleMouseMove]);

  useEffect(() => {
    mouseUpRef.current = handleMouseUp;
  }, [handleMouseUp]);

  useEffect(() => {
    isInteractingRef.current = !!(draggingLayer || resizingLayer || rotatingLayer);
  }, [draggingLayer, resizingLayer, rotatingLayer]);

  // Register window-level mouse events permanently on mount
  useEffect(() => {
    const handleWindowMouseMove = (e: MouseEvent) => {
      if (isInteractingRef.current) {
        mouseMoveRef.current(e as unknown as React.MouseEvent);
      }
    };

    const handleWindowMouseUp = () => {
      if (isInteractingRef.current) {
        mouseUpRef.current();
      }
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, []);

  // Handle canvas click to exit text editing mode and deselect
  const handleCanvasClick = (e: React.MouseEvent) => {
    // If click reached here, it wasn't handled by any element
    // So select the root/template

    // Safety check: if we just triggered edit mode via double click, 
    // the subsequent click event might bubble here. 
    // We should only clear editing mode if the click target is actually the canvas.
    const isCanvasClick = e.target === e.currentTarget;
    if (editingTextId && isCanvasClick) {
      setEditingTextId(null);
    }

    // Only select root if we aren't already handling a drag/drop
    if (!draggingLayer && isCanvasClick) {
      setSelectedLayerId(null);
    }
  };

  // Element panel handlers
  const handleAddElement = (type: string, data: any, dropX?: number, dropY?: number) => {
    const id = `${type}-${Date.now()}`;

    let baseConfig: Partial<Layer> = {
      id,
      className: 'absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500',
    };

    if (type === 'shape') {
      baseConfig = {
        ...baseConfig,
        name: `${data.type} Shape`,
        type: 'rectangle',
        content: data.type,
        style: { width: '150px', height: '150px' }
      };
    } else if (type === 'icon') {
      baseConfig = {
        ...baseConfig,
        name: `${data.type} Icon`,
        type: 'rectangle',
        content: data.type,
        style: { width: '150px', height: '150px' }
      };
    } else if (type === 'line') {
      baseConfig = {
        ...baseConfig,
        name: `${data.type} Line`,
        type: 'rectangle',
        content: data.type,
        style: { width: '150px', height: '2px' }
      };
    } else if (type === 'arrow') {
      baseConfig = {
        ...baseConfig,
        name: `${data.type} Arrow`,
        type: 'rectangle',
        content: data.type,
        style: { width: '150px', height: '40px' }
      };
    }

    const result = placeElementOnCanvas(baseConfig, dropX, dropY);
    const { layer: newLayer, position } = result;

    // Use a fresh object for position to ensure reactivity
    const newPosition = {
      left: position.left,
      top: position.top,
      width: position.width || 150,
      height: position.height || 150
    };

    setLayerPositions(prev => ({ ...prev, [id]: newPosition }));

    // Nest the new layer within the 'root' frame
    setDynamicLayers(prev => {
      const hasRoot = prev.some(l => l.id === 'root');
      if (hasRoot) {
        return prev.map(layer => {
          if (layer.id === 'root') {
            return {
              ...layer,
              children: [...(layer.children || []), newLayer]
            };
          }
          return layer;
        });
      }
      return [...prev, newLayer];
    });

    // Initialize shape settings if applicable
    if (type === 'shape' || type === 'icon' || type === 'line' || type === 'arrow') {
      setShapeSettings(prev => ({
        ...prev,
        [id]: {
          ...DEFAULT_SHAPE_SETTINGS,
          fill: { type: 'solid', enabled: true, color: '#1e293b' } as FillSettings
        }
      }));
    }

    setSelectedLayerId(id);
    setSelectedLayerIds([id]);
    commitToHistory('LAYER_ADD', `Added ${newLayer.name}`);
  };

  const handleAddBoundLayer = (apiId: string, apiKey: string, value: string, dropX?: number, dropY?: number, customWidth?: number, customHeight?: number, ignoreBounds: boolean = false, isBulkDrop: boolean = false) => {

    const isImage = value && typeof value === 'string' && value.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(value.split('?')[0]);
    const type = isImage ? 'image' : 'text';
    const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Format clean label from key path
    const cleanLabel = apiKey.split('.').pop() || 'Data';
    const layerName = `API ${cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1)}`;

    const width = customWidth || (isImage ? 200 : 300);
    const height = customHeight || (isImage ? 200 : 50);

    const baseConfig: Partial<Layer> = {
      id,
      name: layerName,
      type,
      content: isImage ? '' : value,
      className: 'absolute cursor-move hover:outline hover:outline-2 hover:outline-teal-500',
      style: {
        width: `${width}px`,
        height: `${height}px`
      }
    };

    const result = placeElementOnCanvas(baseConfig, dropX, dropY, ignoreBounds);
    const { layer: newLayer, position } = result;

    const newPosition = {
      left: position.left,
      top: position.top,
      width: position.width || width,
      height: position.height || height
    };



    setLayerPositions(prev => {
      const updated = { ...prev, [id]: newPosition };

      return updated;
    });

    setDynamicLayers(prev => {
      const hasRoot = prev.some(l => l.id === 'root');
      if (hasRoot) {
        return prev.map(layer => {
          if (layer.id === 'root') {
            return {
              ...layer,
              children: [...(layer.children || []), newLayer]
            };
          }
          return layer;
        });
      }
      return [...prev, newLayer];
    });

    const bindingVal = {
      customApiId: apiId,
      apiKey: apiKey,
      fallbackValue: value,
    };

    if (isImage) {
      setImageSettings(prev => ({
        ...prev,
        [id]: {
          ...DEFAULT_IMAGE_SETTINGS,
          url: value,
          binding: bindingVal,
        }
      }));
    } else {
      setTextStyles(prev => {
        const updated = {
          ...prev,
          [id]: {
            fontSize: 24,
            fontWeight: 'semibold' as const,
            color: '#1d2739',
            binding: bindingVal,
          }
        };

        return updated;
      });
      setTextContents(prev => {
        const updated = {
          ...prev,
          [id]: value
        };

        return updated;
      });
    }

    if (!isBulkDrop) {
      setSelectedLayerId(id);
      setSelectedLayerIds([id]);
      commitToHistory('LAYER_ADD', `Added dynamic layer ${layerName}`);
      toast.success(`Added bound layer: "${layerName}"`);
    }
  };

  const handleTemplateSelect = (template: any) => {
    const rawData = normalizeTemplateData(template);

    if (rawData) {
      // Source 1: Full structured template data from API or sidebar
      const layers = rawData.dynamicLayers;

      // Load current workspace from template data
      setDynamicLayers(layers);
      setLayerPositions(rawData.layerPositions);
      setTextContents(rawData.textContents);
      setTextStyles(rawData.textStyles);
      setBackgroundSettings(rawData.backgroundSettings);
      setImageSettings(rawData.imageSettings);
      setShapeSettings(rawData.shapeSettings);
      setScrollerSettings(rawData.scrollerSettings);
      setWidgetSettings(rawData.widgetSettings);
      setTemplateSettings(rawData.templateSettings);

      if (template.name) setPageName(template.name);

      // Sync Redux state so the editor knows which library template is being used
      dispatch(setSelectedTemplate({
        id: template.id,
        name: template.name,
        previewUrl: template.previewUrl || '',
        isCustom: false, // Flag as a library template
        ...rawData
      }));

      // Clear selection
      setSelectedLayerIds([]);
      commitToHistory('TEMPLATE_APPLY', `Applied full template: ${template.name}`);
    } else {
      // Source 2: Legacy/Image-only template fallback
      // Maintain the root structure instead of replacing it
      setDynamicLayers(prev => {
        const hasRoot = prev.some(l => l.id === 'root');

        const newBgLayer: Layer = {
          id: `bg-${Date.now()}`,
          name: 'Background',
          type: 'image',
          className: 'absolute inset-0 w-full h-full object-cover z-0 pointer-events-none',
          style: {
            backgroundImage: `url("${template.previewUrl}")`,
            width: '100%',
            height: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          },
          locked: true
        };

        if (hasRoot) {
          return prev.map(layer => {
            if (layer.id === 'root') {
              return {
                ...layer,
                children: [newBgLayer] // Replace existing children with background
              };
            }
            return layer;
          });
        } else {
          // Fallback: create root if somehow missing
          return [{
            id: 'root',
            name: 'Canvas',
            type: 'frame',
            className: 'w-[1920px] h-[1080px] bg-white relative',
            style: { width: '1920px', height: '1080px' },
            children: [newBgLayer]
          }];
        }
      });

      // Reset other states
      setLayerPositions({});
      setTextContents({});
      setTextStyles({});
      setBackgroundSettings({});
      setImageSettings({});
      setShapeSettings({});
      setWidgetSettings({});

      commitToHistory('TEMPLATE_APPLY', `Applied background template: ${template.name}`);
    }

    // Close panel
    setActiveLeftPanel(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();

    // Check if the drop is within the canvas
    if (e.clientX < canvasRect.left || e.clientX > canvasRect.right ||
      e.clientY < canvasRect.top || e.clientY > canvasRect.bottom) {
      return;
    }

    const x = (e.clientX - canvasRect.left) / canvasScale;
    const y = (e.clientY - canvasRect.top) / canvasScale;

    // Try to extract data safely
    let elementType = '';
    let elementData: any = null;

    try {
      // 1. Try standard text/plain which we set as JSON in ElementsPanel.tsx
      const plainData = e.dataTransfer.getData('text/plain');
      if (plainData) {
        const parsed = JSON.parse(plainData);
        if (parsed && typeof parsed === 'object') {
          elementType = parsed.type || '';
          elementData = parsed.data || null;
        }
      }

      // 2. Fallback to custom types if not found
      if (!elementType) {
        elementType = e.dataTransfer.getData('element-type');
      }

      if (elementType && !elementData) {
        const customDataStr = e.dataTransfer.getData(`${elementType}-data`);
        if (customDataStr) {
          elementData = JSON.parse(customDataStr);
        }
      }
    } catch (err) {
      console.error('Error parsing drop data:', err);
    }

    // Check for template drop
    const templateDataStr = e.dataTransfer.getData('template-data');
    if (templateDataStr) {
      try {
        const template = JSON.parse(templateDataStr);
        handleTemplateSelect(template);
        return;
      } catch (e) { /* ignore */ }
    }

    // Check for uploaded file drop
    const uploadedFileStr = e.dataTransfer.getData('uploaded-file');
    if (uploadedFileStr) {
      try {
        const file = JSON.parse(uploadedFileStr);
        handleAddUploadToCanvas(file, x, y);
        return;
      } catch (e) { /* ignore */ }
    }

    if (!elementType) return;

    // Handle Data Binding Drop
    if (elementType === 'data-binding' && elementData) {
      const { customApiId, apiKey, value } = elementData;

      // Bulk Drop: If apiKey is empty or points to an object/array, we perform bulk placement
      const targetApi = customApis.find(api => String(api._id) === String(customApiId));
      if (targetApi && targetApi.customapidata) {
        const mergedData = targetApi.customapidata;
        const resolvedVal = apiKey === '' ? mergedData : resolveJsonPath(mergedData, apiKey);
        if (resolvedVal && typeof resolvedVal === 'object') {
          
          const isArrayDrop = Array.isArray(resolvedVal);
          const autoPaginate = localStorage.getItem('wejha_auto_array_pagination') !== 'false';
          let targetData = resolvedVal;
          
          if (isArrayDrop && resolvedVal.length > 0 && autoPaginate) {
            targetData = [resolvedVal[0]]; // Only process the first item for the current page
          }

          const allSubKeys = getFlattenedKeys(targetData);
          const technicalKeys = [
            'headers', 'config', 'request', 'response', 'status', 'statuscode',
            'success', 'message', 'error', 'errors', 'ok', 'method', 'url', 'origin',
            'headers_list', 'header_values', 'request_body', 'api_url', 'token',
            'total', 'skip', 'limit', 'count', 'size', 'page', 'pages', 'totalpages', 'perpage',
            'data_json'
          ];
          const validSubKeys = allSubKeys.filter(subKey => {
            const val = resolveJsonPath(resolvedVal, subKey);
            if (val === null || val === undefined || String(val) === 'null' || String(val) === 'undefined') return false;
            const parts = subKey.toLowerCase().split('.');
            return !parts.some((part, index) => {
              if (index > 0) return false;
              return technicalKeys.includes(part.replace(/\[\d+\]/g, ''));
            });
          });
          
          // Prepare the items to calculate column layout
          const items = validSubKeys.map((subKey) => {
            const fullKey = apiKey === '' ? subKey : `${apiKey}.${subKey}`;
            const val = resolveJsonPath(resolvedVal, subKey);
            const valStr = val !== undefined && val !== null ? String(val) : '';
            
            const isImage = valStr.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(valStr.split('?')[0]);
            
            let estimatedHeight = 50;
            let estimatedWidth = 300;
            
            if (isImage) {
              estimatedHeight = 200;
              estimatedWidth = 200;
            } else {
              // Dynamically calculate estimated height for text to prevent overlaps
              const charsPerLine = 25; // roughly 300px width / 12px char width
              const linesFromNewlines = valStr.split('\n').reduce((acc, line) => {
                return acc + Math.max(1, Math.ceil(line.length / charsPerLine));
              }, 0);
              const lines = Math.max(1, linesFromNewlines);
              estimatedHeight = Math.max(50, lines * 36 + 20); // 36px line height + 20px padding
            }
            return { fullKey, valStr, estimatedWidth, estimatedHeight };
          });

          // Layout into columns
          const COLUMN_WIDTH = 320;
          let layoutY = 50;
          let columns = 1;
          for (const item of items) {
             if (layoutY + item.estimatedHeight > CANVAS_HEIGHT - 50 && layoutY > 50) {
                 columns++;
                 layoutY = 50;
             }
             layoutY += item.estimatedHeight + 15;
          }

          // Total width of all columns
          const totalWidth = columns * 300 + (columns - 1) * 20;
          // Center the entire block
          let startX = (CANVAS_WIDTH - totalWidth) / 2;
          if (startX < 20) startX = 20; // At least some padding

          let currentOffsetX = startX;
          let currentOffsetY = 50;

          items.forEach((item) => {
            // Check if we need to wrap to the next column
            if (currentOffsetY + item.estimatedHeight > CANVAS_HEIGHT - 50 && currentOffsetY > 50) {
              currentOffsetY = 50;
              currentOffsetX += COLUMN_WIDTH;
            }
            
            handleAddBoundLayer(customApiId, item.fullKey, item.valStr, currentOffsetX, currentOffsetY, item.estimatedWidth, item.estimatedHeight, true, true);
            
            // Increment Y for the next item
            currentOffsetY += item.estimatedHeight + 15; // 15px gap between items
          });

          toast.success(`Created ${validSubKeys.length} bound layers from JSON`);
          commitToHistory('LAYER_ADD', `Bulk added ${validSubKeys.length} bound layers from JSON`);
          
          // Auto pagination disabled in editor per user request
          /*
          if (isArrayDrop && resolvedVal.length > 1 && autoPaginate) {
            const pagesToCreate = Math.min(resolvedVal.length, 20) - 1;
            if (pagesToCreate > 0) {
              setPendingArrayPagination({ count: pagesToCreate, prefix: apiKey ? `${apiKey}.0` : '0', customApiId });
            }
          }
          */
          
          return;
        }
      }

      const hitLayer = findLayerAtPosition(dynamicLayers, x, y, layerPositions, canvasRect || undefined, canvasScale);

      // ONLY bind directly if hitLayer exists, is NOT the root canvas, and is a bindable element type (text, image, scroller)
      const isBindableHit = hitLayer && hitLayer.id !== 'root' && ['text', 'image', 'scroller'].includes(hitLayer.type);

      if (isBindableHit && hitLayer) {
        handleBindingChange(hitLayer.id, {
          customApiId,
          apiKey,
          fallbackValue: value || '',
        });
        commitToHistory('STYLE_CHANGE', `Bound variable ${apiKey} to ${hitLayer.name}`);
        toast.success(`Bound ${apiKey.split('.').pop()} to layer "${hitLayer.name}"`);
      } else {
        // Drop on empty canvas space -> Quick Add a new bound text/image layer
        handleAddBoundLayer(customApiId, apiKey, value || '', x, y);
      }
      return;
    }

    // Handle Brand Color Drop
    if (elementType === 'brand-color') {
      const color = e.dataTransfer.getData('color-value');
      const hitLayer = findLayerAtPosition(dynamicLayers, x, y, layerPositions, canvasRect || undefined, canvasScale);

      // Don't apply to root canvas in this branch; treat root as empty space to change the canvas background
      const targetLayer = hitLayer && hitLayer.id !== 'root' ? hitLayer : null;

      if (targetLayer) {
        if (targetLayer.type === 'text' || textStyles[targetLayer.id]) {
          setTextStyles(prev => ({
            ...prev,
            [targetLayer.id]: { ...prev[targetLayer.id], color }
          }));
          commitToHistory('STYLE_CHANGE', 'Applied brand color to text');
        } else if (['rectangle', 'circle', 'triangle', 'rhombus', 'pentagon', 'hexagon', 'octagon', 'star', 'heart', 'shape'].includes(targetLayer.type)) {
          setShapeSettings(prev => ({
            ...prev,
            [targetLayer.id]: { ...prev[targetLayer.id], fill: { type: 'solid', enabled: true, color } }
          }));
          commitToHistory('STYLE_CHANGE', 'Applied brand color to shape');
        }
      } else {
        setBackgroundSettings(prev => ({
          ...prev,
          'root': { ...prev['root'], type: 'solid', solidColor: color }
        }));
        commitToHistory('STYLE_CHANGE', 'Applied brand color to background');
      }
      return;
    }

    // Handle Brand Logo Drop
    if (elementType === 'brand-logo') {
      const url = e.dataTransfer.getData('logo-url');
      if (url) {
        handleAddBrandLogo(url, x, y);
      }
      return;
    }

    // Handle App/Playlist Drops
    if (elementType === 'app' || elementType === 'playlist') {
      // These have specific config generators
      const id = `${elementType}-${Date.now()}`;
      let baseConfig: any;
      let w = 150, h = 150;

      if (elementType === 'app') {
        const appName = e.dataTransfer.getData('app-name');
        baseConfig = getAppConfig(appName, id);
      } else {
        const playlistDataStr = e.dataTransfer.getData('playlist-data');
        let playlistData: any = { name: "Unknown Playlist" };
        try {
          playlistData = JSON.parse(playlistDataStr);
        } catch (err) { console.error("Could not parse playlist data"); }
        baseConfig = getPlaylistConfig(playlistData, id);
      }

      if (baseConfig) {
        w = parseFloat(String(baseConfig.style?.width || 150).replace('px', ''));
        h = parseFloat(String(baseConfig.style?.height || 100).replace('px', ''));
        const result = placeElementOnCanvas(baseConfig, x - w / 2, y - h / 2);
        if (result) {
          const { layer: newLayer, position } = result;
          setLayerPositions(prev => ({ ...prev, [id]: position }));
          setDynamicLayers(prev => {
            const hasRoot = prev.some(l => l.id === 'root');
            if (hasRoot) {
              return prev.map(l => l.id === 'root' ? { ...l, children: [...(l.children || []), newLayer] } : l);
            }
            return [...prev, newLayer];
          });
          setSelectedLayerId(id);
          commitToHistory('LAYER_ADD', `Added ${newLayer.name}`);
        }
      }
      return;
    }

    // Handle Text Preset Drop
    if (elementType === 'text-preset') {
      const textType = e.dataTransfer.getData('text-type') as 'heading' | 'subheading' | 'body';
      handleAddText(textType || 'body', x - 100, y - 20);
      return;
    }

    if (['shape', 'icon', 'line', 'arrow'].includes(elementType) && elementData) {
      // Calculate centering offsets based on expected size
      let w = 150, h = 150;
      if (elementType === 'line') { w = 150; h = 2; }
      if (elementType === 'arrow') { w = 150; h = 40; }

      handleAddElement(elementType, elementData, x - w / 2, y - h / 2);
    }
  };

  /*
  const handleSaveAllPages = async () => {
    if (savingStatus === 'saving') return;
   
    const originalPageIndex = currentPageIndex;
    const originalSelectedIds = [...selectedLayerIds];
   
    setSavingStatus('saving');
   
    try {
      // 1. Clear selection Globally
      setSelectedLayerIds([]);
      setSnapGuides([]);
   
      // 2. BAKE current state into the pages array ONCE before starting
      setPages(prev => {
        const updated = [...prev];
        if (updated[currentPageIndex]) {
          updated[currentPageIndex] = {
            ...updated[currentPageIndex],
            dynamicLayers: [...dynamicLayers],
            layerPositions: { ...layerPositions },
            textContents: { ...textContents },
            textStyles: { ...textStyles },
            backgroundSettings: { ...backgroundSettings },
            imageSettings: { ...imageSettings },
            shapeSettings: { ...shapeSettings },
            scrollerSettings: { ...scrollerSettings },
           widgetSettings: { ...widgetSettings },
           templateSettings: { ...templateSettings },
         };
        }
        return updated;
      });
   
      await new Promise(resolve => setTimeout(resolve, 50));
   
      for (let i = 0; i < pages.length; i++) {

   
        // 3. Switch to the page directly
        setCurrentPageIndex(i);
   
        // 4. Wait for UI to settle - increased timeout for safety
        await new Promise(resolve => setTimeout(resolve, 500));
   
        let thumbnailBlob = null;
        if (canvasRef.current) {
          thumbnailBlob = await toBlob(canvasRef.current, {
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            style: {
              transform: 'scale(1)',
              transformOrigin: 'top left',
            },
            cacheBust: false,
          });
        }
   
        // 3. Prepare data ONLY for this specific page
        const currentPageData = pages[i];
   
        const canvasData: Partial<SavedState> = {
          version: '1.0',
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          dynamicLayers: currentPageData.dynamicLayers,
          layerPositions: currentPageData.layerPositions,
          textContents: currentPageData.textContents,
          textStyles: currentPageData.textStyles,
          imageSettings: currentPageData.imageSettings,
          shapeSettings: currentPageData.shapeSettings,
          backgroundSettings: currentPageData.backgroundSettings,
          templateSettings: currentPageData.templateSettings,
          scrollerSettings: currentPageData.scrollerSettings,
          widgetSettings: currentPageData.widgetSettings,
          pageName: currentPageData.name || `Page ${i + 1}`,
          // Save as a single-page design
          pages: [currentPageData],
          currentPageIndex: 0
        };
   
        const formData = new FormData();
        formData.append('name', currentPageData.name || `${selectedTemplate.name || 'Design'} - Page ${i + 1}`);
        formData.append('data', JSON.stringify(canvasData));
   
        if (thumbnailBlob) {
          formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
        }
   
        if (selectedTemplate.id && selectedTemplate.id !== 'dev') {
          const normalizedId = normalizeId(selectedTemplate.id);
          const isTemporary = normalizedId.startsWith('custom-') || normalizedId.startsWith('new-');
          if (selectedTemplate.isCustom && !isTemporary) {
            formData.append('_id', normalizedId);
            formData.append('id', normalizedId);
            formData.append('savedTemplateId', normalizedId);
          } else if (!isTemporary && selectedTemplate.originTemplateId) {
            formData.append('originTemplateId', normalizeId(selectedTemplate.originTemplateId));
          }
        }

        // Add player data and offline assets for the player engine
        try {
          const playerData = compilePlayerData(canvasData as any);
          formData.append('player_data', JSON.stringify(playerData));
          
          const offlineAssets = extractOfflineAssets(playerData);
          formData.append('offline_assets', JSON.stringify(offlineAssets));
        } catch (compileErr) {
          console.error('❌ [SAVE ALL] Player Data compilation failed:', compileErr);
        }
   
        // 4. Save to remote API
        const result = await saveUserDesign(formData).unwrap();
        const rawNewId = result?.data?._id;
        const newId = normalizeId(rawNewId);
        
        if (newId) {

          dispatch(setSelectedTemplate({
            ...selectedTemplate,
            id: newId,
            isCustom: true
          }));
        }
   
        // 5. Keep local IndexedDB save for redundancy
        const editorState = {
          id: newId || `page-${Date.now()}`,
          ...canvasData,
          lastModified: Date.now()
        };
        await saveState(editorState);
      }
   
      // Cleanup local draft of the entire project if it exists
      if (selectedTemplate.id) {
        await deleteState(normalizeId(selectedTemplate.id));
      }
   
      setSavingStatus('saved');
      alert('All pages saved successfully!');
   
      // navigate('/my-projects');
    } catch (err) {
      console.error('Multi-page save failed:', err);
      setSavingStatus('error');
      // Restore state
      setCurrentPageIndex(originalPageIndex);
      setSelectedLayerIds(originalSelectedIds);
      setTimeout(() => setSavingStatus('saved'), 3000);
    }
  };
  */

  return (
    <div
      className="bg-[#fafafa] fixed inset-0 flex flex-col overflow-hidden"
      data-name="Template Editor"
    >
      {/* 
          UNIFIED SIDEBAR BACKDROP
          Covers everything except the sidebar (z-50) and top bar (z-30)
          Closes the active panel when clicked
      */}
      <AnimatePresence>
        {isManualSaving && (
          <SavingOverlay
            current={saveProgress.current}
            total={saveProgress.total}
            status={saveProgress.status}
            subStep={saveProgress.subStep}
          />
        )}
      </AnimatePresence>

      {/* Global Backdrop for Sidebar Panels */}
      {activeLeftPanel && (
        <div
          className="fixed inset-0 bg-black/5 z-[45] transition-opacity duration-300"
          onClick={() => setActiveLeftPanel(null)}
        />
      )}



      {/* ═══════════════════════════════════════════════════════════════════
          FIXED LAYOUT ZONES - DO NOT MODIFY STRUCTURE
          ═══════════════════════════════════════════════════════════════════
          
          CRITICAL LAYOUT RULES (LOCKED - DO NOT MODIFY):
          ✓ Root container MUST be: flex flex-col h-screen overflow-hidden
          ✓ ALL sidebars/toolbars/controls MUST be: position: fixed
          ✓ Main Editor Area is the ONLY scrollable zone: overflow-y-auto
          ✗ NEVER add: items-center, justify-center, or any vertical centering
          ✗ NEVER use absolute positioning for Page Header or Canvas
          ✗ NEVER add nested overflow-auto or overflow-y-auto zones
          ✗ NEVER add inline padding/margin - use documented values only
          ✗ NEVER add min-h-screen or h-screen to inner wrappers
          
          Layout Hierarchy (Render Order):
          
          1. Root Container (flex-col, h-screen, overflow-hidden)
             ├─ Background (decorative, absolute)
             ├─ Right Sidebar (fixed, right-0, z-20)
             ├─ Bottom Controls (fixed, bottom-[16px], z-20)
             ├─ Left Toolbar (fixed, left-[16px], top-[80px], z-20)
             │
             ├─ Main Editor Area (flex-1, overflow-y-auto) ← ONLY SCROLLABLE ZONE
             │  ├─ Page Header (h-[40px], shrink-0, w-[1172px])
             │  └─ Canvas (1172x659px, shrink-0, transform scale)
             │
             ├─ Property Panels (absolute, slide from sides)
             ├─ Floating Toolbars (absolute, dynamic positioning)
             └─ Top Nav Header (fixed, top-0, h-[60px], z-30)
          
          Layout Constants (DO NOT CHANGE):
          - Nav Header Height: 60px
          - Page Header Height: 40px
          - Canvas Width: 1172px
          - Canvas Height: 659px
          - Bottom Controls Height: ~54px
          - Left Toolbar Width: 82px
          
          Spacing Rules:
          - Main Editor paddingTop: 80px (nav header 60px + gap 20px)
          - Main Editor paddingBottom: 40px (minimal spacing after canvas)
          - Page Header height: 24px (compact height for page name controls)
          - Page Header marginTop: 0px (spacing handled by paddingTop)
          - Page Header marginBottom: 8px (mb-2, spacing before canvas)
          
          ═══════════════════════════════════════════════════════════════════ */}

      {/* Background (decorative only) */}
      <div className="absolute h-[964px] left-0 top-[60px] w-[1440px] pointer-events-none" />

      {/* ═══════════════════════════════════════════════════════════���═══════
          RIGHT SIDEBAR - Layers Panel
          ═════════════════════════════════��═════════════════════════════════
          - Position: fixed, right-0, z-20
          - Vertically centered with translate-y-[-50%]
          ══════════════════════════════════════════════════════���════════════ */}
      <div className="fixed bg-white right-0 rounded-bl-[8px] rounded-tl-[8px] top-[50%] translate-y-[-50%] z-20">
        <div className="content-stretch flex items-center overflow-clip px-[12px] py-[24px] relative rounded-[inherit]">
          <div className="content-stretch flex flex-col gap-[48px] items-center relative shrink-0">
            <div
              className="content-stretch flex flex-col gap-[8px] items-center px-0 py-[6px] relative rounded-[4px] shrink-0 w-full cursor-pointer transition-all"
              onClick={(e) => {
                e.stopPropagation();
                setIsLayersPanelOpen(!isLayersPanelOpen);
                setIsDataBindingSidepaneOpen(false);
                setIsAppSidepaneOpen(false);
              }}
            >
              <div
                className="app-selector-indicator"
                style={{ '--background-width': isLayersPanelOpen ? '100%' : '0%' } as React.CSSProperties}
              />
              <div className="relative flex items-center justify-center w-5 h-5 pointer-events-none z-10">
                <Layers className={`w-full h-full ${isLayersPanelOpen ? 'app-active-state' : 'text-[#344054]'}`} strokeWidth={1.25} />
              </div>
              <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${isLayersPanelOpen ? 'app-active-state' : 'text-[#344054]'}`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                Layers
              </p>
            </div>
            <div
              className={`content-stretch flex flex-col gap-[8px] items-center justify-center px-0 py-[6px] relative rounded-[4px] shrink-0 w-full transition-colors ${pages.length >= 5 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
              onClick={() => {
                if (pages.length >= 5) return;
                handleAddPage();
              }}
            >
              <div className="relative flex items-center justify-center w-[22px] h-[22px] pointer-events-none">
                <FilePlus className="w-full h-full text-[#344054]" strokeWidth={1.25} />
              </div>
              <p className="font-sans font-medium leading-[22px] relative shrink-0 text-[#344054] text-[11px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                Add Page
              </p>
            </div>
            <div
              className={`content-stretch flex flex-col gap-[8px] items-center justify-center px-0 py-[6px] relative rounded-[4px] shrink-0 w-full transition-colors ${pages.length >= 5 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
              onClick={() => {
                if (pages.length >= 5) return;
                handleDuplicatePage();
              }}
            >
              <div className="relative flex items-center justify-center w-5 h-5 pointer-events-none">
                <Copy className="w-full h-full text-[#344054]" strokeWidth={1.25} />
              </div>
              <p className="font-sans font-medium leading-[22px] relative shrink-0 text-[#344054] text-[11px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                Duplicate
              </p>
            </div>
          </div>
        </div>
        <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-bl-[8px] rounded-tl-[8px] shadow-[0px_8px_15px_-5px_rgba(0,0,0,0.1)]" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          RIGHT LAYERS PANEL - Slides out from right
          ═══════════════════════════════════════════════════════════════════
          - Position: fixed, right-[70px], z-30
          - Vertically centered relative to canvas
          - Height: Max 60-65% of viewport, Min enough for 6-8 rows
          - Internal scroll
          ════════════════════════════════════════════════════════��══════════ */}
      <div
        className={`fixed right-[70px] top-1/2 -translate-y-1/2 w-[280px] bg-white rounded-lg shadow-xl z-30 transition-transform duration-300 ease-in-out border border-gray-100 flex flex-col overflow-hidden max-h-[60vh] h-auto ${isLayersPanelOpen ? 'translate-x-0' : 'translate-x-[400px]'
          }`}
      >
        <LayersPanel
          layers={dynamicLayers}
          setLayers={setDynamicLayers}
          selectedLayerId={selectedLayerId}
          onSelectLayer={setSelectedLayerId}
          onUpdateLayer={(id, updates) => {
            handleUpdateLayer(id, updates);
            // We don't commit history for visibility/lock toggles inside the panel item itself 
            // because it might be too frequent? 
            // Actually, toggling visibility is a significant action.
            // The panel calls onUpdateLayer.
            commitToHistory('LAYER_EDIT', 'Updated layer properties');
          }}
          onClose={() => setIsLayersPanelOpen(false)}
          layerPositions={layerPositions}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          APP PROPERTIES SIDEPANE - Slides out from right
          ════════════��══════════════════════════════════════════════════════ */}
      <div
        className={`fixed right-0 top-[60px] h-[calc(100vh-60px)] z-[45] transition-transform duration-300 ease-in-out ${isAppSidepaneOpen && selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.isApp ? 'translate-x-0' : 'translate-x-[400px]'
          }`}
      >
        {(() => {
          const layer = selectedLayerId ? findLayerById(dynamicLayers, selectedLayerId) : null;
          if (layer && layer.isApp) {
            const defaultScrollerContent = (layer.children?.[0]?.content as string) || 'SCROLLER TEXT HERE';
            return (
              <AppPropertiesSidepane
                selectedAppId={selectedLayerId!}
                appName={layer.name}
                settings={
                  (layer.name.toLowerCase().includes('scroller') ? scrollerSettings[selectedLayerId!] : widgetSettings[selectedLayerId!])
                  || { ...DEFAULT_SCROLLER_SETTINGS, content: defaultScrollerContent }
                }
                onSettingsChange={(updates) => {
                  if (layer.name.toLowerCase().includes('scroller')) {
                    handleScrollerSettingsChange(selectedLayerId!, updates);
                  } else {
                    handleWidgetSettingsChange(selectedLayerId!, updates);
                  }
                }}
                onClose={() => setSelectedLayerId(null)}
                onDelete={handleLayerDeleteDirect}
              />
            );
          }
          return null;
        })()}
      </div>

      {/* DATA BINDING SIDEPANE - Slides out from right */}
      <div
        className={`fixed right-0 top-[60px] h-[calc(100vh-60px)] z-[49] transition-transform duration-300 ease-in-out ${
          isDataBindingSidepaneOpen ? 'translate-x-0' : 'translate-x-[400px]'
        }`}
      >
        <DataBindingSidepane
          layers={dynamicLayers}
          textStyles={textStyles}
          imageSettings={imageSettings}
          selectedLayerId={selectedLayerId}
          onSelectLayer={setSelectedLayerId}
          category={selectedTemplate?.category || 'general'}
          onBindingChange={handleBindingChange}
          onAutoMapAll={handleAutoMapAll}
          onAddBoundLayer={handleAddBoundLayer}
          onClose={() => setIsDataBindingSidepaneOpen(false)}
          showPlaceholders={showPlaceholders}
          onShowPlaceholdersChange={(val) => {
            setShowPlaceholders(val);
          }}
          autoPaginate={templateSettings.autoPaginate || false}
          onAutoPaginateChange={(val) => {
            setTemplateSettings(prev => ({ ...prev, autoPaginate: val }));
          }}
          templateSettings={templateSettings}
          onSettingsChange={(updates) => {
            setTemplateSettings(prev => ({ ...prev, ...updates }));
          }}
        />
      </div>

      {/* ═══════════════════════════════════��══════��════════════════════════
          BOTTOM CONTROLS - Timeline and Zoom Bar
          ═══════════════════════════════════════════════════════════════════
          - Position: fixed, bottom-[16px], z-20
          - Horizontally centered with translate-x-[-50%]
          - Height: ~54px
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="fixed z-50 bg-white/95 backdrop-blur-sm rounded-[8px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 px-5 py-1.5 flex items-center gap-3 transition-all duration-300 ease-out left-[50%] bottom-[16px] translate-x-[-50%]">
        <div className="flex items-center gap-3 h-9">
          <div
            className={`flex items-center gap-1.5 h-7 px-2 cursor-pointer rounded transition-colors ${isTimelineOpen ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            onClick={() => setIsTimelineOpen(!isTimelineOpen)}
          >
            <Wrapper2>
              <g clipPath="url(#clip0_163_428)" id="Frame">
                <path d={svgPaths.p8ff3780} id="Vector" stroke={isTimelineOpen ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p2d52d00} id="Vector_2" stroke={isTimelineOpen ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </g>
              <defs>
                <clipPath id="clip0_163_428">
                  <rect fill="white" height="18" width="18" />
                </clipPath>
              </defs>
            </Wrapper2>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${isTimelineOpen ? 'app-active-state' : 'text-[#344054]'}`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Timeline
            </p>
          </div>

          <div className="w-px h-5 bg-gray-200" />

          <div className="flex items-center h-7 px-1 gap-1">
            <button
              disabled={currentPageIndex === 0}
              onClick={() => handleSwitchPage(currentPageIndex - 1)}
              className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous Page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6"></path>
              </svg>
            </button>
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-tighter w-14 text-center">
              Page {currentPageIndex + 1}/{pages.length}
            </span>
            <button
              disabled={currentPageIndex === pages.length - 1}
              onClick={() => handleSwitchPage(currentPageIndex + 1)}
              className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next Page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </button>
            <div className="w-px h-3 bg-gray-200 mx-1"></div>
            <button
              onClick={handleAddPage}
              disabled={pages.length >= 20}
              className="p-1 hover:bg-green-50 hover:text-green-600 rounded text-gray-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={pages.length >= 20 ? "Maximum limit of 20 pages reached" : "Add Blank Page"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
              </svg>
            </button>
            {pages.length > 1 && (
              <button
                onClick={() => handleDeletePage(currentPageIndex)}
                className="p-1 hover:bg-red-50 hover:text-red-500 rounded text-gray-500 transition-colors"
                title="Delete Current Page"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="w-px h-5 bg-gray-200" />

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setHasManuallyZoomed(true);
                setCanvasScale(prev => Math.max(prev - 0.25, 0.25));
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Zoom Out"
            >
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M3.75 9H14.25" stroke="#475367" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </svg>
            </button>

            <input
              type="range"
              min="0.25"
              max="4"
              step="0.01"
              value={canvasScale}
              onChange={(e) => {
                setHasManuallyZoomed(true);
                setCanvasScale(parseFloat(e.target.value));
              }}
              className="w-[96px] h-[3px] bg-[#e4e7ec] rounded-full appearance-none cursor-pointer accent-[#0ba29b]"
            />

            <button
              onClick={() => {
                setHasManuallyZoomed(true);
                setCanvasScale(prev => Math.min(prev + 0.25, 4));
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Zoom In"
            >
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M3.75 9H14.25" stroke="#475367" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                <path d="M9 3.75V14.25" stroke="#475367" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </svg>
            </button>

            <span className="text-[11px] font-bold text-gray-500 min-w-[32px] text-center select-none tabular-nums">
              {Math.round(canvasScale * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* ════════════════════════════��══════════════════════════════════════
          LEFT TOOLBAR - Tools Panel
          ═══════════════════════════════════════════════════════════════════
          - Position: fixed, left-[16px], top-[80px], z-20
          - Width: 82px (DO NOT CHANGE)
          - Height: calc(100vh - 100px) - dynamic based on viewport
          ══════════════════════════════════════════════════════��════════════ */}
      <div className="fixed bg-white border-[#e4e7ec] border-[0.75px] border-solid h-[calc(100vh-100px)] left-[16px] overflow-hidden rounded-[8px] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] top-[80px] w-[82px] z-50">
        <style>{`
          .no-scrollbar::-webkit-scrollbar {
            display: none !important;
          }
          .no-scrollbar {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `}</style>
        <div className="absolute content-stretch flex flex-col gap-[24px] items-center left-1/2 top-[19.25px] bottom-[19.25px] translate-x-[-50%] w-[66px] overflow-y-auto no-scrollbar">
          <div className="relative rounded-[4px] shrink-0 w-full">
            <div className="flex flex-col items-center justify-center overflow-clip rounded-[inherit] size-full">
              <div
                className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveLeftPanel(prev => prev === 'contents' ? null : 'contents');
                }}
              >
                <div
                  className="app-selector-indicator"
                  style={{ '--background-width': activeLeftPanel === 'contents' ? '100%' : '0%' } as React.CSSProperties}
                />
                <div className="relative flex items-center justify-center w-5 h-5 relative z-10 transition-colors duration-400">
                  <Database className={`w-full h-full ${activeLeftPanel === 'contents' ? 'app-active-state' : 'text-[#344054]'}`} />
                </div>
                <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'contents' ? 'app-active-state' : 'text-[#344054]'
                  }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  Contents
                </p>
              </div>
            </div>
          </div>
          <div className="relative rounded-[4px] shrink-0 w-full">
            <div className="flex flex-col items-center justify-center overflow-clip rounded-[inherit] size-full">
              <div
                className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveLeftPanel(prev => prev === 'uploads' ? null : 'uploads');
                }}
              >
                <div
                  className="app-selector-indicator"
                  style={{ '--background-width': activeLeftPanel === 'uploads' ? '100%' : '0%' } as React.CSSProperties}
                />
                <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
                  <Frame className="w-full h-full">
                    <path d="M9 2.25V11.25" id="Vector" stroke={activeLeftPanel === 'uploads' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                    <path d="M12.75 6L9 2.25L5.25 6" id="Vector_2" stroke={activeLeftPanel === 'uploads' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                    <path d={svgPaths.p1c7ad000} id="Vector_3" stroke={activeLeftPanel === 'uploads' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                  </Frame>
                </div>
                <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'uploads' ? 'app-active-state' : 'text-[#344054]'
                  }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  Uploads
                </p>
              </div>
            </div>
          </div>
          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'templates' ? null : 'templates');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'templates' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <Frame className="w-full h-full">
                <path d={svgPaths.p2cd86400} id="Vector" stroke={activeLeftPanel === 'templates' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p3f33cb80} id="Vector_2" stroke={activeLeftPanel === 'templates' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p15dde980} id="Vector_3" stroke={activeLeftPanel === 'templates' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </Frame>
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'templates' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Templates
            </p>
          </div>
          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'apps' ? null : 'apps');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'apps' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <svg className="block w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 18 18">
                <g id="Frame">
                  <path d={appsSvgPaths.p2f7c3ff0} id="Vector" stroke={activeLeftPanel === 'apps' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                  <path d={appsSvgPaths.p772e900} id="Vector_2" stroke={activeLeftPanel === 'apps' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                  <path d={appsSvgPaths.p99ad200} id="Vector_3" stroke={activeLeftPanel === 'apps' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                  <path d={appsSvgPaths.p3fc0d440} id="Vector_4" stroke={activeLeftPanel === 'apps' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                </g>
              </svg>
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'apps' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Apps
            </p>
          </div>
          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'playlist' ? null : 'playlist');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'playlist' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <Frame className="w-full h-full">
                <path d="M15.75 3.75H2.25" id="Vector" stroke={activeLeftPanel === 'playlist' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d="M7.5 9H2.25" id="Vector_2" stroke={activeLeftPanel === 'playlist' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d="M7.5 14.25H2.25" id="Vector_3" stroke={activeLeftPanel === 'playlist' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={playlistSvgPaths.p2f7c3ff0} id="Vector_4" stroke={activeLeftPanel === 'playlist' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </Frame>
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'playlist' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Playlist
            </p>
          </div>
          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'brand' ? null : 'brand');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'brand' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <Palette
                className={`w-full h-full ${activeLeftPanel === 'brand' ? 'app-active-state' : 'text-[#344054]'}`}
                strokeWidth={1.25}
              />
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'brand' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Brand Kit
            </p>
          </div>
          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'text' ? null : 'text');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'text' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <Frame className="w-full h-full">
                <path d="M9 3V15" id="Vector" stroke={activeLeftPanel === 'text' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p110e2f80} id="Vector_2" stroke={activeLeftPanel === 'text' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d="M6.75 15H11.25" id="Vector_3" stroke={activeLeftPanel === 'text' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </Frame>
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'text' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Text
            </p>
          </div>

          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setActiveLeftPanel(prev => prev === 'elements' ? null : 'elements');
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': activeLeftPanel === 'elements' ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 z-10 transition-colors duration-400">
              <Frame className="w-full h-full">
                <path d={svgPaths.p6cfad80} id="Vector" stroke={activeLeftPanel === 'elements' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p9e34f00} id="Vector_2" stroke={activeLeftPanel === 'elements' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d={svgPaths.p4031f00} id="Vector_3" stroke={activeLeftPanel === 'elements' ? "#30A84B" : "#344054"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </Frame>
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${activeLeftPanel === 'elements' ? 'app-active-state' : 'text-[#344054]'
              }`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Elements
            </p>
          </div>

          <div
            className="content-stretch flex flex-col gap-[4px] h-[48px] items-center justify-center relative shrink-0 w-full cursor-pointer transition-all rounded-[8px] hover:bg-[#f9fafb]"
            onClick={(e) => {
              e.stopPropagation();
              setIsDataBindingSidepaneOpen(!isDataBindingSidepaneOpen);
              setIsLayersPanelOpen(false);
              setIsAppSidepaneOpen(false);
            }}
          >
            <div
              className="app-selector-indicator"
              style={{ '--background-width': isDataBindingSidepaneOpen ? '100%' : '0%' } as React.CSSProperties}
            />
            <div className="relative flex items-center justify-center w-5 h-5 pointer-events-none z-10 transition-colors duration-400">
              <Database className={`w-full h-full ${isDataBindingSidepaneOpen ? 'app-active-state' : 'text-[#344054]'}`} strokeWidth={1.25} />
            </div>
            <p className={`font-sans font-medium leading-[22px] relative shrink-0 text-[11px] text-nowrap z-10 transition-colors duration-400 ${isDataBindingSidepaneOpen ? 'app-active-state' : 'text-[#344054]'}`} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Data Feeds
            </p>
          </div>
        </div>

        {/* Elements Panel - slides out from sidebar */}
        <ElementsPanel
          isOpen={activeLeftPanel === 'elements'}
          onClose={() => setActiveLeftPanel(null)}
          onAddElement={handleAddElement}
        />



        {/* Templates Panel - slides out from sidebar */}
        <TemplatesPanel
          isOpen={activeLeftPanel === 'templates'}
          onClose={() => setActiveLeftPanel(null)}
          onSelectTemplate={handleTemplateSelect}
        />

        {/* Apps Panel - slides out from sidebar */}
        <AppsPanel
          isOpen={activeLeftPanel === 'apps'}
          onClose={() => setActiveLeftPanel(null)}
          onAddApp={handleAddApp}
          onOpenCreateAppModal={() => setIsCreateAppModalOpen(true)}
          apps={apps}
        />

        {/* Playlist Panel - slides out from sidebar */}
        <PlaylistPanel
          isOpen={activeLeftPanel === 'playlist'}
          onClose={() => setActiveLeftPanel(null)}
          onAddPlaylist={handleAddPlaylist}
        />

        {/* Uploads Panel - slides out from sidebar */}
        <UploadsPanel
          isOpen={activeLeftPanel === 'uploads'}
          onClose={() => setActiveLeftPanel(null)}
          onAddToCanvas={handleAddUploadToCanvas}
        />

        {/* Text Panel */}
        <TextPanel
          isOpen={activeLeftPanel === 'text'}
          onClose={() => setActiveLeftPanel(null)}
          onAddText={handleAddText}
        />

        {/* Brand Kit Panel */}
        <BrandKitPanel
          isOpen={activeLeftPanel === 'brand'}
          onClose={() => setActiveLeftPanel(null)}
          onApplyColor={handleApplyBrandColor}
          onApplyFont={handleApplyBrandFont}
          onAddLogo={handleAddBrandLogo}
        />

        {/* Contents Panel */}
        <ContentsPanel
          isOpen={activeLeftPanel === 'contents'}
          onClose={() => setActiveLeftPanel(null)}
          onAddToCanvas={handleAddUploadToCanvas}
        />
      </div>

      {/* ═══��═══════════��════════════��═══════════════════════════════��══════
          MAIN EDITOR AREA - NO SCROLL (flex-1, overflow: hidden)
          ════════════════════════════════════���══════════════════════════════ */}
      <div className="flex-1 overflow-hidden flex flex-col relative pt-[60px]">
        {/* Contextual Properties Toolbar - Absolute/Static Positioning */}
        <div
          className="absolute z-50 bg-white/95 backdrop-blur-md rounded-[12px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-gray-100 p-1.5 flex flex-col items-center gap-1.5 transition-all duration-300 ease-out animate-in fade-in slide-in-from-top-4 top-1 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center">
            {!selectedLayerId ? (
              /* Floating Page Header (Shown when nothing is selected) */
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center h-9 px-1 gap-0.5 bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-[0_4px_12px_rgba(0,0,0,0.05)] rounded-lg mx-auto min-w-max"
              >
                {/* Active Page Name */}
                <div className="flex items-center h-7 px-2 border-r border-gray-100 mr-1 gap-2">
                  {isEditingPageName ? (
                    <input
                      ref={pageNameInputRef}
                      value={pageName}
                      onChange={handlePageNameChange}
                      onBlur={handlePageNameBlur}
                      onKeyDown={handlePageNameKeyDown}
                      className="text-[13px] font-semibold text-gray-700 bg-transparent outline-none w-32 px-1 -ml-1 hover:bg-gray-50 rounded transition-all focus:bg-white"
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={handlePageNameClick}
                      className="text-[13px] font-semibold text-gray-700 cursor-pointer hover:text-green-600 transition-colors truncate max-w-[150px]"
                    >
                      {pageName}
                    </span>
                  )}
                  <div className="w-3.5 h-3.5 text-gray-400 shrink-0">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Interactive Resolution Property */}
                <div className="flex items-center h-7 px-2 border-r border-gray-100 mr-1 gap-3 group" title="Adjust Canvas Resolution">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 9H21" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 21V9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex items-center gap-1.5 px-1.5 py-0.5 bg-gray-50/50 rounded border border-transparent hover:border-gray-100 transition-all cursor-default" title="Canvas Resolution">
                    <span className="text-[11px] font-bold tabular-nums text-gray-500">{canvasDimensions.width}</span>
                    <span className="text-[10px] font-bold text-gray-300">×</span>
                    <span className="text-[11px] font-bold tabular-nums text-gray-500">{canvasDimensions.height}</span>
                    <span className="text-[9px] font-bold text-gray-400 ml-0.5">{canvasDimensions.unit}</span>
                  </div>
                </div>

                {/* Page Navigation & Management */}
                <div className="flex items-center h-7 px-1 border-r border-gray-100 mr-1 gap-1">
                  <button
                    onClick={() => handleSwitchPage(currentPageIndex - 1)}
                    disabled={currentPageIndex === 0}
                    className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-tighter w-14 text-center">
                    Page {currentPageIndex + 1}/{pages.length}
                  </span>
                  <button
                    onClick={() => handleSwitchPage(currentPageIndex + 1)}
                    disabled={currentPageIndex === pages.length - 1}
                    className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <div className="w-px h-3 bg-gray-100 mx-1" />

                  <button
                    onClick={handleAddPage}
                    disabled={pages.length >= 20}
                    className="p-1 hover:bg-green-50 hover:text-green-600 rounded text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    title={pages.length >= 20 ? "Maximum limit of 20 pages reached" : "Add Blank Page"}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {/* <button
                    onClick={handleDuplicatePage}
                    className="p-1 hover:bg-green-50 hover:text-green-600 rounded text-gray-400 transition-colors"
                    title="Duplicate Current Page"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button> */}
                  {pages.length > 1 && (
                    <button
                      onClick={() => handleDeletePage(currentPageIndex)}
                      className="p-1 hover:bg-red-50 hover:text-red-500 rounded text-gray-400 transition-colors"
                      title="Delete Current Page"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Global Settings Button */}
                <button
                  onClick={() => setSelectedLayerIds(['root'])}
                  className={`flex items-center gap-2 h-7 px-2.5 rounded-md text-[13px] font-semibold transition-all ${selectedLayerIds.includes('root')
                    ? 'bg-green-50 text-green-600'
                    : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  title="Page Settings"
                >
                  <Palette className="w-3.5 h-3.5" />
                  <span>Settings</span>
                </button>

                {/* Saving Status Badge */}
                <div className="flex items-center h-7 px-2 ml-1 border-l border-gray-100">
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${savingStatus === 'saved' ? 'bg-green-50/50 text-green-600' :
                    savingStatus === 'saving' ? 'bg-blue-50 text-blue-700' :
                      savingStatus === 'syncing' ? 'bg-gray-50 text-gray-500' :
                        'bg-red-50 text-red-700'
                    }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${savingStatus === 'saved' ? 'bg-green-400' :
                      savingStatus === 'saving' ? 'bg-blue-500 animate-pulse' :
                        savingStatus === 'syncing' ? 'bg-gray-400 animate-bounce' :
                          'bg-red-500'
                      }`} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      {savingStatus === 'saved' ? 'Version Synced' :
                        savingStatus === 'saving' ? 'Saving...' :
                          savingStatus === 'syncing' ? 'Syncing Draft' :
                            'Error saving'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="contents">
                {/* Template Properties (Page Settings) */}
                {selectedLayerId === 'root' && !activeLeftPanel && (
                  <TemplatePropertiesPanel
                    settings={templateSettings}
                    onSettingsChange={(updates) => {
                      setTemplateSettings(prev => ({ ...prev, ...updates }));
                      commitToHistory('STYLE_CHANGE', 'Updated template settings');
                    }}
                    onClose={() => setSelectedLayerIds([])}
                  />
                )}

                {/* Text Properties Panel */}
                {selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'text' && (
                  <TextPropertiesPanel
                    selectedTextId={selectedLayerId}
                    textStyle={(() => {
                      if (textStyles[selectedLayerId]) return textStyles[selectedLayerId];
                      const element = document.getElementById(selectedLayerId);
                      if (element) {
                        const computed = window.getComputedStyle(element);
                        const fontSize = parseFloat(computed.fontSize);
                        return {
                          fontFamily: computed.fontFamily.replace(/['"]/g, '').split(',')[0].trim(),
                          fontSize: fontSize,
                          fontWeight: normalizeFontWeight(computed.fontWeight),
                          lineHeight: computed.lineHeight === 'normal' ? 1.5 : parseFloat(computed.lineHeight) / fontSize,
                          letterSpacing: parseFloat(computed.letterSpacing) || 0,
                          textAlign: (computed.textAlign as 'left' | 'center' | 'right') || 'left',
                          textTransform: (computed.textTransform as 'none' | 'uppercase' | 'lowercase') || 'none',
                          color: rgbToHex(computed.color),
                          opacity: parseFloat(computed.opacity) * 100 || 100,
                          italic: computed.fontStyle === 'italic',
                          underline: computed.textDecoration.includes('underline'),
                          uppercase: computed.textTransform === 'uppercase',
                        };
                      }
                      return {};
                    })()}
                    dimensions={selectedLayerId && layerPositions[selectedLayerId] ? {
                      left: layerPositions[selectedLayerId].left,
                      top: layerPositions[selectedLayerId].top,
                      width: layerPositions[selectedLayerId].width || 0,
                      height: layerPositions[selectedLayerId].height || 0,
                      rotation: layerPositions[selectedLayerId].rotation || 0
                    } : undefined}
                    onStyleChange={handleTextStyleChange}
                    onAutoMapAll={handleAutoMapAll}
                    layerName={findLayerById(dynamicLayers, selectedLayerId)?.name}
                    onDimensionsChange={(updates) => handleDimensionsChange(selectedLayerId, updates)}
                    onDimensionsCommit={() => handleDimensionsCommit(selectedLayerId, 'text')}
                    onClose={() => {
                      setSelectedLayerId(null);
                      setEditingTextId(null);
                    }}
                    onDelete={handleLayerDeleteDirect}
                    onOpenDataBindingSidepane={() => {
                      setIsDataBindingSidepaneOpen(true);
                      setIsLayersPanelOpen(false);
                      setIsAppSidepaneOpen(false);
                    }}
                  />
                )}

                {/* Background Properties Panel */}
                {(selectedLayerId === 'left-panel' || selectedLayerId === 'root') && (
                  <BackgroundPropertiesPanel
                    selectedPanelId={selectedLayerId}
                    settings={backgroundSettings[selectedLayerId] || {
                      type: 'solid', solidColor: '#1a1a1a', opacity: 100, gradientStart: '#1a1a1a',
                      gradientEnd: '#4a4a4a', gradientAngle: 90, imageFit: 'cover', blur: 0,
                      overlayColor: '#000000', overlayOpacity: 0
                    }}
                    onSettingsChange={handleBackgroundSettingsChange}
                    onClose={() => setSelectedLayerId(null)}
                  />
                )}

                {/* Image Properties Panel */}
                {selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'image' && (
                  <ImagePropertiesPanel
                    selectedImageId={selectedLayerId}
                    settings={imageSettings[selectedLayerId] || DEFAULT_IMAGE_SETTINGS}
                    dimensions={layerPositions[selectedLayerId] as { left: number; top: number; width: number; height: number; rotation?: number }}
                    onSettingsChange={handleImageSettingsChange}
                    onAutoMapAll={handleAutoMapAll}
                    layerName={findLayerById(dynamicLayers, selectedLayerId)?.name}
                    onDimensionsChange={(updates) => handleDimensionsChange(selectedLayerId, updates)}
                    onDimensionsCommit={() => handleDimensionsCommit(selectedLayerId, 'image')}
                    onClose={() => setSelectedLayerId(null)}
                    onDelete={handleLayerDeleteDirect}
                    onOpenDataBindingSidepane={() => {
                      setIsDataBindingSidepaneOpen(true);
                      setIsLayersPanelOpen(false);
                      setIsAppSidepaneOpen(false);
                    }}
                  />
                )}

                {/* Video Properties Panel */}
                {selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'video' && (
                  <VideoPropertiesPanel
                    selectedVideoId={selectedLayerId}
                    settings={videoSettings[selectedLayerId] || DEFAULT_VIDEO_SETTINGS}
                    dimensions={layerPositions[selectedLayerId] as { left: number; top: number; width: number; height: number }}
                    onSettingsChange={handleVideoSettingsChange}
                    onDimensionsChange={(updates) => handleDimensionsChange(selectedLayerId, updates)}
                    onDimensionsCommit={() => handleDimensionsCommit(selectedLayerId, 'video')}
                    onClose={() => setSelectedLayerId(null)}
                    onDelete={handleLayerDeleteDirect}
                  />
                )}

                {/* Shape Properties Panel */}
                <ShapePropertiesPanel
                  selectedShapeId={(() => {
                    if (!selectedLayerId) return null;
                    const layer = findLayerById(dynamicLayers, selectedLayerId);
                    if (layer && layer.type === 'rectangle') {
                      if (layer.isApp) return null;
                      return selectedLayerId;
                    }
                    return null;
                  })()}
                  settings={(() => {
                    if (!selectedLayerId || !shapeSettings[selectedLayerId]) return DEFAULT_SHAPE_SETTINGS;
                    const layer = findLayerById(dynamicLayers, selectedLayerId);
                    if (!layer) return DEFAULT_SHAPE_SETTINGS;
                    const defaultSettings = {
                      fill: layer.fill || { enabled: true, color: '#1e293b' },
                      stroke: layer.stroke || { enabled: false, color: '#000000', width: 2, style: 'solid' },
                      cornerRadius: layer.cornerRadius ?? 0,
                      rotation: 0,
                      opacity: layer.opacity ?? 1,
                      blur: layer.blur ?? 0,
                      animation: layer.animation || { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
                    };
                    return {
                      fill: shapeSettings[selectedLayerId].fill || defaultSettings.fill,
                      stroke: shapeSettings[selectedLayerId].stroke || defaultSettings.stroke,
                      cornerRadius: shapeSettings[selectedLayerId].cornerRadius ?? defaultSettings.cornerRadius,
                      rotation: shapeSettings[selectedLayerId].rotation ?? defaultSettings.rotation,
                      opacity: shapeSettings[selectedLayerId].opacity ?? defaultSettings.opacity,
                      blur: shapeSettings[selectedLayerId].blur ?? defaultSettings.blur,
                      animation: shapeSettings[selectedLayerId].animation || defaultSettings.animation
                    } as ShapeSettings;
                  })()}
                  dimensions={selectedLayerId && layerPositions[selectedLayerId] ? {
                    left: layerPositions[selectedLayerId].left,
                    top: layerPositions[selectedLayerId].top,
                    width: layerPositions[selectedLayerId].width || 0,
                    height: layerPositions[selectedLayerId].height || 0,
                    rotation: layerPositions[selectedLayerId].rotation || 0
                  } : undefined}
                  onSettingsChange={handleShapeSettingsChange}
                  onDimensionsChange={(updates) => handleDimensionsChange(selectedLayerId, updates)}
                  onDimensionsCommit={() => handleDimensionsCommit(selectedLayerId, 'shape')}
                  onDuplicate={handleLayerDuplicateDirect}
                  onDelete={handleLayerDeleteDirect}
                  onClose={() => setSelectedLayerId(null)}
                />

                {/* Group Properties Panel */}
                {selectedLayerId && findLayerById(dynamicLayers, selectedLayerId)?.type === 'group' && (
                  <GroupPropertiesPanel
                    groupId={selectedLayerId}
                    properties={{
                      x: layerPositions[selectedLayerId]?.left || 0,
                      y: layerPositions[selectedLayerId]?.top || 0,
                      width: layerPositions[selectedLayerId]?.width || 0,
                      height: layerPositions[selectedLayerId]?.height || 0,
                      rotation: layerPositions[selectedLayerId]?.rotation || 0,
                      opacity: findLayerById(dynamicLayers, selectedLayerId)?.opacity ?? 100,
                      locked: findLayerById(dynamicLayers, selectedLayerId)?.locked || false,
                      hidden: findLayerById(dynamicLayers, selectedLayerId)?.hidden || false,
                    }}
                    onPropertiesChange={(updates) => {
                      if (!selectedLayerId) return;
                      const pos = layerPositions[selectedLayerId];

                      if ('locked' in updates || 'hidden' in updates || 'opacity' in updates) {
                        setDynamicLayers(prev => updateLayerDeep(prev, selectedLayerId, updates));
                      }
                      if ('x' in updates || 'y' in updates || 'width' in updates || 'height' in updates || 'rotation' in updates) {
                        const newWidth = updates.width !== undefined ? updates.width : (pos?.width || 0);
                        const newHeight = updates.height !== undefined ? updates.height : (pos?.height || 0);
                        const deltaW = newWidth - (pos?.width || 0);
                        const deltaH = newHeight - (pos?.height || 0);

                        if (pos && (deltaW !== 0 || deltaH !== 0)) {
                          const anchored = calculateAnchoredPosition(
                            {
                              left: pos.left,
                              top: pos.top,
                              width: pos.width || 0,
                              height: pos.height || 0,
                              rotation: pos.rotation || 0
                            },
                            deltaW,
                            deltaH,
                            0, // Anchor X = 0
                            0  // Anchor Y = 0
                          );
                          setLayerPositions(prev => ({
                            ...prev,
                            [selectedLayerId]: {
                              ...prev[selectedLayerId],
                              ...(updates.x !== undefined ? { left: updates.x } : { left: anchored.left }),
                              ...(updates.y !== undefined ? { top: updates.y } : { top: anchored.top }),
                              ...(updates.width !== undefined ? { width: updates.width } : {}),
                              ...(updates.height !== undefined ? { height: updates.height } : {}),
                              ...(updates.rotation !== undefined ? { rotation: updates.rotation } : {}),
                            }
                          }));
                        } else {
                          setLayerPositions(prev => ({
                            ...prev,
                            [selectedLayerId]: {
                              ...prev[selectedLayerId],
                              ...(updates.x !== undefined ? { left: updates.x } : {}),
                              ...(updates.y !== undefined ? { top: updates.y } : {}),
                              ...(updates.width !== undefined ? { width: updates.width } : {}),
                              ...(updates.height !== undefined ? { height: updates.height } : {}),
                              ...(updates.rotation !== undefined ? { rotation: updates.rotation } : {}),
                            }
                          }));
                        }
                      }
                    }}
                    onUngroup={handleUngroupSelected}
                    onClose={() => setSelectedLayerId(null)}
                  />
                )}

                {/* Scroller Properties Panel - MOVED TO SIDEPANE */}
              </div>
            )}
          </div>
        </div>
        <div
          className="flex-1 overflow-hidden flex flex-col items-center justify-center bg-[#eaebed] relative select-none"
          onClick={() => setSelectedLayerIds([])}
        >

          {/* Canvas Wrapper - Correctly sized layout box for flex centering */}
          <div
            className="relative shrink-0 shadow-[0_20px_50px_rgba(0,0,0,0.15)] rounded-[4px] border border-gray-300/50 bg-white"
            style={{
              width: `${CANVAS_WIDTH * canvasScale}px`,
              height: `${CANVAS_HEIGHT * canvasScale}px`,
            }}
          >

            {/* 1. Actual Canvas Area - Strictly clipped to the 1920x400 boundary */}
            <div
              ref={canvasRef}
              className="bg-white select-none shrink-0 outline-none absolute top-0 left-0 overflow-hidden"
              style={{
                ...applyBackgroundSettings(backgroundSettings['root']),
                width: `${CANVAS_WIDTH}px`,
                height: `${CANVAS_HEIGHT}px`,
                transform: `scale(${canvasScale}) translate(${templateSettings.flipHorizontal ? CANVAS_WIDTH : 0}px, ${templateSettings.flipVertical ? CANVAS_HEIGHT : 0}px) scaleX(${templateSettings.flipHorizontal ? -1 : 1}) scaleY(${templateSettings.flipVertical ? -1 : 1})`,
                transformOrigin: 'top left',
                opacity: (templateSettings.opacity / 100) * (Number(applyBackgroundSettings(backgroundSettings['root']).opacity ?? 1)),
                clipPath: templateSettings.crop?.enabled
                  ? `inset(${templateSettings.crop?.top ?? 0}px ${templateSettings.crop?.right ?? 0}px ${templateSettings.crop?.bottom ?? 0}px ${templateSettings.crop?.left ?? 0}px)`
                  : undefined,
                animationName: templateSettings.animation?.type && templateSettings.animation.type !== 'none'
                  ? `shape${templateSettings.animation.type.charAt(0).toUpperCase() + templateSettings.animation.type.slice(1)}`
                  : 'none',
                animationDuration: `${templateSettings.animation?.duration}ms`,
                animationDelay: isTimelineOpen && !isPlaying
                  ? `${(templateSettings.animation?.delay || 0) - currentTime}ms`
                  : `${templateSettings.animation?.delay || 0}ms`,
                animationTimingFunction: templateSettings.animation?.easing,
                animationPlayState: isTimelineOpen && !isPlaying ? 'paused' : 'running',
                cursor: draggingLayer ? 'grabbing' : 'default',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                WebkitTouchCallout: 'none'
              }}
              tabIndex={-1}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={handleCanvasClick}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={handleDrop}
            >
              {!isInitialLoadComplete ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-[9999]">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-3 border-[#30A84B] border-t-transparent rounded-full animate-spin" />
                    <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-widest animate-pulse">Syncing Design...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Template Selection Layer (Interaction Only - Behind) */}
                  <div
                    className="absolute inset-0 z-0 shadow-none outline-none"
                    style={{ cursor: isHoveringCanvas ? 'pointer' : 'default' }}
                    onMouseEnter={() => !draggingLayer && setIsHoveringCanvas(true)}
                    onMouseLeave={() => setIsHoveringCanvas(false)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLayerIds(['root']);
                    }}
                  />

                  {/* Root Layer Container - Pointer events none so clicks pass to Selection Layer */}
                  {dynamicLayers.map((layer, index) => (
                    <div key={`canvas-wrapper-${layer.id || index}`} className={layer.id === 'root' ? "pointer-events-none contents" : "pointer-events-auto contents"}>
                      <LayerRenderer
                        key={`canvas-layer-${layer.id || index}`}
                        layer={layer}
                        selectedId={selectedLayerId}
                        selectedIds={selectedLayerIds}
                        onSelect={setSelectedLayerId}
                        onSelectMulti={handleLayerSelect}
                        onDragStart={handleLayerDragStart}
                        layerPositions={layerPositions}
                        isDragging={draggingLayer?.hasMoved || false}
                        justDraggedRef={justDraggedRef}
                        textContents={resolvedBindings.textContents}
                        onTextContentChange={handleTextContentChange}
                        editingTextId={editingTextId}
                        onTextDoubleClick={handleTextDoubleClick}
                        textStyles={textStyles}
                        onTextEditComplete={handleTextEditComplete}
                        backgroundSettings={backgroundSettings}
                        imageSettings={resolvedBindings.imageSettings}
                        shapeSettings={shapeSettings}
                        scrollerSettings={resolvedBindings.scrollerSettings}
                        widgetSettings={widgetSettings}
                        hoveredId={hoveredLayerId}
                        onHoverChange={setHoveredLayerId}
                        onImageLoad={handleImageLoad}
                        currentTime={currentTime}
                        isPlaying={isPlaying}
                        isTimelineOpen={isTimelineOpen}
                        lastClickRef={lastClickRef}
                        rotatingLayer={rotatingLayer}
                        resizingLayer={resizingLayer}
                        videoSettings={videoSettings}
                        canvasScale={canvasScale}
                      />
                    </div>
                  ))}
                </>
              )}

              {/* Template Visual Overlay (Front) */}
              <div
                className={`absolute inset-0 z-50 pointer-events-none transition-[outline] duration-200 ${selectedLayerIds.includes('root')
                  ? 'outline outline-2 outline-blue-400'
                  : isHoveringCanvas
                    ? 'outline outline-2 outline-dashed outline-gray-300'
                    : 'outline-none'
                  }`}
              />

            </div>

            {/* 2. UI Interaction Layer - Same scale but OVERFLOW VISIBLE to show controls and snap guides */}
            <div
              className="absolute inset-0 pointer-events-none z-50 overflow-visible"
              style={{
                width: `${CANVAS_WIDTH}px`,
                height: `${CANVAS_HEIGHT}px`,
                transform: `scale(${canvasScale})`,
                transformOrigin: 'top left',
              }}
            >
              {/* Selection overlay with corner handles - Move/Rotate controls can bleed out */}
              {selectionBounds && !draggingLayer?.hasMoved && !editingTextId && !isPreviewOpen && (
                <SelectionOverlay
                  bounds={selectionBounds}
                  isLocked={selectedLayerIds.some(id => findLayerById(dynamicLayers, id)?.locked)}
                  onResizeStart={handleResizeStart}
                  onRotateStart={handleRotateStart}
                  lastGlobalClickRef={lastClickRef}
                  onDragStart={(e) => {
                    const primaryId = selectedLayerIds[0];
                    if (primaryId) handleLayerDragStart(primaryId, e);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    // Only support double-click to edit if a single text layer is selected
                    if (selectedLayerIds.length === 1) {
                      const id = selectedLayerIds[0];
                      const layer = findLayerById(dynamicLayers, id);
                      if (layer?.type === 'text') {
                        handleTextDoubleClick(id);
                      }
                    }
                  }}
                />
              )}

              {/* High-precision Snap Guides */}
              {snapGuides.length > 0 && (
                <SnapGuides
                  guides={snapGuides}
                  canvasWidth={CANVAS_WIDTH}
                  canvasHeight={CANVAS_HEIGHT}
                />
              )}
            </div>
          </div>
          {/* End Canvas Wrapper */}

          {/* Widget Context Toolbar (Pencil/Delete) - Now supports both Apps and Text */}
          {selectedLayerIds.length === 1 && selectedLayerId && selectionBounds && !draggingLayer?.hasMoved && !editingTextId && (() => {
            const layer = findLayerById(dynamicLayers, selectedLayerId);
            if (!layer) return null;

            // Show for Apps, Text, OR Playlists
            if (!layer.isApp && layer.type !== 'text' && layer.type !== 'playlist') return null;
            if (isPreviewOpen) return null;

            return (
              <WidgetContextToolbar
                selectedId={selectedLayerId}
                selectionBounds={selectionBounds}
                canvasScale={canvasScale}
                canvasRef={canvasRef}
                isCmsApp={layer.type === 'playlist'}
                selectedCount={selectedLayerIds.length}
                onEdit={() => {
                  if (layer.type === 'text') {
                    handleTextDoubleClick(selectedLayerId);
                  } else {
                    setIsAppSidepaneOpen(true);
                  }
                }}
                onDelete={() => handleLayerDeleteDirect(selectedLayerId)}
              />
            );
          })()}

          {/* Floating Context Toolbar (Multi-selection actions) */}
          {selectedLayerIds.length > 1 && selectionBounds && !draggingLayer?.hasMoved && !isPreviewOpen && (
            <FloatingContextToolbar
              selectedIds={selectedLayerIds}
              selectionBounds={{
                x: selectionBounds.left,
                y: selectionBounds.top,
                width: selectionBounds.width,
                height: selectionBounds.height
              }}
              canvasScale={canvasScale}
              canvasRef={canvasRef}
              canGroup={selectedLayerIds.length >= 2}
              allLocked={selectedLayerIds.every(id => findLayerById(dynamicLayers, id)?.locked)}
              onGroup={handleGroupSelected}
              onToggleLock={handleToggleLockSelected}
              onAlign={handleAlignSelected}
              onDuplicate={handleDuplicateSelected}
              onDelete={handleDeleteSelected}
            />
          )}

        </div>
      </div>
      {/* End Main Editor Area */}

      {/* ═══════════════════════════════════════════════════════════════════
      TOP NAV HEADER - Fixed positioned at top
      ═══════════════════════════════════════════════════════════════════
      - Position: fixed, top-0, left-0, z-30
      - Height: 60px (DO NOT CHANGE - affects layout calculations)
      - Always visible above all content (except when viewing upload)
      ═════════════════════════════════════════════��═════════════════════ */}
      <div
        className="fixed left-0 top-0 w-full z-30 h-[60px] border-b border-[#e4e7ec]"
        style={{ background: 'linear-gradient(to right, #43B48D, #0BA29B)' }}
        data-name="Nav Header"
      >
        <div className="flex flex-row items-center size-full">
          {!isCreateAppModalOpen && (
            <div className="content-stretch flex items-center justify-between px-[16px] py-[12px] relative size-full">
              <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
                <div
                  className="bg-white/10 hover:bg-white/20 overflow-clip relative rounded-[5px] shrink-0 size-[36px] cursor-pointer transition-colors flex items-center justify-center"
                  onClick={handleBack}
                >
                  <div className="size-[24px]" data-name="arrow-left">
                    <div className="contents inset-0" data-name="vuesax/linear/arrow-left">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
                        <g id="arrow-left">
                          <path d="M9.57 5.93L3.5 12L9.57 18.07" id="Vector" stroke="currentColor" className="text-white" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2" />
                          <path d="M20.5 12H3.67" id="Vector_2" stroke="currentColor" className="text-white" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2" />
                        </g>
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="content-stretch flex gap-[8px] items-center relative shrink-0 overflow-hidden">
                  {isEditingTemplateName ? (
                    <input
                      autoFocus
                      type="text"
                      className="font-sans font-extrabold leading-[normal] relative shrink-0 text-[18px] text-white tracking-[-0.36px] bg-transparent outline-none border-b border-white px-2 py-0.5 min-w-[150px] max-w-[300px]"
                      style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                      value={tempTemplateName}
                      onChange={(e) => setTempTemplateName(e.target.value)}
                      onBlur={() => {
                        setIsEditingTemplateName(false);
                        const newName = tempTemplateName.trim() || 'Untitled Design';
                        dispatch(setSelectedTemplate({
                          ...selectedTemplate,
                          name: newName
                        }));
                        // Sync page name if single-page design
                        if (pages.length === 1) {
                          setPageName(newName);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === 'Escape') {
                          setTempTemplateName(selectedTemplate.name || 'Untitled Design');
                          setIsEditingTemplateName(false);
                        }
                      }}
                    />
                  ) : (
                    <>
                      <p
                        className="font-sans font-extrabold leading-[normal] relative shrink-0 text-[18px] text-nowrap text-white tracking-[-0.36px] cursor-text max-w-[300px] overflow-hidden text-ellipsis hover:opacity-80 transition-opacity"
                        style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                        onClick={() => {
                          setTempTemplateName(selectedTemplate.name || 'Untitled Design');
                          setIsEditingTemplateName(true);
                        }}
                      >
                        {selectedTemplate.name}
                      </p>
                      <button
                        className="relative shrink-0 flex items-center justify-center cursor-pointer text-white/70 hover:text-white transition-colors"
                        onClick={() => {
                          setTempTemplateName(selectedTemplate.name || 'Untitled Design');
                          setIsEditingTemplateName(true);
                        }}
                        title="Edit Template Name"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </>
                  )}

                  <div className="w-px h-4 bg-white/30 mx-2 shrink-0" />

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={handleUndo}
                      disabled={!canUndo(history)}
                      className={`p-1.5 rounded-md transition-all ${canUndo(history)
                        ? 'text-white hover:bg-white/10 active:bg-white/20'
                        : 'text-white/30 cursor-not-allowed'
                        }`}
                      title={`Undo ${navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '(⌘Z)' : '(Ctrl+Z)'}`}
                    >
                      <Undo className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleRedo}
                      disabled={!canRedo(history)}
                      className={`p-1.5 rounded-md transition-all ${canRedo(history)
                        ? 'text-white hover:bg-white/10 active:bg-white/20'
                        : 'text-white/30 cursor-not-allowed'
                        }`}
                      title={`Redo ${navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '(⌘⇧Z)' : '(Ctrl+Y)'}`}
                    >
                      <Redo className="w-5 h-5" />
                    </button>

                    {/* <div className="w-px h-4 bg-[#e4e7ec] mx-2 shrink-0" />

                    <button
                      onClick={() => setIsCustomSizeModalOpen(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[#1D2739] hover:bg-gray-100 active:bg-gray-200 transition-all border border-transparent hover:border-gray-200 shadow-sm sm:shadow-none"
                      title="Resize Canvas"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-[13px] font-semibold hidden sm:inline">Resize</span>
                    </button> */}
                  </div>
                </div>
              </div>
              <div className="content-stretch flex gap-[16px] items-center justify-end relative shrink-0 w-[824px]">
                <div
                  className="bg-white/10 border border-white/20 content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 hover:bg-white/20 cursor-pointer transition-colors shadow-sm"
                  data-name="Secondary - Sm"
                  onClick={() => {
                    setIsPreviewOpen(true);
                  }}
                >
                  <Frame2>
                    <path d={navHeaderSvgPaths.p36f329f0} id="Vector" stroke="currentColor" className="text-white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  </Frame2>
                  <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                    Preview
                  </p>
                </div>
                <div
                  className="hidden bg-white border border-[#e4e7ec] content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 hover:bg-gray-50 cursor-pointer transition-colors shadow-sm"
                  onClick={async () => {
                    const canvas = canvasRef.current;
                    if (!canvas || pages.length === 0) return;

                    try {
                      if (pages.length === 1) {
                        // Single page download (Optimized)
                        const url = await toPng(canvas, { cacheBust: false });
                        const link = document.createElement('a');
                        link.download = `${selectedTemplate.name || 'design'}.png`;
                        link.href = url;
                        link.click();
                      } else {
                        // Multi-page ZIP download
                        const zip = new JSZip();
                        const originalPageIndex = currentPageIndex;

                        for (let i = 0; i < pages.length; i++) {
                          // Change the active page
                          setCurrentPageIndex(i);

                          // Wait for React to render the new page content on the canvas
                          await new Promise(resolve => setTimeout(resolve, 300));

                          // Ensure we have the latest DOM ref after timeout
                          const updatedCanvas = canvasRef.current;
                          if (!updatedCanvas) continue;

                          // Convert DOM to Blob
                          const blob = await toBlob(updatedCanvas, { cacheBust: false });
                          if (blob) {
                            zip.file(`page_${i + 1}.png`, blob);
                          }
                        }

                        // Restore original page
                        setCurrentPageIndex(originalPageIndex);

                        // Generate and download zip
                        const content = await zip.generateAsync({ type: 'blob' });
                        saveAs(content, `${selectedTemplate.name || 'design'}.zip`);
                      }
                    } catch (err) {
                      console.error('Download failed', err);
                    }
                  }}
                  title="Download as Image (PNG)"
                >
                  <Download className="w-4 h-4 text-[#1D2739]" />
                  <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-[#1D2739]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                    Download Image
                  </p>
                </div>

                <div
                  className={`content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 transition-all duration-200 bg-white hover:bg-gray-50 text-[#344054] cursor-pointer shadow-sm active:scale-[0.98] ${savingStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-name="Save Template CTA"
                  onClick={() => {
                    if (savingStatus === 'saving') return;
                    handleSaveDesign();
                  }}
                >
                  {savingStatus === 'saving' ? (
                    <div className="w-4 h-4 border-2 border-[#344054] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Frame2>
                      <path d={navHeaderSvgPaths.p3c401780} id="Vector" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <path d={navHeaderSvgPaths.pd7dbd80} id="Vector_2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <path d={navHeaderSvgPaths.p1d241140} id="Vector_3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                    </Frame2>
                  )}
                  <p className="font-sans font-medium leading-[normal] relative shrink-0 text-current text-[14px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                    {savingStatus === 'saving' ? 'Saving...' : 'Save Template'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* End Top Nav Header */}

      {/* ═══════════════════════════════════════════════════════════════════
          END OF LAYOUT ZONES
          ═════════════════════════════════════════════════════════════════
          
          All layout zones properly established:
          ✓ Root container: flex-col, no centering
          ✓ Main Editor Area: flex-1, scrollable
          ✓ Page Header: visible, in flow
          ✓ Canvas: centered, transform-scaled
          ✓ Fixed overlays: properly positioned
          
          DO NOT add new layout wrappers or modify positioning!
          ═══════════════════════════════════���═══════════════════���═══════════ */}

      <TimelinePanel
        isOpen={isTimelineOpen}
        onClose={() => setIsTimelineOpen(false)}
        layers={dynamicLayers}
        onUpdateLayer={handleTimelineLayerUpdate}
        textStyles={textStyles}
        imageSettings={imageSettings}
        shapeSettings={shapeSettings}
        templateSettings={templateSettings}
        videoSettings={videoSettings}
        onUpdateTemplateSettings={(updates) => setTemplateSettings(prev => ({ ...prev, ...updates }))}
        selectedLayerIds={selectedLayerIds}
        onSelectLayer={(id) => setSelectedLayerIds([id])}
        currentTime={currentTime}
        onTimeChange={setCurrentTime}
        isPlaying={isPlaying}
        onTogglePlay={() => setIsPlaying(!isPlaying)}
        templateData={{
          dynamicLayers,
          textStyles,
          imageSettings,
          shapeSettings,
          videoSettings,
          templateSettings
        } as any}
      />

      <SelectAppModal
        isOpen={isCreateAppModalOpen}
        onClose={() => setIsCreateAppModalOpen(false)}
        onAppCreated={handleAppCreated}
      />

      <CustomSizeModal
        isOpen={isCustomSizeModalOpen}
        onClose={() => setIsCustomSizeModalOpen(false)}
        onApply={handleResizeApply}
        initialDimensions={canvasDimensions}
      />

      {/* Local Preview Modal Overlay */}
      {isPreviewOpen && (
        <div className="fixed inset-0 bg-neutral-950 z-[9999] flex flex-col overflow-hidden">
          {/* Header Overlay */}
          <div className="absolute top-0 inset-x-0 h-16 bg-gradient-to-b from-black/60 to-transparent flex items-center justify-between px-6 z-10 pointer-events-none">
            <div className="flex flex-col pointer-events-auto">
              <h1 className="text-white font-semibold text-lg">{selectedTemplate.name || 'Current Design'}</h1>
              <p className="text-white/60 text-xs">Local Preview • In-Progress Changes</p>
            </div>

            <button
              onClick={() => setIsPreviewOpen(false)}
              className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all pointer-events-auto active:scale-90"
              title="Close Preview"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Main Preview */}
          <div className="flex-1 flex items-center justify-center bg-black">
            <TemplatePreview data={currentTemplateData as any} />
          </div>

          {/* Footer UI */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10 opacity-0 hover:opacity-100 transition-opacity duration-500 delay-300">
            <div className="px-5 py-2.5 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full text-white/90 text-sm font-medium shadow-2xl tracking-wide">
              Press <span className="text-blue-400 font-bold">ESC</span> to Exit Preview
            </div>
          </div>
        </div>
      )}

    </div >
  );
}
